import Layout from "@/components/Layout";
import { DatePartsPicker } from "@/components/hr/DatePartsPicker";
import { ConfirmAlertDialog } from "@/components/ConfirmAlertDialog";
import { VehicleProductNameModal } from "@/components/catalog/VehicleProductNameModal";
import { ProductImageGallery } from "@/components/catalog/ProductImageGallery";
import { AttributeSelectControl, AttributeSelectRuntimeAddButton } from "@/components/catalog/AttributeSelectControl";
import {
  QuickAddAttributeDialog,
  type QuickAddAttributeGroup,
} from "@/components/catalog/QuickAddAttributeDialog";
import { AssignCategoryAttributesDialog } from "@/components/catalog/AssignCategoryAttributesDialog";
import { SearchableSelect } from "@/components/catalog/SearchableSelect";
import { SalePriceInput } from "@/components/catalog/SalePriceField";
import { PurchaseCostCategorySelect } from "@/components/catalog/PurchaseCostCategorySelect";
import { DealInformationSection } from "@/components/catalog/DealInformationSection";
import { CompanyOwnerCommissionSection } from "@/components/catalog/CompanyOwnerCommissionSection";
import { DealPartnersSection } from "@/components/catalog/DealPartnersSection";
import { CreateInvestorDialog } from "@/components/investors/CreateInvestorDialog";
import { CreateSupplierDialog } from "@/components/suppliers/CreateSupplierDialog";
import {
  applyPaymentMethodChange,
  emptyPaymentMethodDraft,
  type PaymentMethodValue,
} from "@/components/sales/PaymentMethodFields";
import { countryFlagEmoji, fetchCountries, type CountryOption } from "@/lib/geo/geoStore";
import { CountryFlagIcon } from "@/components/geo/CountryFlagIcon";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { getCategoryProductSchema, listCategories, mockUploadFile } from "@/lib/catalog/mockCatalogApi";
import {
  PRODUCT_DOCS_ATTR,
  isAllowedProductDocFile,
  stripLocalFilesFromDraft,
} from "@/lib/catalog/productMedia";
import type {
  Attribute,
  AttributeGroup,
  AttributeOption,
  AttributeValue,
  Category,
  CategoryProductSchema,
  ProductDraft,
  ProductInvoice,
  ProductInvoiceRole,
  UploadedFile,
} from "@/lib/catalog/types";
import { listBrandRecords, syncBrandsFromApi, type BrandRecord } from "@/lib/catalog/brandsStore";
import { listWarehouseRecords, syncWarehousesFromApi, type WarehouseRecord } from "@/lib/catalog/warehousesStore";
import {
  clearAddProductWizard,
  loadAddProductWizard,
  saveAddProductWizard,
} from "@/lib/catalog/addProductWizardStore";
import {
  catalogProductErrorMessage,
  deleteProduct,
  fetchProductFromApi,
  finalizeWizardProduct,
  getProductById,
  persistProductWizardProgress,
  syncProductsFromApi,
  updateProduct,
  updateProductPurchaseDetails,
} from "@/lib/catalog/productsStore";
import { attributeScalarToOptionString } from "@/lib/catalog/attributeScalarString";
import { mergeAttributeOptionsInSchema } from "@/lib/catalog/attributeOptionsApi";
import {
  filterVisibleAttributes,
  flattenSchemaAttributes,
  isAttributeVisible,
  pruneHiddenAttributeValues,
  validateVisibleRequiredAttributes,
} from "@/lib/catalog/attributeVisibility";
import { CurrencySelect } from "@/components/catalog/CurrencySelect";
import { DocumentTypeSelect } from "@/components/catalog/DocumentTypeSelect";
import { PurchaseCostTypeSelect } from "@/components/catalog/PurchaseCostTypeSelect";
import { ImportCountrySelect } from "@/components/catalog/ImportCountrySelect";
import { PartySelect } from "@/components/catalog/PartySelect";
import {
  attachNormalizedPurchaseFields,
  computeLandedCostForRole,
  filterCostLinesByRole,
  isDiscountCostLine,
  resolveAutoCostLinePayer,
  sumPurchaseCostLinesForRole,
} from "@/lib/catalog/purchaseCost";
import {
  createPurchaseCostType,
  listPurchaseCostTypeRecords,
  syncPurchaseCostTypesFromApi,
} from "@/lib/catalog/purchaseCostTypesStore";
import {
  listPurchaseCostCategories,
  syncPurchaseCostCategoriesFromApi,
} from "@/lib/catalog/purchaseCostCategoriesStore";
import {
  createDocumentType,
  listDocumentTypeRecords,
  syncDocumentTypesFromApi,
} from "@/lib/catalog/documentTypesStore";
import {
  DEAL_TYPE_OPTIONS,
  applyOwnerVehicleSurplusPartnerDefaults,
  applyPartnerInvestmentAmountEdit,
  applyRemainingProfitPercentToPartners,
  clearedDealDependents,
  companyOwnerDealPartnerRow,
  emptyDealPartnerRow,
  emptyShowroomCharge,
  investmentAmountTotal,
  isOwnerVehicleSurplusDeal,
  isShowroomIndependentOfPartnerProfits,
  ownerVehicleSurplusPartnersMatch,
  partnerProfitsMatchRemaining,
  remainingInvestmentPool,
  showroomCommissionPreviewBase,
  validateDealDraft,
  withRecalculatedInvestmentRatios,
  type DealPartnerDraft,
  type ProductDealType,
} from "@/lib/catalog/dealPartners";
import { listCompanyPartyOptions, listPartyOptions, syncPartiesFromApi } from "@/lib/parties/partiesStore";
import { can, getStoredAuthUser } from "@/lib/authUser";
import { openDocumentPrintView } from "@/lib/printPdf";
import { slugify } from "@/lib/slugify";
import { cn } from "@/lib/utils";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  ArrowLeft,
  Camera,
  Check,
  CheckSquare,
  ChevronDown,
  ChevronLeft,
  ClipboardList,
  Edit,
  FileText,
  Image as ImageIcon,
  Layers,
  ListTree,
  Loader2,
  Package,
  Plus,
  Receipt,
  Save,
  Search,
  Square,
  Tag,
  Trash2,
  Upload,
  Users,
  Wallet,
  X,
  Gavel,
} from "lucide-react";
import { motion } from "framer-motion";
import { isAxiosError } from "axios";
import { Fragment, useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { fetchCurrencies, formatCurrency, getActiveCompanyCurrency, getCachedCurrencies } from "@/lib/currencies/currenciesStore";
import { pkrAmountShortLabel } from "@/lib/formatPkrAmount";

function formatMoneyAmount(n: number | null | undefined, currency?: string): string {
  if (n === null || n === undefined || !Number.isFinite(Number(n))) return "—";
  return formatCurrency(Number(n), currency);
}

function sortByOrder<T extends { sortOrder?: number }>(items: T[]) {
  return [...items].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
}

const SectionHeader = ({
  title,
  subtitle,
  icon,
  right,
}: {
  title: string;
  subtitle?: string;
  icon?: React.ComponentType<{ className?: string }>;
  right?: ReactNode;
}) => {
  const Icon = icon;
  return (
    <div className="flex items-center justify-between gap-3 mb-2.5">
      <div className="flex min-w-0 flex-1 items-center gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-primary/20 bg-primary/10">
          {Icon ? <Icon className="h-4 w-4 text-primary" /> : <FileText className="h-4 w-4 text-primary" />}
        </div>
        <div className="min-w-0 flex-1 space-y-1">
          <h3 className="font-display text-sm font-semibold leading-tight tracking-tight text-foreground">{title}</h3>
          {subtitle ? <p className="text-xs leading-snug text-muted-foreground">{subtitle}</p> : null}
        </div>
      </div>
      {right != null && right !== false ? <div className="shrink-0 flex items-center gap-2">{right}</div> : null}
    </div>
  );
};

const ADDITIONAL_SELECT_TRIGGER =
  "h-10 w-full rounded-md border border-emerald-200/80 bg-emerald-50/90 px-3 text-sm text-foreground shadow-sm ring-offset-background focus:ring-2 focus:ring-ring/40 dark:border-emerald-800/50 dark:bg-emerald-950/35 dark:text-emerald-50/95";

const ADDITIONAL_TEXT_INPUT =
  "h-10 w-full rounded-md border border-input bg-background px-3 text-sm text-foreground shadow-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/30";

function AdditionalInfoFieldRow({
  labelText,
  required,
  metaRight,
  alignTop,
  labelAction,
  children,
}: {
  labelText: string;
  required?: boolean;
  metaRight?: ReactNode;
  alignTop?: boolean;
  labelAction?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div
      className={cn(
        "grid grid-cols-1 gap-2 py-4 sm:grid-cols-[minmax(10rem,15rem)_minmax(0,1fr)] sm:gap-x-10 sm:gap-y-0",
        alignTop ? "sm:items-start" : "sm:items-center",
      )}
    >
      <div className={cn("text-sm text-foreground sm:text-right", alignTop && "sm:pt-2.5")}>
        <div className="flex items-center justify-between gap-2 sm:justify-end">
          <span className="font-medium text-foreground/95">
            {labelText}
            {required ? <span className="text-destructive"> *</span> : null}
          </span>
          {labelAction}
        </div>
        {metaRight ? <div className="mt-1 text-left text-[11px] text-muted-foreground sm:text-right">{metaRight}</div> : null}
      </div>
      <div className="min-w-0">{children}</div>
    </div>
  );
}

function AttributeField({
  attribute,
  value,
  onChange,
  layout = "default",
  suppressMultiselectLabel = false,
  featureGridCell = false,
  onAttributeOptionsUpdated,
}: {
  attribute: Attribute;
  value: AttributeValue | undefined;
  onChange: (v: AttributeValue) => void;
  layout?: "default" | "additional";
  /** Under the shared “Features” heading: show only the checkbox grid (no left label). */
  suppressMultiselectLabel?: boolean;
  /** Compact Yes/No tile inside the Features grid (5 columns). */
  featureGridCell?: boolean;
  onAttributeOptionsUpdated?: (attributeId: string, options: AttributeOption[]) => void;
}) {
  const [runtimeOptionOpen, setRuntimeOptionOpen] = useState(false);
  const label = (
    <div className="flex items-center justify-between gap-3">
      <Label className="text-xs text-muted-foreground">
        {attribute.name}
        {attribute.isRequired ? <span className="text-destructive"> *</span> : null}
      </Label>
      {attribute.type === "file" || attribute.type === "files" || attribute.type === "image" || attribute.type === "images" ? (
        <span className="text-[11px] text-muted-foreground/70">
          {attribute.maxSizeMb ? `Max ${attribute.maxSizeMb}MB` : null}
          {attribute.maxSizeMb && attribute.maxFiles ? " • " : null}
          {attribute.maxFiles ? `Up to ${attribute.maxFiles}` : null}
        </span>
      ) : null}
    </div>
  );

  const fileMeta =
    attribute.type === "file" || attribute.type === "files" || attribute.type === "image" || attribute.type === "images" ? (
      <span className="text-[11px] text-muted-foreground/80">
        {attribute.maxSizeMb ? `Max ${attribute.maxSizeMb}MB` : null}
        {attribute.maxSizeMb && attribute.maxFiles ? " · " : null}
        {attribute.maxFiles ? `Up to ${attribute.maxFiles} files` : null}
      </span>
    ) : null;

    if (layout === "additional") {
    if (attribute.type === "textarea") {
      return (
        <AdditionalInfoFieldRow labelText={attribute.name} required={attribute.isRequired} alignTop>
          <Textarea
            className="min-h-[120px] w-full resize-y rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground shadow-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/30"
            value={typeof value === "string" ? value : ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder={`Enter ${attribute.name.toLowerCase()}`}
          />
        </AdditionalInfoFieldRow>
      );
    }
    if (attribute.type === "boolean") {
      if (featureGridCell) {
        return (
          <label className="flex min-h-0 cursor-pointer items-center gap-2.5 rounded-md border border-border/55 bg-background/90 p-2.5 shadow-sm">
            <Checkbox
              checked={Boolean(value)}
              onCheckedChange={(checked) => onChange(Boolean(checked))}
              className="shrink-0 border-border data-[state=checked]:bg-primary data-[state=checked]:border-primary"
            />
            <span className="min-w-0 flex-1 text-[0.8125rem] font-medium leading-snug text-foreground">
              {attribute.name}
              {attribute.isRequired ? <span className="text-destructive"> *</span> : null}
            </span>
          </label>
        );
      }
      return (
        <AdditionalInfoFieldRow labelText={attribute.name} required={attribute.isRequired}>
          <label className="flex cursor-pointer items-center gap-2.5 rounded-md py-1 text-sm text-foreground">
            <Checkbox
              checked={Boolean(value)}
              onCheckedChange={(checked) => onChange(Boolean(checked))}
              className="border-border data-[state=checked]:bg-primary data-[state=checked]:border-primary"
            />
          </label>
        </AdditionalInfoFieldRow>
      );
    }
    if (attribute.type === "select") {
      return (
        <AdditionalInfoFieldRow
          labelText={attribute.name}
          required={attribute.isRequired}
          labelAction={
            <AttributeSelectRuntimeAddButton attribute={attribute} onClick={() => setRuntimeOptionOpen(true)} />
          }
        >
          <AttributeSelectControl
            attribute={attribute}
            value={value}
            onChange={onChange}
            onOptionsUpdated={onAttributeOptionsUpdated}
            triggerClassName={ADDITIONAL_SELECT_TRIGGER}
            placeholder={attribute.name}
            runtimeOpen={runtimeOptionOpen}
            onRuntimeOpenChange={setRuntimeOptionOpen}
          />
        </AdditionalInfoFieldRow>
      );
    }
    if (attribute.type === "multiselect") {
      const selected = Array.isArray(value)
        ? (value as unknown[]).map((v) => attributeScalarToOptionString(v as AttributeValue))
        : [];
      const options = sortByOrder(attribute.options ?? []);
      const grid = (
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4 md:gap-x-2 md:gap-y-2">
          {options.map((opt) => {
            const key = String(opt.value ?? "");
            if (!key) return null;
            const checked = selected.includes(key);
            return (
              <label
                key={opt.id}
                className="flex min-w-0 cursor-pointer items-center gap-2 py-1 text-xs leading-snug text-foreground/90 transition-colors hover:text-foreground sm:text-[0.8125rem]"
              >
                <Checkbox
                  checked={checked}
                  onCheckedChange={(next) => {
                    const nextChecked = Boolean(next);
                    const nextValues = nextChecked ? [...selected, key] : selected.filter((v) => v !== key);
                    onChange(nextValues);
                  }}
                  className="shrink-0 border-border data-[state=checked]:bg-primary data-[state=checked]:border-primary"
                />
                <span className="min-w-0 truncate sm:whitespace-normal sm:break-words">{opt.label}</span>
              </label>
            );
          })}
        </div>
      );
      if (suppressMultiselectLabel) {
        return <div className="py-3 sm:py-4">{grid}</div>;
      }
      return (
        <AdditionalInfoFieldRow labelText={attribute.name} required={attribute.isRequired} alignTop>
          {grid}
        </AdditionalInfoFieldRow>
      );
    }
    if (attribute.type === "date") {
      return (
        <AdditionalInfoFieldRow labelText={attribute.name} required={attribute.isRequired}>
          <Input
            type="date"
            className={ADDITIONAL_TEXT_INPUT}
            value={typeof value === "string" ? value : ""}
            onChange={(e) => onChange(e.target.value)}
          />
        </AdditionalInfoFieldRow>
      );
    }
    if (attribute.type === "number") {
      return (
        <AdditionalInfoFieldRow labelText={attribute.name} required={attribute.isRequired}>
          <Input
            type="number"
            className={ADDITIONAL_TEXT_INPUT}
            value={typeof value === "number" ? String(value) : typeof value === "string" ? value : ""}
            onChange={(e) => {
              const raw = e.target.value;
              if (raw === "") return onChange(null);
              const n = Number(raw);
              onChange(Number.isFinite(n) ? n : null);
            }}
            placeholder={attribute.name}
          />
        </AdditionalInfoFieldRow>
      );
    }
    if (attribute.type === "image" || attribute.type === "file") {
      const current = value as UploadedFile | undefined;
      return (
        <AdditionalInfoFieldRow labelText={attribute.name} required={attribute.isRequired} metaRight={fileMeta ?? undefined}>
          <div className="flex flex-wrap items-start gap-3">
            <Input
              type="file"
              className={cn(ADDITIONAL_TEXT_INPUT, "h-auto min-h-10 cursor-pointer py-2")}
              accept={attribute.accept}
              onChange={async (e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                const uploaded = await mockUploadFile(file, attribute);
                onChange(uploaded);
              }}
            />
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md border border-border/50 bg-muted/30">
              {attribute.type === "image" ? <ImageIcon className="h-5 w-5 text-muted-foreground" /> : <Upload className="h-5 w-5 text-muted-foreground" />}
            </div>
          </div>
          {attribute.type === "image" && current?.url ? (
            <div className="mt-3 max-w-xs overflow-hidden rounded-md border border-border/40">
              <img src={current.url} alt={current.filename} className="h-auto w-full object-cover" />
            </div>
          ) : null}
          {current ? (
            <p className="mt-2 text-xs text-muted-foreground">
              Selected: <span className="text-foreground/90">{current.filename}</span>
            </p>
          ) : null}
        </AdditionalInfoFieldRow>
      );
    }
    if (attribute.type === "images" || attribute.type === "files") {
      const current = Array.isArray(value) ? (value as UploadedFile[]) : [];
      return (
        <AdditionalInfoFieldRow labelText={attribute.name} required={attribute.isRequired} metaRight={fileMeta ?? undefined} alignTop>
          <Input
            type="file"
            multiple
            className={cn(ADDITIONAL_TEXT_INPUT, "h-auto min-h-10 cursor-pointer py-2")}
            accept={attribute.accept}
            onChange={async (e) => {
              const files = Array.from(e.target.files ?? []);
              if (files.length === 0) return;
              const uploads: UploadedFile[] = [];
              for (const f of files) {
                uploads.push(await mockUploadFile(f, attribute));
              }
              const max = attribute.maxFiles ?? uploads.length;
              onChange(uploads.slice(0, max));
            }}
          />
          {current.length ? (
            <div className="mt-2 space-y-2 text-xs text-muted-foreground">
              <p>
                Selected {current.length} file{current.length === 1 ? "" : "s"}.
              </p>
              {attribute.type === "images" ? (
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                  {current
                    .filter((u) => Boolean(u.url))
                    .map((u) => (
                      <div key={u.id} className="aspect-video overflow-hidden rounded-md border border-border/40 bg-muted/20">
                        <img src={u.url} alt={u.filename} className="h-full w-full object-cover" />
                      </div>
                    ))}
                </div>
              ) : (
                <ul className="list-disc pl-5">
                  {current.map((u) => (
                    <li key={u.id}>
                      <span className="text-foreground/90">{u.filename}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ) : null}
        </AdditionalInfoFieldRow>
      );
    }
    return (
      <AdditionalInfoFieldRow labelText={attribute.name} required={attribute.isRequired}>
        <Input
          className={ADDITIONAL_TEXT_INPUT}
          value={typeof value === "string" ? value : ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={attribute.name}
        />
      </AdditionalInfoFieldRow>
    );
  }

  if (attribute.type === "textarea") {
    return (
      <div className="space-y-2">
        {label}
        <Textarea
          className="glass-input min-h-[96px]"
          value={typeof value === "string" ? value : ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={`Enter ${attribute.name.toLowerCase()}`}
        />
      </div>
    );
  }

  if (attribute.type === "boolean") {
    return (
      <label className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground cursor-pointer transition-colors p-2 rounded-lg hover:bg-secondary/30">
        <Checkbox
          checked={Boolean(value)}
          onCheckedChange={(checked) => onChange(Boolean(checked))}
          className="border-border data-[state=checked]:bg-primary data-[state=checked]:border-primary"
        />
        <span>
          {attribute.name}
          {attribute.isRequired ? <span className="text-destructive"> *</span> : null}
        </span>
      </label>
    );
  }

  if (attribute.type === "select") {
    return (
      <div className="space-y-2">
        <div className="flex items-center justify-between gap-3">
          <Label className="text-xs text-muted-foreground">
            {attribute.name}
            {attribute.isRequired ? <span className="text-destructive"> *</span> : null}
          </Label>
          <AttributeSelectRuntimeAddButton attribute={attribute} onClick={() => setRuntimeOptionOpen(true)} />
        </div>
        <AttributeSelectControl
          attribute={attribute}
          value={value}
          onChange={onChange}
          onOptionsUpdated={onAttributeOptionsUpdated}
          placeholder={`Select ${attribute.name.toLowerCase()}`}
          runtimeOpen={runtimeOptionOpen}
          onRuntimeOpenChange={setRuntimeOptionOpen}
        />
      </div>
    );
  }

  if (attribute.type === "multiselect") {
    const selected = Array.isArray(value)
      ? (value as unknown[]).map((v) => attributeScalarToOptionString(v as AttributeValue))
      : [];
    const options = sortByOrder(attribute.options ?? []);
    return (
      <div className="space-y-2">
        {label}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {options.map((opt) => {
            const key = String(opt.value ?? "");
            if (!key) return null;
            const checked = selected.includes(key);
            return (
              <label
                key={opt.id}
                className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground cursor-pointer transition-colors p-2 rounded-lg hover:bg-secondary/30"
              >
                <Checkbox
                  checked={checked}
                  onCheckedChange={(next) => {
                    const nextChecked = Boolean(next);
                    const nextValues = nextChecked ? [...selected, key] : selected.filter((v) => v !== key);
                    onChange(nextValues);
                  }}
                  className="border-border data-[state=checked]:bg-primary data-[state=checked]:border-primary"
                />
                {opt.label}
              </label>
            );
          })}
        </div>
      </div>
    );
  }

  if (attribute.type === "date") {
    return (
      <div className="space-y-2">
        {label}
        <Input
          type="date"
          className="glass-input h-10"
          value={typeof value === "string" ? value : ""}
          onChange={(e) => onChange(e.target.value)}
        />
      </div>
    );
  }

  if (attribute.type === "number") {
    return (
      <div className="space-y-2">
        {label}
        <Input
          type="number"
          className="glass-input h-10"
          value={typeof value === "number" ? String(value) : typeof value === "string" ? value : ""}
          onChange={(e) => {
            const raw = e.target.value;
            if (raw === "") return onChange(null);
            const n = Number(raw);
            onChange(Number.isFinite(n) ? n : null);
          }}
          placeholder={`Enter ${attribute.name.toLowerCase()}`}
        />
      </div>
    );
  }

  if (attribute.type === "image" || attribute.type === "file") {
    const current = value as UploadedFile | undefined;
    return (
      <div className="space-y-2">
        {label}
        <div className="flex items-start gap-3">
          <div className="flex-1">
            <Input
              type="file"
              className="glass-input h-10 pt-2"
              accept={attribute.accept}
              onChange={async (e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                const uploaded = await mockUploadFile(file, attribute);
                onChange(uploaded);
              }}
            />
            {current ? (
              <p className="text-xs text-muted-foreground mt-2">
                Selected: <span className="text-foreground/90">{current.filename}</span>
              </p>
            ) : null}
          </div>
          <div className="w-12 h-12 rounded-lg border border-border/40 bg-secondary/20 flex items-center justify-center">
            {attribute.type === "image" ? <ImageIcon className="w-5 h-5 text-muted-foreground" /> : <Upload className="w-5 h-5 text-muted-foreground" />}
          </div>
        </div>
        {attribute.type === "image" && current?.url ? (
          <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="aspect-video rounded-lg overflow-hidden border border-border/40 bg-secondary/20">
              <img src={current.url} alt={current.filename} className="w-full h-full object-cover" />
            </div>
          </div>
        ) : null}
      </div>
    );
  }

  if (attribute.type === "images" || attribute.type === "files") {
    const current = Array.isArray(value) ? (value as UploadedFile[]) : [];
    return (
      <div className="space-y-2">
        {label}
        <Input
          type="file"
          multiple
          className="glass-input h-10 pt-2"
          accept={attribute.accept}
          onChange={async (e) => {
            const files = Array.from(e.target.files ?? []);
            if (files.length === 0) return;
            const uploads: UploadedFile[] = [];
            for (const f of files) {
              uploads.push(await mockUploadFile(f, attribute));
            }
            const max = attribute.maxFiles ?? uploads.length;
            onChange(uploads.slice(0, max));
          }}
        />
        {current.length ? (
          <div className="mt-2 space-y-2">
            <p className="text-xs text-muted-foreground">
              Selected {current.length} file{current.length === 1 ? "" : "s"}.
            </p>
            {attribute.type === "images" ? (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {current
                  .filter((u) => Boolean(u.url))
                  .map((u) => (
                    <div key={u.id} className="aspect-video rounded-lg overflow-hidden border border-border/40 bg-secondary/20">
                      <img src={u.url} alt={u.filename} className="w-full h-full object-cover" />
                    </div>
                  ))}
              </div>
            ) : (
              <ul className="text-xs text-muted-foreground list-disc pl-5">
                {current.map((u) => (
                  <li key={u.id}>
                    <span className="text-foreground/90">{u.filename}</span>
                    <span className="text-muted-foreground/70"> ({Math.round(u.size / 1024)} KB)</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {label}
      <Input
        className="glass-input h-10"
        value={typeof value === "string" ? value : ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={`Enter ${attribute.name.toLowerCase()}`}
      />
    </div>
  );
}

/** Category schema group shown on Basic information (after status), not duplicated on the Additional information step. */
function isBasicSpecsAttributeGroup(group: AttributeGroup): boolean {
  const slug = (group.slug ?? "").toLowerCase();
  const name = (group.name ?? "").toLowerCase();
  if (slug === "basic-specs" || slug === "basic-spec") return true;
  if (name.includes("basic spec") || name.includes("basic details")) return true;
  return false;
}

/** “Basic details” group: no bordered/muted shell on basic info (matches plain grid fields). */
function isBasicDetailsAttributeGroup(group: AttributeGroup): boolean {
  const slug = (group.slug ?? "").toLowerCase();
  const name = (group.name ?? "").toLowerCase();
  if (slug === "basic-details" || slug === "basic-detail") return true;
  return name.includes("basic detail");
}

/** Shown under Additional information (after Transmission), not on Basic. */
function isAssemblyAttribute(a: Attribute): boolean {
  const n = (a.name ?? "").trim().toLowerCase();
  const c = (a.code ?? "").trim().toLowerCase().replace(/-/g, "_");
  return n === "assembly" || c === "assembly" || c.endsWith("_assembly");
}

function collectAssemblyAttributes(allGroups: AttributeGroup[]): Attribute[] {
  const out: Attribute[] = [];
  const seen = new Set<string>();
  for (const g of allGroups) {
    for (const a of sortByOrder(g.attributes)) {
      if (!isAssemblyAttribute(a) || seen.has(a.id)) continue;
      seen.add(a.id);
      out.push(a);
    }
  }
  return out;
}

function filterBasicSpecGridAttributes(attrs: Attribute[]): Attribute[] {
  return sortByOrder(attrs).filter((a) => !isAssemblyAttribute(a));
}

function validateAdditionalInformationStep(
  d: ProductDraft,
  specOnlyGroups: AttributeGroup[],
  assemblyAttrs: Attribute[],
  allAttrs: Attribute[],
): boolean {
  if (
    !validateVisibleRequiredAttributes(d, specOnlyGroups, (msg) => toast.error(msg), (a) => isAssemblyAttribute(a))
  ) {
    return false;
  }
  for (const attr of assemblyAttrs) {
    if (!isAttributeVisible(attr, allAttrs, d.attributes)) continue;
    if (!attr.isRequired) continue;
    const value = d.attributes[attr.code];
    const missing =
      value === undefined || value === null || value === "" || (Array.isArray(value) && value.length === 0);
    if (missing) {
      toast.error(`Required field: ${attr.name}`);
      return false;
    }
  }
  return true;
}

/** Engine Type → Engine Capacity → Transmission → Assembly, then any other fields (original schema order). */
function sortAdditionalMainAttrs(attrs: Attribute[], stableOrder: Map<string, number>): Attribute[] {
  const norm = (s: string) => s.trim().toLowerCase().replace(/-/g, "_").replace(/\s+/g, " ");

  const matchers: ((a: Attribute) => boolean)[] = [
    (a) => {
      const n = norm(a.name);
      const c = norm(a.code ?? "");
      return (
        n === "engine type" ||
        (n.includes("engine") && n.includes("type") && !n.includes("capacity")) ||
        c.includes("engine_type") ||
        c === "engine_type" ||
        c === "enginetype"
      );
    },
    (a) => {
      const n = norm(a.name);
      const c = norm(a.code ?? "");
      return (
        (n.includes("engine") && n.includes("capacity")) ||
        n.includes("(cc)") ||
        c.includes("engine_capacity") ||
        c.includes("enginecapacity") ||
        (c.includes("displacement") && !c.includes("type"))
      );
    },
    (a) => {
      const n = norm(a.name);
      const c = norm(a.code ?? "");
      return n.includes("transmission") || c.includes("transmission");
    },
    (a) => {
      const n = norm(a.name);
      const c = norm(a.code ?? "");
      return n.includes("assembly") || c.includes("assembly") || c.includes("assembled");
    },
  ];

  const rank = (a: Attribute): number => {
    for (let i = 0; i < matchers.length; i++) {
      if (matchers[i](a)) return i;
    }
    return matchers.length;
  };

  return [...attrs].sort((a, b) => {
    const d = rank(a) - rank(b);
    if (d !== 0) return d;
    return (stableOrder.get(a.id) ?? 0) - (stableOrder.get(b.id) ?? 0);
  });
}

/** Preferred order for vehicle Yes/No feature booleans (normalized labels). */
const FEATURE_BOOLEAN_ORDER: string[] = [
  "sun roof",
  "panoramic sunroof",
  "drls",
  "led headlights",
  "front fog lights",
  "alloy wheels",
  "front speakers",
  "rear speakers",
  "paddle shifters",
  "infotainment system",
  "hud",
  "android auto",
  "apple carplay",
  "speakers",
  "steering switches",
  "parking sensors",
  "rear camera",
  "front camera",
  "360 camera",
  "abs",
  "airbags",
  "air bags",
  "traction control",
  "tpms",
  "cameras",
  "immobilizer",
  "power locks",
  "power mirrors",
  "power steering",
  "rear ac vents",
  "ventilated seats",
  "power seats",
  "heated seats",
  "climate control",
  "cruise control",
  "push start",
  "keyless entry",
  "ac",
];

function normFeatureLabel(s: string): string {
  return s.trim().toLowerCase().replace(/[-_]+/g, " ").replace(/\s+/g, " ").replace(/\s*\(\s*/g, " ").replace(/\s*\)\s*/g, " ").trim();
}

function featureBooleanSortRank(a: Attribute, stableOrder: Map<string, number>): number {
  const n = normFeatureLabel(a.name);
  const c = (a.code ?? "").trim().toLowerCase().replace(/[-\s]+/g, "_");
  for (let i = 0; i < FEATURE_BOOLEAN_ORDER.length; i++) {
    const key = FEATURE_BOOLEAN_ORDER[i];
    const keySnake = key.replace(/\s/g, "_");
    const keyCompact = key.replace(/\s/g, "");
    if (n === key || c === keySnake || c === keyCompact) return i;
  }
  for (let i = 0; i < FEATURE_BOOLEAN_ORDER.length; i++) {
    const key = FEATURE_BOOLEAN_ORDER[i];
    if (key.length < 3) continue;
    if (n.includes(key)) return i;
  }
  return FEATURE_BOOLEAN_ORDER.length + (stableOrder.get(a.id) ?? 0);
}

function sortFeatureBooleanAttrs(attrs: Attribute[], stableOrder: Map<string, number>): Attribute[] {
  return [...attrs].sort((a, b) => {
    const d = featureBooleanSortRank(a, stableOrder) - featureBooleanSortRank(b, stableOrder);
    if (d !== 0) return d;
    return (stableOrder.get(a.id) ?? 0) - (stableOrder.get(b.id) ?? 0);
  });
}

function savePayload(draft: ProductDraft): ProductDraft {
  const showroomBase =
    draft.showroomCharge ?? draft.companyOwnerCommission ?? emptyShowroomCharge();
  const companyParties = listCompanyPartyOptions();
  const fallbackParty = companyParties[0] ?? null;
  const showroomCharge = emptyShowroomCharge({
    ...showroomBase,
    partyId: showroomBase.partyId ?? fallbackParty?.id ?? null,
    partyName: showroomBase.partyName ?? fallbackParty?.name ?? null,
  });

  const isConsignment = draft.acquisitionType === "consignment";
  const consignmentAmount =
    draft.purchaseCurrencyAmount != null && Number.isFinite(Number(draft.purchaseCurrencyAmount))
      ? Number(draft.purchaseCurrencyAmount)
      : draft.purchasePrice === null || draft.purchasePrice === undefined
        ? null
        : Number(draft.purchasePrice);

  return attachNormalizedPurchaseFields({
    ...draft,
    name: (draft.name ?? "").trim(),
    slug: undefined,
    brandId: draft.brandId || null,
    supplierId: isConsignment ? null : (draft.supplierId || null),
    // Keep a non-null value for DB; consignment UI does not use local/import.
    purchaseType: isConsignment ? "local" : (draft.purchaseType === "import" ? "import" : "local"),
    importCountryId: !isConsignment && draft.purchaseType === "import" ? draft.importCountryId ?? null : null,
    purchasePrice: isConsignment
      ? consignmentAmount
      : draft.purchasePrice === null || draft.purchasePrice === undefined
        ? null
        : Number(draft.purchasePrice),
    purchaseCurrency: isConsignment
      ? getActiveCompanyCurrency().code
      : draft.purchaseCurrency || getActiveCompanyCurrency().code,
    purchaseCurrencyAmount: isConsignment ? consignmentAmount : (draft.purchaseCurrencyAmount ?? null),
    purchaseExchangeRate: isConsignment ? 1 : (draft.purchaseExchangeRate ?? null),
    // Consignment has no product-level investor purchase quote; cost lines may still dual-track.
    investorPurchasePrice: isConsignment ? null : draft.investorPurchasePrice,
    investorPurchaseCurrency: isConsignment ? null : draft.investorPurchaseCurrency,
    investorPurchaseCurrencyAmount: isConsignment ? null : draft.investorPurchaseCurrencyAmount,
    investorPurchaseExchangeRate: isConsignment ? null : draft.investorPurchaseExchangeRate,
    purchaseNotes: draft.purchaseNotes?.trim() || null,
    productImages: draft.productImages ?? [],
    productDocuments: draft.productDocuments ?? [],
    documentType: draft.documentType === "duplicate" ? "duplicate" : "original",
    numberOfDocuments: (() => {
      const n = Number(draft.numberOfDocuments ?? 0);
      if (!Number.isInteger(n) || n < 0) return 0;
      return Math.min(100, n);
    })(),
    purchaseCostLines: (draft.purchaseCostLines ?? []).map((line) => {
      const dealType = isConsignment ? "owner_vehicle" : (draft.dealType ?? "purchased");
      const autoPayer = resolveAutoCostLinePayer({
        dealType,
        acquisitionType: isConsignment ? "consignment" : "purchased",
        dealPartners: draft.dealPartners,
        fallbackCompanyPartyId: fallbackParty?.id != null ? String(fallbackParty.id) : null,
      });
      return {
        ...line,
        role: line.role === "investor" ? "investor" : "supplier",
        paidByPartyId: autoPayer.paidByPartyId,
        costBearerPartyId: autoPayer.costBearerPartyId,
      };
    }),
    salePrice: draft.salePrice === null || draft.salePrice === undefined ? null : Number(draft.salePrice),
    expectedSalePrice:
      draft.expectedSalePrice === null || draft.expectedSalePrice === undefined
        ? null
        : Number(draft.expectedSalePrice),
    ownerDemandPrice:
      draft.ownerDemandPrice === null || draft.ownerDemandPrice === undefined
        ? null
        : Number(draft.ownerDemandPrice),
    dealType: draft.dealType ?? null,
    profitRuleType: draft.profitRuleType ?? null,
    dealPartners: (() => {
      const dealType = isConsignment ? "owner_vehicle" : (draft.dealType ?? "purchased");
      const partners = draft.dealPartners ?? [];
      if (dealType === "owner_vehicle" && partners.length > 0) {
        // Consignment investment / floor = Purchased Amount only (costs settle separately).
        const consignmentFloor =
          consignmentAmount != null && Number.isFinite(consignmentAmount) ? consignmentAmount : null;
        const supplierLanded = computeLandedCostForRole(
          draft.purchasePrice === null || draft.purchasePrice === undefined
            ? null
            : Number(draft.purchasePrice),
          draft.purchaseCostLines,
          "supplier",
        );
        const investorLanded = computeLandedCostForRole(
          draft.investorPurchasePrice === null || draft.investorPurchasePrice === undefined
            ? null
            : Number(draft.investorPurchasePrice),
          draft.purchaseCostLines,
          "investor",
        );
        const landedCost = isConsignment
          ? consignmentFloor
          : (investorLanded ?? supplierLanded);
        return partners.map((p, idx) =>
          idx === 0
            ? {
                ...p,
                investmentAmount: landedCost ?? p.investmentAmount,
                investmentRatio: 100,
              }
            : p,
        );
      }
      return partners;
    })(),
    showroomCharge,
    companyOwnerCommission: showroomCharge,
  });
}

const MONEY_WIZARD_STEP_IDS = ["purchase_details", "purchase"] as const;

const BASE_WIZARD_STEPS = [
  { id: "basic", label: "Basic information", short: "Basics", icon: Package },
  { id: "spec", label: "Additional Information", short: "Additional", icon: Layers },
  { id: "media", label: "Images / documents", short: "Media", icon: Camera },
  { id: "purchase_details", label: "Purchase Details", short: "Details", icon: ClipboardList },
  { id: "purchase", label: "Purchase & landed cost", short: "Purchase", icon: Wallet },
] as const;

function defaultProductDraft(): ProductDraft {
  return {
    categoryId: "",
    brandId: null,
    vehicleModelId: null,
    vehicleVersionId: null,
    modelYear: null,
    supplierId: null,
    acquisitionType: "purchased",
    acquisitionDate: (() => {
      const d = new Date();
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${y}-${m}-${day}`;
    })(),
    purchaseType: "local",
    importCountryId: null,
    warehouseId: null,
    name: "",
    slug: "",
    description: "",
    inventoryStatus: "draft",
    purchasePrice: null,
    purchaseCurrency: getActiveCompanyCurrency().code,
    purchaseCurrencyAmount: null,
    purchaseExchangeRate: 1,
    purchaseNotes: "",
    productImages: [],
    productDocuments: [],
    documentCategory: null,
    documentCategoryId: null,
    documentType: "original",
    numberOfDocuments: 0,
    purchaseCostLines: [],
    landedCost: null,
    investorPurchasePrice: null,
    investorPurchaseCurrency: getActiveCompanyCurrency().code,
    investorPurchaseCurrencyAmount: null,
    investorPurchaseExchangeRate: 1,
    investorLandedCost: null,
    salePrice: null,
    dealType: null,
    profitRuleType: null,
    expectedSalePrice: null,
    ownerDemandPrice: null,
    dealPartners: [],
    companyOwnerCommission: companyOwnerDealPartnerRow(),
    showroomCharge: emptyShowroomCharge(),
    attributes: {},
    invoices: [],
    baseInvoiceId: null,
    investorBaseInvoiceId: null,
    importData: null,
  };
}

function mergeWizardDraft(saved: ProductDraft): ProductDraft {
  const raw = saved as ProductDraft & { additionalNotes?: unknown };
  const { additionalNotes: _omit, ...rest } = raw;
  const base = defaultProductDraft();
  return {
    ...base,
    ...rest,
    purchaseType: rest.purchaseType === "import" ? "import" : "local",
    importCountryId: rest.importCountryId ?? null,
    importData: rest.importData ?? base.importData ?? null,
    vehicleModelId: rest.vehicleModelId ?? base.vehicleModelId ?? null,
    vehicleVersionId: rest.vehicleVersionId ?? base.vehicleVersionId ?? null,
    modelYear: rest.modelYear ?? base.modelYear ?? null,
    warehouseId: rest.warehouseId ?? base.warehouseId ?? null,
    inventoryStatus: rest.inventoryStatus ?? base.inventoryStatus ?? "draft",
    attributes: { ...base.attributes, ...(rest.attributes ?? {}) },
    productImages: Array.isArray(rest.productImages) ? rest.productImages : [],
    productDocuments: Array.isArray(rest.productDocuments) ? rest.productDocuments : [],
    documentCategory: rest.documentCategory ?? null,
    documentCategoryId: rest.documentCategoryId ?? null,
    documentType: rest.documentType === "duplicate" ? "duplicate" : "original",
    numberOfDocuments: (() => {
      const n = Number(rest.numberOfDocuments ?? 0);
      if (!Number.isInteger(n) || n < 0) return 0;
      return Math.min(100, n);
    })(),
    purchaseCostLines: Array.isArray(rest.purchaseCostLines) ? rest.purchaseCostLines : [],
    dealPartners: Array.isArray(rest.dealPartners) ? rest.dealPartners : [],
    companyOwnerCommission: rest.showroomCharge ?? rest.companyOwnerCommission ?? emptyShowroomCharge(),
    showroomCharge: rest.showroomCharge ?? rest.companyOwnerCommission ?? emptyShowroomCharge(),
  };
}

function validateBasicStep(d: ProductDraft): boolean {
  if (!d.categoryId?.trim()) {
    toast.error("Select a category to continue.");
    return false;
  }
  if (!d.name?.trim()) {
    toast.error("Enter a product name.");
    return false;
  }
  return true;
}

/** Consignment requires Purchased Amount (> 0) as the deal valuation floor. */
function validateConsignmentPurchaseAmount(d: ProductDraft): boolean {
  if ((d.acquisitionType ?? "purchased") !== "consignment") return true;
  const fromCurrency =
    d.purchaseCurrencyAmount != null && Number.isFinite(Number(d.purchaseCurrencyAmount))
      ? Number(d.purchaseCurrencyAmount)
      : null;
  const fromPrice =
    d.purchasePrice != null && Number.isFinite(Number(d.purchasePrice))
      ? Number(d.purchasePrice)
      : null;
  const amt = fromCurrency ?? fromPrice;
  if (amt == null || !Number.isFinite(amt) || amt <= 0) {
    toast.error("Purchased Amount is required for consignment.");
    return false;
  }
  return true;
}

/** Purchase Details step: acquisition meta + deal type before pricing / landed cost. */
function validatePurchaseDetailsStep(d: ProductDraft): boolean {
  if (!validateConsignmentPurchaseAmount(d)) return false;
  if ((d.acquisitionType ?? "purchased") === "purchased" && d.purchaseType === "import") {
    const countryId = d.importData?.importCountryId ?? d.importCountryId;
    if (countryId == null || String(countryId).trim() === "") {
      toast.error("Import Country is required for import purchase.");
      return false;
    }
  }
  const dealType =
    (d.acquisitionType ?? "purchased") === "consignment"
      ? "owner_vehicle"
      : (d.dealType ?? null);
  if (!dealType) {
    toast.error("Select a deal type.");
    return false;
  }
  const partners = (d.dealPartners ?? []).filter((p) => p.partyId);
  if (dealType === "owner_vehicle" && partners.length < 1) {
    toast.error("Select a vehicle owner.");
    return false;
  }
  if (dealType === "partnership" && partners.length < 1) {
    toast.error("Select at least one partner.");
    return false;
  }
  return true;
}

function validateDocumentMeta(d: ProductDraft): boolean {
  const docs = d.productDocuments ?? [];
  if (!docs.length) return true;
  for (const doc of docs) {
    const copy = doc.documentCopy ?? d.documentType;
    if (copy !== "original" && copy !== "duplicate") {
      toast.error(`Select a document copy for “${doc.filename}”.`);
      return false;
    }
    const n = Number(doc.numberOfPages ?? 0);
    if (!Number.isInteger(n) || n < 0 || n > 100) {
      toast.error(`No. of pages for “${doc.filename}” must be a whole number from 0 to 100.`);
      return false;
    }
  }
  return true;
}

const AddProduct = () => {
  const navigate = useNavigate();
  const { id: routeId } = useParams<{ id: string }>();
  const isEdit = Boolean(routeId);
  const [authUser, setAuthUser] = useState(() => getStoredAuthUser());
  const canAccessEditor = isEdit ? can(authUser, "product_can_update") : can(authUser, "product_can_create");
  const canViewPrices = can(authUser, "product_can_view_prices");
  const canCreatePurchaseCostType = can(authUser, "purchase_cost_type_can_create");
  const canCreateInvestor = can(authUser, "investor_can_create");
  const canCreateSupplier = can(authUser, "supplier_can_create");
  const canCreateDocumentType =
    can(authUser, "document_type_can_create") ||
    can(authUser, "product_can_create") ||
    can(authUser, "product_can_update");
  const canQuickAddCategoryAttribute = can(authUser, "catalog_attribute_can_create");
  const canAssignCategoryAttributes = can(authUser, "category_can_update");

  const [categories, setCategories] = useState<Category[]>([]);
  const [brands, setBrands] = useState<BrandRecord[]>([]);
  const [warehouses, setWarehouses] = useState<WarehouseRecord[]>([]);
  const [countries, setCountries] = useState<CountryOption[]>([]);
  const [parties, setParties] = useState(() => listPartyOptions());
  const [partnerSearch, setPartnerSearch] = useState("");
  const [addInvestorOpen, setAddInvestorOpen] = useState(false);
  const [addSupplierOpen, setAddSupplierOpen] = useState(false);
  const [addOwnerOpen, setAddOwnerOpen] = useState(false);

  useEffect(() => {
    void fetchCountries().then(setCountries).catch(() => setCountries([]));
  }, []);
  const [companyParties, setCompanyParties] = useState(() => listCompanyPartyOptions());
  const [schema, setSchema] = useState<CategoryProductSchema | null>(null);
  const [loadingSchema, setLoadingSchema] = useState(false);
  const [purchaseCostTypesVersion, setPurchaseCostTypesVersion] = useState(0);
  const [purchaseCostCategoriesVersion, setPurchaseCostCategoriesVersion] = useState(0);
  const [documentTypesVersion, setDocumentTypesVersion] = useState(0);
  const [costTypeDialogOpen, setCostTypeDialogOpen] = useState(false);
  const [costTypeDialogLineIndex, setCostTypeDialogLineIndex] = useState<number | null>(null);
  const [costTypeLabel, setCostTypeLabel] = useState("");
  const [costTypeCategoryId, setCostTypeCategoryId] = useState("");
  const [costTypeSaving, setCostTypeSaving] = useState(false);
  const [documentTypeDialogOpen, setDocumentTypeDialogOpen] = useState(false);
  const [documentTypeName, setDocumentTypeName] = useState("");
  const [documentTypeSaving, setDocumentTypeSaving] = useState(false);
  const [quickAddAttributeOpen, setQuickAddAttributeOpen] = useState(false);
  const [quickAddAttributeGroup, setQuickAddAttributeGroup] = useState<QuickAddAttributeGroup | null>(null);
  const [assignCategoryAttributesOpen, setAssignCategoryAttributesOpen] = useState(false);

  const openQuickAddAttribute = useCallback((group?: QuickAddAttributeGroup | null) => {
    setQuickAddAttributeGroup(group ?? null);
    setQuickAddAttributeOpen(true);
  }, []);

  const [wizardStep, setWizardStep] = useState(0);
  const wizardSteps = useMemo(
    () =>
      BASE_WIZARD_STEPS.filter(
        (s) => canViewPrices || !(MONEY_WIZARD_STEP_IDS as readonly string[]).includes(s.id),
      ),
    [canViewPrices],
  );
  const stepCount = wizardSteps.length;
  const currentStepId = wizardSteps[wizardStep]?.id ?? "basic";
  const wizardStepScrollReady = useRef(false);

  useEffect(() => {
    if (!wizardStepScrollReady.current) {
      wizardStepScrollReady.current = true;
      return;
    }
    const main = document.querySelector("main");
    if (main instanceof HTMLElement) {
      main.scrollTo({ top: 0, behavior: "smooth" });
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [wizardStep]);

  const [draft, setDraft] = useState<ProductDraft>(() => defaultProductDraft());
  const [purchaseExchangeRateInput, setPurchaseExchangeRateInput] = useState<string>("1");
  const [purchaseCurrencyAmountInput, setPurchaseCurrencyAmountInput] = useState<string>("");
  const [investorExchangeRateInput, setInvestorExchangeRateInput] = useState<string>("1");
  const [investorCurrencyAmountInput, setInvestorCurrencyAmountInput] = useState<string>("");
  /** Once the user edits investor quote, stop auto-mirroring from supplier. */
  const investorQuoteCustomizedRef = useRef(false);
  const [persistedProductId, setPersistedProductId] = useState<string | null>(null);
  const [saveLoading, setSaveLoading] = useState(false);
  const [pendingCostLineRemoveIndex, setPendingCostLineRemoveIndex] = useState<number | null>(null);

  // Additional Cost Line Modal state
  const [costLineModalOpen, setCostLineModalOpen] = useState(false);
  const [costLineExchangeRateInput, setCostLineExchangeRateInput] = useState<string>("1");
  const [costLineCurrencyAmountInput, setCostLineCurrencyAmountInput] = useState<string>("");
  const [costLineInvestorCurrencyAmountInput, setCostLineInvestorCurrencyAmountInput] = useState<string>("");
  const [costLineInvestorExchangeRateInput, setCostLineInvestorExchangeRateInput] = useState<string>("1");
  const [costLineInvestorTrack, setCostLineInvestorTrack] = useState<{
    currencyAmount: number;
    exchangeRate: number | null;
    invoiceId: string | number | null;
    paidToPartyId: number | null;
  }>({ currencyAmount: 0, exchangeRate: 1, invoiceId: null, paidToPartyId: null });
  /** Once investor cost-line amounts are edited, stop mirroring from supplier. */
  const costLineInvestorQuoteCustomizedRef = useRef(false);
  const [editingCostLineIndex, setEditingCostLineIndex] = useState<number | null>(null);
  const [editingInvestorCostLineIndex, setEditingInvestorCostLineIndex] = useState<number | null>(null);
  const [costTypeSelectKey, setCostTypeSelectKey] = useState(0);
  const [costLineModalForm, setCostLineModalForm] = useState<PurchaseCostLine>(() => ({
    role: "supplier",
    typeId: "",
    amount: 0,
    currencyCode: "PKR",
    currencyAmount: 0,
    exchangeRate: 1,
    paidByPartyId: null,
    paidToPartyId: null,
    costBearerPartyId: "partnership",
    paymentMethod: "cash",
    bankName: null,
    transactionReferenceNo: null,
    payOrderNo: null,
    description: null,
  }));

  // Supplier Invoices Modal state
  const [invoiceModalOpen, setInvoiceModalOpen] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState<ProductInvoice | null>(null);
  const [invoiceForm, setInvoiceForm] = useState<Partial<ProductInvoice>>({});
  /** Dual-track add: files for supplier and/or investor in one save. */
  const [invoiceDualFiles, setInvoiceDualFiles] = useState<{
    supplier: File | string | null;
    investor: File | string | null;
  }>({ supplier: null, investor: null });
  const [invoiceDualParties, setInvoiceDualParties] = useState<{
    supplier: string;
    investor: string;
  }>({ supplier: "", investor: "" });
  /** True when editing supplier+investor together in the dual modal. */
  const [editingInvoicePair, setEditingInvoicePair] = useState(false);

  /** Edit mode: wait for fresh product from API so category + attributes match server (local cache can be stale). */
  const [editReady, setEditReady] = useState(!isEdit);
  const wizardBootstrapped = useRef(false);
  const persistedIdRef = useRef<string | null>(null);
  /** Tracks Base Purchase Cost last used to auto-fill Owner Demand Price. */
  const basePurchaseForOwnerDemandRef = useRef<number | null>(null);

  const draftRef = useRef(draft);
  draftRef.current = draft;

  useEffect(() => {
    persistedIdRef.current = persistedProductId;
  }, [persistedProductId]);

  useEffect(() => {
    setWizardStep((s) => Math.min(s, Math.max(0, stepCount - 1)));
  }, [stepCount]);

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === "auth_user") setAuthUser(getStoredAuthUser());
    };
    const onAuth = () => setAuthUser(getStoredAuthUser());
    window.addEventListener("storage", onStorage);
    window.addEventListener("auth_user_updated", onAuth);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("auth_user_updated", onAuth);
    };
  }, []);

  useEffect(() => {
    if (!canAccessEditor) {
      toast.error("User does not have the right permissions.");
      navigate("/dashboard", { replace: true });
    }
  }, [canAccessEditor, navigate]);

  // New product: always start empty. Do not restore add-product session draft (it showed the previous
  // car’s fields when opening /products/add again in the same browser session).
  useEffect(() => {
    if (isEdit) return;
    if (wizardBootstrapped.current) return;
    wizardBootstrapped.current = true;

    clearAddProductWizard();
    setWizardStep(0);
    setDraft(defaultProductDraft());
    setPersistedProductId(null);
    persistedIdRef.current = null;
    investorQuoteCustomizedRef.current = false;
    setPurchaseCurrencyAmountInput("");
    setPurchaseExchangeRateInput("1");
    setInvestorCurrencyAmountInput("");
    setInvestorExchangeRateInput("1");
  }, [isEdit]);

  useEffect(() => {
    if (!isEdit || !routeId) {
      if (!isEdit) setEditReady(true);
      return;
    }

    let cancelled = false;
    setEditReady(false);

    (async () => {
      let p = getProductById(routeId);
      try {
        const fromApi = await fetchProductFromApi(routeId);
        if (!cancelled && fromApi) p = fromApi;
      } catch (error) {
        if (!(isAxiosError(error) && error.response?.status === 403)) {
          console.error("[AddProduct] Failed to load product for edit", error);
        }
      }

      if (cancelled) return;

      const categoryId = p?.categoryId?.trim() ?? "";
      if (!p || !categoryId) {
        toast.error("Product not found.");
        navigate("/products", { replace: true });
        return;
      }

      if (!wizardBootstrapped.current) wizardBootstrapped.current = true;
      setWizardStep(0);
      setPersistedProductId(routeId);
      persistedIdRef.current = routeId;
      setDraft(
        mergeWizardDraft({
          categoryId: p.categoryId,
          brandId: p.brandId ?? null,
          vehicleModelId: p.vehicleModelId ?? null,
          vehicleVersionId: p.vehicleVersionId ?? null,
          modelYear: p.modelYear ?? null,
          supplierId: p.supplierId ?? null,
          acquisitionType: p.acquisitionType ?? "purchased",
          acquisitionDate: p.acquisitionDate ?? null,
          purchaseType: p.purchaseType ?? "local",
          importCountryId: p.importCountryId ?? null,
          importData: p.importData ?? null,
          warehouseId: p.warehouseId ?? null,
          name: p.name,
          slug: p.slug,
          description: p.description ?? "",
          purchasePrice: p.purchasePrice ?? null,
          purchaseCurrency: p.purchaseCurrency ?? "PKR",
          purchaseCurrencyAmount: p.purchaseCurrencyAmount ?? null,
          purchaseExchangeRate: p.purchaseExchangeRate ?? null,
          purchaseNotes: p.purchaseNotes ?? "",
          purchaseCostLines: p.purchaseCostLines ?? [],
          landedCost: p.landedCost ?? null,
          investorPurchasePrice: p.investorPurchasePrice ?? null,
          investorPurchaseCurrency: p.investorPurchaseCurrency ?? getActiveCompanyCurrency().code,
          investorPurchaseCurrencyAmount: p.investorPurchaseCurrencyAmount ?? null,
          investorPurchaseExchangeRate: p.investorPurchaseExchangeRate ?? 1,
          investorLandedCost: p.investorLandedCost ?? null,
          salePrice: p.salePrice ?? null,
          dealType:
            (p.acquisitionType ?? "purchased") === "consignment"
              ? "owner_vehicle"
              : (p.dealType ?? null),
          profitRuleType: p.profitRuleType ?? null,
          expectedSalePrice: p.expectedSalePrice ?? null,
          ownerDemandPrice: p.ownerDemandPrice ?? null,
          dealPartners: p.dealPartners ?? [],
          companyOwnerCommission: p.showroomCharge ?? p.companyOwnerCommission ?? emptyShowroomCharge(),
          showroomCharge: p.showroomCharge ?? p.companyOwnerCommission ?? emptyShowroomCharge(),
          productImages: p.productImages ?? [],
          productDocuments: p.productDocuments ?? [],
          documentCategory: p.documentCategory ?? null,
          documentCategoryId: p.documentCategoryId ?? null,
          documentType: p.documentType === "duplicate" ? "duplicate" : "original",
          numberOfDocuments: (() => {
            const n = Number(p.numberOfDocuments ?? 0);
            if (!Number.isInteger(n) || n < 0) return 0;
            return Math.min(100, n);
          })(),
          attributes: p.attributes ?? {},
          inventoryStatus: p.inventoryStatus,
          invoices: p.invoices ?? [],
          baseInvoiceId: p.baseInvoiceId ?? null,
          investorBaseInvoiceId: p.investorBaseInvoiceId ?? null,
        }),
      );
      setPurchaseExchangeRateInput(p.purchaseExchangeRate != null ? String(p.purchaseExchangeRate) : "1");
      setPurchaseCurrencyAmountInput(p.purchaseCurrencyAmount != null ? String(p.purchaseCurrencyAmount) : "");
      setInvestorExchangeRateInput(
        p.investorPurchaseExchangeRate != null ? String(p.investorPurchaseExchangeRate) : "1",
      );
      setInvestorCurrencyAmountInput(
        p.investorPurchaseCurrencyAmount != null ? String(p.investorPurchaseCurrencyAmount) : "",
      );
      {
        const sameNum = (a: number | null | undefined, b: number | null | undefined) => {
          if (a == null && b == null) return true;
          if (a == null || b == null) return false;
          return Number(a) === Number(b);
        };
        const supplierCurrency = p.purchaseCurrency ?? getActiveCompanyCurrency().code;
        const investorCurrency = p.investorPurchaseCurrency ?? getActiveCompanyCurrency().code;
        const investorEmpty =
          (p.investorPurchaseCurrencyAmount == null || !Number.isFinite(Number(p.investorPurchaseCurrencyAmount))) &&
          (p.investorPurchasePrice == null || !Number.isFinite(Number(p.investorPurchasePrice)));
        const mirrorsSupplier =
          investorEmpty ||
          (investorCurrency === supplierCurrency &&
            sameNum(p.investorPurchaseCurrencyAmount, p.purchaseCurrencyAmount) &&
            sameNum(p.investorPurchaseExchangeRate ?? 1, p.purchaseExchangeRate ?? 1));
        investorQuoteCustomizedRef.current = !mirrorsSupplier;
      }
      setEditReady(true);
    })();

    return () => {
      cancelled = true;
    };
  }, [isEdit, routeId, navigate]);

  const docInputRef = useRef<HTMLInputElement>(null);
  const [docDropActive, setDocDropActive] = useState(false);
  const [vehiclePickerOpen, setVehiclePickerOpen] = useState(false);
  const [currencies, setCurrencies] = useState<Currency[]>(getCachedCurrencies());

  useEffect(() => {
    let mounted = true;
    const loadCatalogRefs = async () => {
      try {
        const cats = await listCategories();
        if (mounted) setCategories(cats);
      } catch (error) {
        console.error("[AddProduct] Categories load failed", error);
        if (mounted) {
          setCategories([]);
          toast.error("Could not load categories.");
        }
      }
      try {
        await syncBrandsFromApi();
        if (mounted) setBrands(listBrandRecords());
      } catch (error) {
        console.error("[AddProduct] Brands load failed", error);
        if (mounted) {
          setBrands(listBrandRecords());
          toast.error("Could not load brands. Showing cached list if available.");
        }
      }
      try {
        await syncWarehousesFromApi({ activeOnly: true });
        if (mounted) setWarehouses(listWarehouseRecords());
      } catch (error) {
        if (!(isAxiosError(error) && error.response?.status === 403)) {
          console.error("[AddProduct] Locations load failed", error);
        }
        if (mounted) {
          setWarehouses(listWarehouseRecords());
        }
      }
      try {
        await syncPartiesFromApi();
        await syncPartiesFromApi({ isCompany: true });
        if (mounted) {
          setParties(listPartyOptions());
          setCompanyParties(listCompanyPartyOptions());
        }
      } catch (error) {
        console.error("[AddProduct] Parties load failed", error);
        if (mounted) {
          setParties(listPartyOptions());
          setCompanyParties(listCompanyPartyOptions());
        }
      }
      fetchCurrencies().then((rows) => { if (mounted) setCurrencies(rows); });
      try {
        await syncProductsFromApi();
        if (!isEdit && mounted) {
          const wid = persistedIdRef.current;
          if (wid && !getProductById(wid)) {
            persistedIdRef.current = null;
            setPersistedProductId(null);
            const snap = loadAddProductWizard();
            if (snap) {
              saveAddProductWizard({
                step: snap.step,
                draft: snap.draft,
              });
            }
          }
        }
      } catch (error) {
        if (!(isAxiosError(error) && error.response?.status === 403)) {
          console.error("[AddProduct] Products sync failed", error);
        }
      }
      try {
        await syncPurchaseCostCategoriesFromApi();
        if (mounted) setPurchaseCostCategoriesVersion((v) => v + 1);
      } catch (error) {
        console.error("[AddProduct] Purchase cost categories sync failed", error);
      }
      try {
        await syncPurchaseCostTypesFromApi();
        if (mounted) setPurchaseCostTypesVersion((v) => v + 1);
      } catch (error) {
        console.error("[AddProduct] Purchase cost types sync failed", error);
      }
      try {
        await syncDocumentTypesFromApi();
        if (mounted) setDocumentTypesVersion((v) => v + 1);
      } catch (error) {
        console.error("[AddProduct] Document types sync failed", error);
      }
    };
    void loadCatalogRefs();
    return () => {
      mounted = false;
    };
  }, []);

  const refreshCategorySchema = useCallback(async () => {
    if (!draft.categoryId) {
      setSchema(null);
      return;
    }
    setLoadingSchema(true);
    try {
      const s = await getCategoryProductSchema(draft.categoryId);
      setSchema(s);
    } catch {
      setSchema(null);
    } finally {
      setLoadingSchema(false);
    }
  }, [draft.categoryId]);

  useEffect(() => {
    void refreshCategorySchema();
  }, [refreshCategorySchema]);

  const groups = useMemo(() => sortByOrder(schema?.groups ?? []), [schema]);
  const basicSpecGroups = useMemo(() => groups.filter(isBasicSpecsAttributeGroup), [groups]);
  const specOnlyGroups = useMemo(() => groups.filter((g) => !isBasicSpecsAttributeGroup(g)), [groups]);
  const allSchemaAttributes = useMemo(() => flattenSchemaAttributes(groups), [groups]);
  const assemblyAttrsList = useMemo(() => collectAssemblyAttributes(groups), [groups]);

  const updateProductAttribute = useCallback(
    (code: string, value: AttributeValue, attr?: Attribute) => {
      setDraft((d) => {
        let attributes = { ...d.attributes, [code]: value };
        if (attr && (attr.type === "select" || attr.type === "multiselect")) {
          attributes = pruneHiddenAttributeValues(attributes, groups);
        }
        return { ...d, attributes };
      });
    },
    [groups],
  );

  const handleAttributeOptionsUpdated = useCallback((attributeId: string, options: AttributeOption[]) => {
    setSchema((prev) => (prev ? mergeAttributeOptionsInSchema(prev, attributeId, options) : prev));
  }, []);

  /**
   * Main block: non-boolean fields from additional groups + Assembly (after Transmission order).
   * Features: boolean Yes/No attributes plus multiselect checklists.
   */
  const { additionalMainAttrs, additionalFeatureBooleans, additionalFeatureMultiselects } = useMemo(() => {
    const main: Attribute[] = [];
    const featureBool: Attribute[] = [];
    const featureMulti: Attribute[] = [];
    const stableOrderMain = new Map<string, number>();
    const stableOrderFeature = new Map<string, number>();
    let posMain = 0;
    let posFeat = 0;
    for (const g of specOnlyGroups) {
      for (const a of sortByOrder(g.attributes)) {
        if (isAssemblyAttribute(a)) continue;
        if (!isAttributeVisible(a, allSchemaAttributes, draft.attributes)) continue;
        if (a.type === "multiselect") {
          featureMulti.push(a);
          stableOrderFeature.set(a.id, posFeat++);
        } else if (a.type === "boolean") {
          featureBool.push(a);
          stableOrderFeature.set(a.id, posFeat++);
        } else {
          main.push(a);
          stableOrderMain.set(a.id, posMain++);
        }
      }
    }
    for (const a of assemblyAttrsList) {
      if (!isAttributeVisible(a, allSchemaAttributes, draft.attributes)) continue;
      main.push(a);
      stableOrderMain.set(a.id, posMain++);
    }
    featureMulti.sort((a, b) => (stableOrderFeature.get(a.id) ?? 0) - (stableOrderFeature.get(b.id) ?? 0));
    return {
      additionalMainAttrs: sortAdditionalMainAttrs(main, stableOrderMain),
      additionalFeatureBooleans: sortFeatureBooleanAttrs(featureBool, stableOrderFeature),
      additionalFeatureMultiselects: featureMulti,
    };
  }, [specOnlyGroups, assemblyAttrsList, allSchemaAttributes, draft.attributes]);

  const handleToggleAllFeatures = useCallback(
    (selectAll: boolean) => {
      setDraft((d) => {
        let attributes = { ...d.attributes };

        for (const attr of additionalFeatureBooleans) {
          attributes[attr.code] = selectAll;
        }

        for (const attr of additionalFeatureMultiselects) {
          if (selectAll) {
            const allOptionValues = (attr.options ?? [])
              .map((o) => String(o.value ?? ""))
              .filter(Boolean);
            attributes[attr.code] = allOptionValues;
          } else {
            attributes[attr.code] = [];
          }
        }

        attributes = pruneHiddenAttributeValues(attributes, groups);
        return { ...d, attributes };
      });
    },
    [additionalFeatureBooleans, additionalFeatureMultiselects, groups],
  );

  const handleToggleMultiselectGroup = useCallback(
    (attr: Attribute, selectAll: boolean) => {
      setDraft((d) => {
        let attributes = { ...d.attributes };
        if (selectAll) {
          attributes[attr.code] = (attr.options ?? [])
            .map((o) => String(o.value ?? ""))
            .filter(Boolean);
        } else {
          attributes[attr.code] = [];
        }
        attributes = pruneHiddenAttributeValues(attributes, groups);
        return { ...d, attributes };
      });
    },
    [groups],
  );

  const areAllFeaturesSelected = useMemo(() => {
    const hasAnyFeatures =
      additionalFeatureBooleans.length > 0 || additionalFeatureMultiselects.length > 0;
    if (!hasAnyFeatures) return false;

    const allBoolsChecked = additionalFeatureBooleans.every((attr) =>
      Boolean(draft.attributes[attr.code]),
    );
    if (!allBoolsChecked) return false;

    const allMultisFullyChecked = additionalFeatureMultiselects.every((attr) => {
      const selected = Array.isArray(draft.attributes[attr.code])
        ? (draft.attributes[attr.code] as unknown[])
        : [];
      const validOptions = (attr.options ?? [])
        .map((o) => String(o.value ?? ""))
        .filter(Boolean);
      return (
        validOptions.length > 0 &&
        validOptions.every((val) => selected.includes(val))
      );
    });

    return allMultisFullyChecked;
  }, [additionalFeatureBooleans, additionalFeatureMultiselects, draft.attributes]);

  const purchaseCostTypes = useMemo(
    () => listPurchaseCostTypeRecords(),
    [wizardStep, purchaseCostTypesVersion],
  );
  const purchaseCostCategories = useMemo(
    () => listPurchaseCostCategories(),
    [wizardStep, purchaseCostCategoriesVersion],
  );

  const documentTypes = useMemo(
    () => listDocumentTypeRecords(),
    [wizardStep, documentTypesVersion],
  );

  const additionalSubtotal = useMemo(
    () => sumPurchaseCostLinesForRole(draft.purchaseCostLines, "supplier"),
    [draft.purchaseCostLines],
  );
  const investorAdditionalCostsTotal = useMemo(
    () => sumPurchaseCostLinesForRole(draft.purchaseCostLines, "investor"),
    [draft.purchaseCostLines],
  );

  const supplierCostBreakdown = useMemo(() => {
    const lines = filterCostLinesByRole(draft.purchaseCostLines, "supplier");
    const activeBase = getActiveCompanyCurrency().code;
    type CurrencyTotals = Map<string, number>;
    type Acc = {
      totalSupplierCost: number;
      bySupplier: Map<string, { name: string; amount: number; count: number; currencyTotals: CurrencyTotals; hasFx: boolean }>;
      noSupplier: { amount: number; count: number; currencyTotals: CurrencyTotals; hasFx: boolean };
    };
    const acc: Acc = {
      totalSupplierCost: 0,
      bySupplier: new Map(),
      noSupplier: { amount: 0, count: 0, currencyTotals: new Map(), hasFx: false },
    };
    for (const line of lines) {
      if (isDiscountCostLine(line)) continue;
      const amt = line.amount != null && Number.isFinite(Number(line.amount)) ? Number(line.amount) : 0;
      const sid = line.paidToPartyId ? String(line.paidToPartyId) : "";
      const lineCurrency = line.currencyCode ?? activeBase;
      const lineCurrencyAmt = line.currencyAmount != null && Number.isFinite(Number(line.currencyAmount)) ? Number(line.currencyAmount) : amt;
      const lineHasFx = lineCurrency !== activeBase;

      if (!sid) {
        acc.noSupplier.amount += amt;
        acc.noSupplier.count += 1;
        const prev = acc.noSupplier.currencyTotals.get(lineCurrency) ?? 0;
        acc.noSupplier.currencyTotals.set(lineCurrency, prev + lineCurrencyAmt);
        if (lineHasFx) acc.noSupplier.hasFx = true;
        continue;
      }
      const party = parties.find((p) => String(p.id) === sid);
      const name = party?.name?.trim() || party?.contact_person?.trim() || `Supplier #${sid}`;
      const existing = acc.bySupplier.get(sid);
      if (existing) {
        existing.amount += amt;
        existing.count += 1;
        const prev = existing.currencyTotals.get(lineCurrency) ?? 0;
        existing.currencyTotals.set(lineCurrency, prev + lineCurrencyAmt);
        if (lineHasFx) existing.hasFx = true;
      } else {
        const ct: CurrencyTotals = new Map();
        ct.set(lineCurrency, lineCurrencyAmt);
        acc.bySupplier.set(sid, { name, amount: amt, count: 1, currencyTotals: ct, hasFx: lineHasFx });
      }
      acc.totalSupplierCost += amt;
    }
    if (acc.noSupplier.count > 0) acc.totalSupplierCost += acc.noSupplier.amount;
    const rows = Array.from(acc.bySupplier.entries())
      .map(([id, r]) => ({ id, name: r.name, amount: r.amount, count: r.count, hasSupplier: true as const, currencyTotals: r.currencyTotals, hasFx: r.hasFx }))
      .sort((a, b) => b.amount - a.amount);
    if (acc.noSupplier.count > 0) {
      rows.push({
        id: "__no_supplier__",
        name: "No Supplier Assigned",
        amount: acc.noSupplier.amount,
        count: acc.noSupplier.count,
        hasSupplier: false as const,
        currencyTotals: acc.noSupplier.currencyTotals,
        hasFx: acc.noSupplier.hasFx,
      });
    }
    return { rows, totalSupplierCost: acc.totalSupplierCost };
  }, [draft.purchaseCostLines, parties]);

  const landedPreview = useMemo(
    () => computeLandedCostForRole(draft.purchasePrice, draft.purchaseCostLines, "supplier"),
    [draft.purchasePrice, draft.purchaseCostLines],
  );
  const investorLandedPreview = useMemo(
    () =>
      computeLandedCostForRole(draft.investorPurchasePrice, draft.purchaseCostLines, "investor"),
    [draft.investorPurchasePrice, draft.purchaseCostLines],
  );
  /** Deal / investor-facing total prefers investor track when set. */
  const dealLandedPreview = investorLandedPreview ?? landedPreview;

  const expectedSalePrice =
    draft.salePrice != null && Number.isFinite(Number(draft.salePrice))
      ? Number(draft.salePrice)
      : draft.expectedSalePrice != null && Number.isFinite(Number(draft.expectedSalePrice))
        ? Number(draft.expectedSalePrice)
        : null;

  const effectiveDealType =
    (draft.acquisitionType ?? "purchased") === "consignment"
      ? "owner_vehicle"
      : (draft.dealType ?? "purchased");

  /**
   * Investor dual-track UI (invoice / quote / purchase amounts).
   * Hidden when Acquisition = purchased and Deal Type = purchased; also hidden for consignment
   * (consignment has no product-level supplier / investor purchase quote columns).
   */
  const showInvestorPurchaseTrack =
    (draft.acquisitionType ?? "purchased") !== "consignment" && effectiveDealType !== "purchased";

  /**
   * Add Cost Line modal + cost-line list: Supplier (actual) | Investor (quoted).
   * Shown for Owner / Partnership purchased deals and for consignment (Pay To supplier left,
   * investor quote right). Not shown for plain Purchased deal type.
   */
  const showCostLineInvestorTrack =
    (draft.acquisitionType ?? "purchased") === "consignment" || showInvestorPurchaseTrack;

  /** Consignment Purchased Amount entered (> 0) — unlocks full showroom charge types. */
  const hasConsignmentPurchaseAmount = useMemo(() => {
    const fromInput =
      purchaseCurrencyAmountInput.trim() !== ""
        ? Number(purchaseCurrencyAmountInput)
        : null;
    const fromDraft =
      draft.purchaseCurrencyAmount != null && Number.isFinite(Number(draft.purchaseCurrencyAmount))
        ? Number(draft.purchaseCurrencyAmount)
        : draft.purchasePrice != null && Number.isFinite(Number(draft.purchasePrice))
          ? Number(draft.purchasePrice)
          : null;
    const amt = fromInput != null && Number.isFinite(fromInput) ? fromInput : fromDraft;
    return amt != null && Number.isFinite(amt) && amt > 0;
  }, [purchaseCurrencyAmountInput, draft.purchaseCurrencyAmount, draft.purchasePrice]);

  const showroomCommissionBase = useMemo(
    () =>
      showroomCommissionPreviewBase({
        commissionType: (draft.showroomCharge ?? draft.companyOwnerCommission)?.commissionType,
        dealType: effectiveDealType,
        profitRuleType: draft.profitRuleType,
        basePurchase: landedPreview,
        expectedSalePrice,
        netProfit:
          expectedSalePrice != null && landedPreview != null
            ? Number((expectedSalePrice - landedPreview).toFixed(2))
            : null,
      }),
    [
      draft.showroomCharge,
      draft.companyOwnerCommission,
      effectiveDealType,
      draft.profitRuleType,
      landedPreview,
      expectedSalePrice,
    ],
  );

  // Partnership Investment Amount / Ratio % pool = investor-quoted landed when set,
  // otherwise supplier landed (same preference as dealLandedPreview).
  const partnerInvestmentPool = useMemo(
    () =>
      remainingInvestmentPool(
        dealLandedPreview,
        null,
        effectiveDealType,
        draft.profitRuleType,
      ),
    [dealLandedPreview, effectiveDealType, draft.profitRuleType],
  );

  const isOwnerSurplus = isOwnerVehicleSurplusDeal(effectiveDealType, draft.profitRuleType);


  // Owner Vehicle + Owner Surplus: default Owner Demand Price from Base Purchase Cost.
  // Stops auto-sync once the user changes Owner Demand away from the tracked Base Purchase Cost.
  useEffect(() => {
    if (!isOwnerSurplus) {
      basePurchaseForOwnerDemandRef.current = null;
      return;
    }
    const basePurchase =
      draft.purchasePrice != null && Number.isFinite(Number(draft.purchasePrice))
        ? Number(draft.purchasePrice)
        : null;
    if (basePurchase == null) return;

    // Recognize values created by the previous Expected Sale Price auto-fill behavior.
    const legacyExpectedSale =
      draft.salePrice != null && Number.isFinite(Number(draft.salePrice))
        ? Number(draft.salePrice)
        : draft.expectedSalePrice != null && Number.isFinite(Number(draft.expectedSalePrice))
          ? Number(draft.expectedSalePrice)
          : null;

    const current =
      draft.ownerDemandPrice != null && Number.isFinite(Number(draft.ownerDemandPrice))
        ? Number(draft.ownerDemandPrice)
        : null;
    const lastAutoBase = basePurchaseForOwnerDemandRef.current;

    // Manual override: ODP differs from the current/last base and legacy auto-filled sale price,
    // or the field was cleared (current is null) after being auto-populated (lastAutoBase is not null).
    if (
      (current != null &&
        current !== basePurchase &&
        current !== legacyExpectedSale &&
        (lastAutoBase == null || current !== lastAutoBase)) ||
      (current == null && lastAutoBase != null)
    ) {
      return;
    }

    basePurchaseForOwnerDemandRef.current = basePurchase;
    if (current === basePurchase) return;
    setDraft((d) => ({ ...d, ownerDemandPrice: basePurchase }));
  }, [
    isOwnerSurplus,
    draft.purchasePrice,
    draft.salePrice,
    draft.expectedSalePrice,
    draft.ownerDemandPrice,
  ]);

  // Default Expected Sale Price from Total Landed Cost + 10% increment.
  // Stops auto-sync once the user changes Expected Sale Price away from the tracked target.
  const landedForExpectedSaleRef = useRef<number | null>(null);
  useEffect(() => {
    if (dealLandedPreview == null) return;
    const targetSalePrice = Number((dealLandedPreview * 1.1).toFixed(2));

    const current =
      draft.expectedSalePrice != null && Number.isFinite(Number(draft.expectedSalePrice))
        ? Number(draft.expectedSalePrice)
        : null;
    const lastAutoTarget = landedForExpectedSaleRef.current;

    if (
      (current != null && current !== targetSalePrice && (lastAutoTarget == null || current !== lastAutoTarget)) ||
      (current == null && lastAutoTarget != null)
    ) {
      return;
    }

    landedForExpectedSaleRef.current = targetSalePrice;
    if (current === targetSalePrice) return;
    setDraft((d) => ({ ...d, expectedSalePrice: targetSalePrice }));
  }, [dealLandedPreview, draft.expectedSalePrice]);

  // Default Deal Partner Investment Amount from Total Landed Cost.
  // Stops auto-sync once the user changes Investment away from the tracked Landed Cost.
  // Purchased deals do not use Deal Partners — skip so we don't create a party-less invested row.
  // For owner_vehicle, there is no manual investment input in the UI; the investor receivable
  // must always track the Total Landed Cost (prefer investor-track landed when set).
  const landedForPartnerInvestRef = useRef<number | null>(null);
  useEffect(() => {
    if (dealLandedPreview == null) return;
    if (effectiveDealType === "purchased") return;

    const partners = draft.dealPartners ?? [];
    if (partners.length === 0) return;

    if (partners.length === 1) {
      const p = partners[0];
      const current = p.investmentAmount != null && Number.isFinite(Number(p.investmentAmount)) ? Number(p.investmentAmount) : null;
      const lastAutoTarget = landedForPartnerInvestRef.current;

      if (effectiveDealType !== "owner_vehicle") {
        if (
          (current != null && current !== dealLandedPreview && (lastAutoTarget == null || current !== lastAutoTarget)) ||
          (current == null && lastAutoTarget != null)
        ) {
          return;
        }
      }

      landedForPartnerInvestRef.current = dealLandedPreview;
      if (current === dealLandedPreview) return;

      const updated = partners.map((row) => ({
        ...row,
        investmentAmount: dealLandedPreview,
        investmentRatio: 100,
      }));
      setDraft((d) => ({ ...d, dealPartners: updated }));
    }
  }, [dealLandedPreview, draft.dealPartners, effectiveDealType]);

  // Owner Vehicle + Owner Surplus:
  // - Showroom Charges = Profit % 100%
  // - Partner Investment = Total Landed Cost; partner profit override is cleared
  useEffect(() => {
    if (!isOwnerSurplus) return;

    const esp =
      draft.salePrice != null && Number.isFinite(Number(draft.salePrice))
        ? Number(draft.salePrice)
        : draft.expectedSalePrice != null && Number.isFinite(Number(draft.expectedSalePrice))
          ? Number(draft.expectedSalePrice)
          : null;

    const commissionOk =
      (draft.showroomCharge ?? draft.companyOwnerCommission)?.commissionType === "profit_percentage" &&
      Number((draft.showroomCharge ?? draft.companyOwnerCommission)?.commissionValue) === 100;
    const partnersOk = ownerVehicleSurplusPartnersMatch(
      draft.dealPartners,
      landedPreview ?? draft.purchasePrice,
      esp,
      draft.ownerDemandPrice,
    );
    if (commissionOk && partnersOk) return;

    setDraft((d) => {
      const expectedSale =
        d.salePrice != null && Number.isFinite(Number(d.salePrice))
          ? Number(d.salePrice)
          : d.expectedSalePrice != null && Number.isFinite(Number(d.expectedSalePrice))
            ? Number(d.expectedSalePrice)
            : null;

      const prev = d.showroomCharge ?? d.companyOwnerCommission ?? emptyShowroomCharge();
      const showroomCharge = emptyShowroomCharge({
        partyId: prev.partyId,
        partyName: prev.partyName,
        commissionType: "profit_percentage",
        commissionValue: 100,
      });

      return {
        ...d,
        showroomCharge,
        companyOwnerCommission: showroomCharge,
        dealPartners: applyOwnerVehicleSurplusPartnerDefaults({
          partners: d.dealPartners,
          totalLandedCost: landedPreview,
          basePurchase: landedPreview ?? d.purchasePrice,
          expectedSalePrice: expectedSale,
          ownerDemandPrice: d.ownerDemandPrice,
        }),
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isOwnerSurplus,
    landedPreview,
    draft.purchasePrice,
    draft.salePrice,
    draft.expectedSalePrice,
    draft.ownerDemandPrice,
    draft.dealPartners?.length,
    (draft.dealPartners ?? []).map((p) => p.key).join("|"),
  ]);

  // Cap partner investments to investor-quoted (deal) landed when the pool shrinks; keep ratios in sync.
  // Manual Investment Amount edits are preserved (no equal auto-split).
  useEffect(() => {
    if (isOwnerSurplus) return;
    const partners = draft.dealPartners ?? [];
    if (partners.length === 0) return;
    const pool = partnerInvestmentPool;
    if (pool == null || !Number.isFinite(pool)) {
      const synced = withRecalculatedInvestmentRatios(partners, pool);
      const drifted = synced.some((row, i) => {
        const cur = partners[i];
        if (!cur) return true;
        const a = cur.investmentRatio;
        const b = row.investmentRatio;
        if (a == null && b == null) return false;
        if (a == null || b == null) return true;
        return Math.abs(Number(a) - Number(b)) > 0.0005;
      });
      if (drifted) setDraft((d) => ({ ...d, dealPartners: synced }));
      return;
    }

    const total = investmentAmountTotal(partners);
    if (total <= pool + 0.009) {
      const synced = withRecalculatedInvestmentRatios(partners, pool);
      const drifted = synced.some((row, i) => {
        const cur = partners[i];
        if (!cur) return true;
        const a = cur.investmentRatio;
        const b = row.investmentRatio;
        if (a == null && b == null) return false;
        if (a == null || b == null) return true;
        return Math.abs(Number(a) - Number(b)) > 0.0005;
      });
      if (drifted) setDraft((d) => ({ ...d, dealPartners: synced }));
      return;
    }

    // Shrink from the last partners until the total fits the investor-quoted / deal landed pool.
    let next = [...partners];
    let excess = Number((total - pool).toFixed(2));
    for (let i = next.length - 1; i >= 0 && excess > 0.009; i--) {
      const current = Number(next[i]?.investmentAmount) || 0;
      if (current <= 0) continue;
      const reduce = Math.min(current, excess);
      next = applyPartnerInvestmentAmountEdit(next, i, Number((current - reduce).toFixed(2)), pool);
      excess = Number((excess - reduce).toFixed(2));
    }
    setDraft((d) => ({ ...d, dealPartners: withRecalculatedInvestmentRatios(next, pool) }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isOwnerSurplus,
    partnerInvestmentPool,
    draft.dealPartners?.length,
    (draft.dealPartners ?? []).map((p) => `${p.key}:${p.investmentAmount ?? ""}`).join("|"),
  ]);

  // Showroom Charges (Fixed/%) and Deal Partner Profit % are independent —
  // neither side auto-rewrites the other.

  // Partnership: default Profit % to an equal split of 100% among selected partners.
  // Respects existing values when every selected partner already has a Profit %.
  useEffect(() => {
    if (!isShowroomIndependentOfPartnerProfits(effectiveDealType, draft.profitRuleType)) return;
    const partners = draft.dealPartners ?? [];
    const selected = partners.filter((p) => p.partyId);
    if (selected.length === 0) return;
    if (partnerProfitsMatchRemaining(partners, 100)) return;
    const allHaveProfit = selected.every(
      (p) =>
        (p.profitType === "percentage" || p.profitType == null) &&
        p.profitValue != null &&
        Number.isFinite(Number(p.profitValue)),
    );
    if (allHaveProfit) return;
    setDraft((d) => ({
      ...d,
      dealPartners: applyRemainingProfitPercentToPartners(d.dealPartners, 100),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    effectiveDealType,
    draft.profitRuleType,
    (draft.dealPartners ?? []).map((p) => `${p.key}:${p.partyId ?? ""}`).join("|"),
  ]);

  const categoryLabel = schema?.category.name?.trim() || "Product";
  const productName = draft.name?.trim() || "";
  const title = isEdit
    ? productName
      ? `Edit ${categoryLabel} — ${productName}`
      : `Edit ${categoryLabel}`
    : schema
      ? `Add New ${categoryLabel}`
      : "Add Product";

  const productImages = draft.productImages ?? [];
  const productDocuments = draft.productDocuments ?? [];
  const isEditLocked =
    isEdit && (draft.inventoryStatus === "sold" || draft.inventoryStatus === "reserved");

  const persistSnapshot = (step: number, d: ProductDraft, productId: string | null) => {
    if (isEdit) return;
    saveAddProductWizard({
      step,
      draft: stripLocalFilesFromDraft(savePayload(d)),
      ...(productId ? { persistedProductId: productId } : {}),
    });
  };

  const handleCancel = () => {
    if (isEdit) {
      navigate(routeId ? `/products/${routeId}` : "/products");
      return;
    }
    const id = persistedIdRef.current;
    if (id) {
      const p = getProductById(id);
      if (p && (p.inventoryStatus ?? "draft") === "draft") {
        void deleteProduct(id).catch(() => {
          /* best-effort cleanup */
        });
      }
    }
    clearAddProductWizard();
    navigate("/products");
  };

  const goNext = async () => {
    if (saveLoading || isEditLocked) return;
    const d = draftRef.current;
    if (currentStepId === "basic") {
      if (!validateBasicStep(d)) return;
      if (
        !validateVisibleRequiredAttributes(d, basicSpecGroups, (msg) => toast.error(msg), (a) =>
          isAssemblyAttribute(a),
        )
      ) {
        return;
      }
    }
    if (currentStepId === "spec") {
      if (!validateAdditionalInformationStep(d, specOnlyGroups, assemblyAttrsList, allSchemaAttributes)) return;
    }
    if (currentStepId === "media") {
      if (!validateDocumentMeta(d)) return;
    }
    if (currentStepId === "purchase_details") {
      if (!validatePurchaseDetailsStep(d)) return;
    }
    if (currentStepId === "purchase") {
      if (!validateConsignmentPurchaseAmount(d)) return;
      const dealTypeForValidate =
        (d.acquisitionType ?? "purchased") === "consignment"
          ? "owner_vehicle"
          : (d.dealType ?? "purchased");
      const dealError = validateDealDraft({
        dealType: dealTypeForValidate,
        profitRuleType: d.profitRuleType,
        ownerDemandPrice: d.ownerDemandPrice,
        dealPartners: d.dealPartners ?? [],
        showroomCharge: d.showroomCharge ?? d.companyOwnerCommission,
        basePurchaseCost:
          computeLandedCostForRole(d.investorPurchasePrice, d.purchaseCostLines, "investor") ??
          computeLandedCostForRole(d.purchasePrice, d.purchaseCostLines, "supplier"),
      });
      if (dealError) {
        toast.error(dealError);
        return;
      }
    }
    const next = Math.min(wizardStep + 1, stepCount - 1);
    if (next === wizardStep) return;
    setSaveLoading(true);
    try {
      // Purchase Details: persist only fields on this step (do not overwrite invoices/costs/etc).
      if (currentStepId === "purchase_details") {
        const existingId = isEdit ? routeId : persistedIdRef.current;
        if (!existingId) throw new Error("Missing product id");
        const updated = await updateProductPurchaseDetails(existingId, d);
        // Keep local draft aligned with what Purchase Details just persisted (esp. consignment → owner_vehicle + partners).
        setDraft((prev) => ({
          ...prev,
          acquisitionType: updated.acquisitionType ?? prev.acquisitionType,
          dealType:
            (updated.acquisitionType ?? prev.acquisitionType) === "consignment"
              ? "owner_vehicle"
              : (updated.dealType ?? prev.dealType),
          dealPartners:
            updated.dealPartners && updated.dealPartners.length > 0
              ? updated.dealPartners
              : prev.dealPartners,
          purchasePrice: updated.purchasePrice ?? prev.purchasePrice,
          purchaseCurrencyAmount: updated.purchaseCurrencyAmount ?? prev.purchaseCurrencyAmount,
        }));
        if (!isEdit) {
          setPersistedProductId(updated.id);
          persistedIdRef.current = updated.id;
          persistSnapshot(next, d, updated.id);
        }
        setWizardStep(next);
        toast.success(isEdit ? "Saved." : "Saved to catalog.");
        return;
      }

      // Edit: keep existing status. Add: stay Draft until an explicit status action elsewhere.
      const normalized = savePayload(
        isEdit ? d : { ...d, inventoryStatus: "draft" },
      );
      if (isEdit) {
        if (!routeId) throw new Error("Missing product id");
        await updateProduct(routeId, normalized);
        setWizardStep(next);
        toast.success("Saved.");
      } else {
        const { id } = await persistProductWizardProgress(normalized, persistedIdRef.current);
        setPersistedProductId(id);
        persistedIdRef.current = id;
        persistSnapshot(next, d, id);
        setWizardStep(next);
        toast.success("Saved to catalog.");
      }
    } catch (e) {
      toast.error(catalogProductErrorMessage(e));
    } finally {
      setSaveLoading(false);
    }
  };

  const goBack = () => {
    const d = draftRef.current;
    const prev = Math.max(0, wizardStep - 1);
    if (prev === wizardStep) return;
    persistSnapshot(prev, d, persistedIdRef.current);
    setWizardStep(prev);
  };

  const finishWizard = async () => {
    if (saveLoading || isEditLocked) return;
    const d = draftRef.current;
    if (!validateBasicStep(d)) {
      setWizardStep(0);
      return;
    }
    if (
      !validateVisibleRequiredAttributes(d, basicSpecGroups, (msg) => toast.error(msg), (a) =>
        isAssemblyAttribute(a),
      )
    ) {
      setWizardStep(0);
      return;
    }
    if (!validateAdditionalInformationStep(d, specOnlyGroups, assemblyAttrsList, allSchemaAttributes)) {
      const specIdx = wizardSteps.findIndex((s) => s.id === "spec");
      if (specIdx >= 0) setWizardStep(specIdx);
      return;
    }
    if (!validateDocumentMeta(d)) {
      const mediaIdx = wizardSteps.findIndex((s) => s.id === "media");
      if (mediaIdx >= 0) setWizardStep(mediaIdx);
      return;
    }
    if (canViewPrices) {
      if (!validatePurchaseDetailsStep(d)) {
        const detailsIdx = wizardSteps.findIndex((s) => s.id === "purchase_details");
        if (detailsIdx >= 0) setWizardStep(detailsIdx);
        return;
      }
      const dealTypeForValidate =
        (d.acquisitionType ?? "purchased") === "consignment"
          ? "owner_vehicle"
          : (d.dealType ?? "purchased");
      const dealError = validateDealDraft({
        dealType: dealTypeForValidate,
        profitRuleType: d.profitRuleType,
        ownerDemandPrice: d.ownerDemandPrice,
        dealPartners: d.dealPartners ?? [],
        showroomCharge: d.showroomCharge ?? d.companyOwnerCommission,
        basePurchaseCost:
          computeLandedCostForRole(d.investorPurchasePrice, d.purchaseCostLines, "investor") ??
          computeLandedCostForRole(d.purchasePrice, d.purchaseCostLines, "supplier"),
      });
      if (dealError) {
        const purchaseIdx = wizardSteps.findIndex((s) => s.id === "purchase");
        if (purchaseIdx >= 0) setWizardStep(purchaseIdx);
        toast.error(dealError);
        return;
      }
    }
    let id = persistedIdRef.current;
    // Edit: keep existing status. Add (full finish): always Draft.
    const payload = savePayload(isEdit ? d : { ...d, inventoryStatus: "draft" });
    setSaveLoading(true);
    try {
      if (isEdit) {
        if (!routeId) throw new Error("Missing product id");
        await updateProduct(routeId, payload);
        toast.success("Product updated.");
        navigate(`/products/${routeId}`);
        return;
      }
      if (!id) {
        const created = await persistProductWizardProgress(payload, null);
        id = created.id;
        setPersistedProductId(id);
        persistedIdRef.current = id;
        persistSnapshot(wizardStep, d, id);
      }
      await finalizeWizardProduct(payload, id);
      clearAddProductWizard();
      toast.success("Product saved as draft.");
      navigate("/products");
    } catch (e) {
      toast.error(catalogProductErrorMessage(e));
    } finally {
      setSaveLoading(false);
    }
  };

  const isLastStep = wizardStep >= stepCount - 1;

  const ingestDocFiles = async (fileList: File[]) => {
    const good = fileList.filter((f) => isAllowedProductDocFile(f));
    if (good.length === 0) return;

    const current = draftRef.current;
    if (!current.documentCategory?.trim()) {
      toast.error("Select a document type before adding files.");
      return;
    }
    const copy = current.documentType === "duplicate" ? "duplicate" : "original";
    const pagesRaw = Number(current.numberOfDocuments ?? 0);
    const pages = Number.isInteger(pagesRaw) && pagesRaw >= 0 ? Math.min(100, pagesRaw) : 0;

    const max = PRODUCT_DOCS_ATTR.maxFiles ?? 20;
    const base = current.productDocuments ?? [];
    const uploads = [...base];
    for (const f of good) {
      if (uploads.length >= max) break;
      const uploaded = await mockUploadFile(f, PRODUCT_DOCS_ATTR);
      uploads.push({
        ...uploaded,
        documentTypeId: current.documentCategoryId ?? null,
        documentTypeName: current.documentCategory?.trim() || null,
        documentCopy: copy,
        numberOfPages: pages,
      });
    }
    setDraft((d) => ({
      ...d,
      productDocuments: uploads,
      documentCategory: null,
      documentCategoryId: null,
      documentType: "original",
      numberOfDocuments: undefined,
    }));
  };

  const resolveCostLineAutoPayer = useCallback(() => {
    const d = draftRef.current;
    const dealType =
      d.acquisitionType === "consignment" ? "owner_vehicle" : (d.dealType ?? "purchased");
    const companyFallback =
      companyParties[0]?.id != null
        ? String(companyParties[0].id)
        : (parties.find((p) => Boolean(p.isCompany))?.id != null
            ? String(parties.find((p) => Boolean(p.isCompany))!.id)
            : null);
    return resolveAutoCostLinePayer({
      dealType,
      acquisitionType: d.acquisitionType ?? "purchased",
      dealPartners: d.dealPartners,
      fallbackCompanyPartyId: companyFallback,
    });
  }, [companyParties, parties]);

  const autoCostLinePayer = useMemo(() => {
    const dealType =
      draft.acquisitionType === "consignment" ? "owner_vehicle" : (draft.dealType ?? "purchased");
    const companyFallback =
      companyParties[0]?.id != null
        ? String(companyParties[0].id)
        : (parties.find((p) => Boolean(p.isCompany))?.id != null
            ? String(parties.find((p) => Boolean(p.isCompany))!.id)
            : null);
    return resolveAutoCostLinePayer({
      dealType,
      acquisitionType: draft.acquisitionType ?? "purchased",
      dealPartners: draft.dealPartners,
      fallbackCompanyPartyId: companyFallback,
    });
  }, [
    draft.acquisitionType,
    draft.dealType,
    draft.dealPartners,
    companyParties,
    parties,
  ]);

  const buildInitialCostLineModalForm = (
    typeIdOverride?: string,
    role: ProductInvoiceRole = "supplier",
  ): PurchaseCostLine => {
    const typeId = typeIdOverride ?? purchaseCostTypes[0]?.id ?? "";
    const invoices = draftRef.current.invoices ?? [];
    const roleInvoice =
      invoices.find((inv) => inv.role === role) ??
      (role === "supplier" ? invoices.find((inv) => !inv.role) : undefined) ??
      invoices[0];
    const defaultInvoiceId = roleInvoice ? (roleInvoice.id || roleInvoice.temp_id || null) : null;
    const defaultPaidToPartyId = roleInvoice?.partyId
      ? Number(roleInvoice.partyId)
      : role === "supplier" && draftRef.current.supplierId
        ? Number(draftRef.current.supplierId)
        : null;
    const defaultDate = roleInvoice?.invoiceDate
      ? roleInvoice.invoiceDate.slice(0, 10)
      : new Date().toISOString().split("T")[0];

    const currCode =
      role === "investor"
        ? roleInvoice?.currency ??
          draftRef.current.investorPurchaseCurrency ??
          draftRef.current.purchaseCurrency ??
          getActiveCompanyCurrency().code
        : roleInvoice?.currency ?? draftRef.current.purchaseCurrency ?? getActiveCompanyCurrency().code;
    const exRate =
      role === "investor"
        ? draftRef.current.investorPurchaseExchangeRate ?? 1
        : draftRef.current.purchaseExchangeRate ?? 1;

    const autoPayer = resolveCostLineAutoPayer();

    return {
      role,
      typeId,
      amount: 0,
      currencyCode: currCode,
      currencyAmount: 0,
      exchangeRate: exRate,
      paidByPartyId: autoPayer.paidByPartyId,
      paidToPartyId: defaultPaidToPartyId,
      costBearerPartyId: autoPayer.costBearerPartyId,
      paymentMethod: "cash",
      bankName: null,
      transactionReferenceNo: null,
      payOrderNo: null,
      description: null,
      invoiceId: defaultInvoiceId,
      date: defaultDate,
    };
  };

  const getInvoiceByRole = useCallback(
    (role: ProductInvoiceRole): ProductInvoice | null => {
      const list = draft.invoices ?? [];
      const byRole = list.find((inv) => inv.role === role);
      if (byRole) return byRole;
      // Legacy rows (no role): surface in the supplier slot so existing products still edit cleanly.
      if (role === "supplier") {
        return list.find((inv) => !inv.role) ?? null;
      }
      return null;
    },
    [draft.invoices],
  );

  const openAddInvoiceModal = (role: ProductInvoiceRole) => {
    setEditingInvoice(null);
    setEditingInvoicePair(false);
    const supplierParty = draft.supplierId ? String(draft.supplierId) : "";
    const investorParty = (draft.dealPartners ?? []).find((p) => p.partyId)?.partyId
      ? String((draft.dealPartners ?? []).find((p) => p.partyId)!.partyId)
      : "";
    const defaultPartyId = role === "supplier" ? supplierParty : investorParty;
    setInvoiceForm({
      role,
      invoiceNo: "",
      invoiceDate: new Date().toISOString().split("T")[0],
      dueDate: "",
      partyId: defaultPartyId,
      notes: "",
      filePath: null,
    });
    setInvoiceDualFiles({ supplier: null, investor: null });
    setInvoiceDualParties({
      supplier: supplierParty,
      investor: investorParty,
    });
    setInvoiceModalOpen(true);
  };

  const openEditInvoiceModal = (inv: ProductInvoice) => {
    const role: ProductInvoiceRole =
      inv.role === "investor" ? "investor" : inv.role === "supplier" ? "supplier" : "supplier";
    setEditingInvoice(inv);
    setEditingInvoicePair(false);
    setInvoiceForm({
      ...inv,
      role,
    });
    setInvoiceDualFiles({
      supplier: role === "supplier" ? (inv.filePath ?? null) : null,
      investor: role === "investor" ? (inv.filePath ?? null) : null,
    });
    setInvoiceDualParties({
      supplier: role === "supplier" ? String(inv.partyId ?? "") : "",
      investor: role === "investor" ? String(inv.partyId ?? "") : "",
    });
    setInvoiceModalOpen(true);
  };

  /** Dual-track: one edit for supplier + investor pair (same pattern as cost lines). */
  const openEditInvoicePairModal = () => {
    const supplier = getInvoiceByRole("supplier");
    const investor = getInvoiceByRole("investor");
    if (!supplier && !investor) return;

    if (!showInvestorPurchaseTrack) {
      if (supplier) openEditInvoiceModal(supplier);
      return;
    }

    const primary = supplier ?? investor!;
    setEditingInvoice(null);
    setEditingInvoicePair(true);
    setInvoiceForm({
      role: supplier ? "supplier" : "investor",
      invoiceNo: primary.invoiceNo ?? "",
      invoiceDate: primary.invoiceDate
        ? String(primary.invoiceDate).slice(0, 10)
        : new Date().toISOString().split("T")[0],
      dueDate: primary.dueDate ? String(primary.dueDate).slice(0, 10) : "",
      partyId: primary.partyId ? String(primary.partyId) : "",
      notes: primary.notes ?? "",
      filePath: primary.filePath ?? null,
    });
    setInvoiceDualFiles({
      supplier: supplier?.filePath ?? null,
      investor: investor?.filePath ?? null,
    });
    setInvoiceDualParties({
      supplier: supplier?.partyId != null ? String(supplier.partyId) : "",
      investor: investor?.partyId != null ? String(investor.partyId) : "",
    });
    setInvoiceModalOpen(true);
  };


  const upsertInvoiceByRole = (
    list: ProductInvoice[],
    payload: ProductInvoice,
    matchExisting: ProductInvoice | null,
  ): ProductInvoice[] => {
    const next = [...list];
    const matchIdx = next.findIndex((inv) => {
      if (matchExisting) {
        return (
          (matchExisting.id != null && inv.id === matchExisting.id) ||
          (matchExisting.temp_id != null && inv.temp_id === matchExisting.temp_id)
        );
      }
      return inv.role === payload.role || (!inv.role && payload.role === "supplier");
    });
    if (matchIdx !== -1) {
      next[matchIdx] = { ...next[matchIdx], ...payload, role: payload.role };
    } else {
      const tempId = `temp-${payload.role}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
      next.push({
        ...payload,
        temp_id: tempId,
        id: tempId,
      });
    }
    const deduped: ProductInvoice[] = [];
    const seenRoles = new Set<string>();
    for (const inv of next) {
      if (inv.role === "supplier" || inv.role === "investor") {
        if (seenRoles.has(inv.role)) continue;
        seenRoles.add(inv.role);
      }
      deduped.push(inv);
    }
    return deduped;
  };

  const handleSaveInvoice = () => {
    if (!invoiceForm.invoiceNo || !invoiceForm.invoiceDate) {
      toast.error("Invoice No and Date are required.");
      return;
    }

    // Edit: single invoice (existing behaviour).
    if (editingInvoice) {
      const role: ProductInvoiceRole =
        invoiceForm.role === "investor" ? "investor" : "supplier";
      const dealInvestorId = (draft.dealPartners ?? []).find((p) => p.partyId)?.partyId;
      const editPartyId =
        role === "investor"
          ? (dealInvestorId ? String(dealInvestorId) : String(invoiceForm.partyId ?? ""))
          : String(invoiceForm.partyId ?? "");
      if (!editPartyId) {
        toast.error(
          role === "investor"
            ? "Attach an investor on this product before saving the investor invoice."
            : "Invoice No, Date, and Supplier are required.",
        );
        return;
      }
      const party = parties.find((p) => String(p.id) === String(editPartyId));
      const partyName = party ? party.name || party.contact_person || "" : "";
      const filePath =
        role === "investor"
          ? (invoiceDualFiles.investor ?? invoiceForm.filePath ?? null)
          : (invoiceDualFiles.supplier ?? invoiceForm.filePath ?? null);
      const newInvoice: ProductInvoice = {
        ...invoiceForm,
        role,
        invoiceNo: invoiceForm.invoiceNo,
        invoiceDate: invoiceForm.invoiceDate,
        dueDate: invoiceForm.dueDate || null,
        partyId: editPartyId,
        partyName,
        filePath,
      } as ProductInvoice;

      setDraft((d) => ({
        ...d,
        invoices: upsertInvoiceByRole(d.invoices ?? [], newInvoice, editingInvoice),
      }));
      setInvoiceModalOpen(false);
      return;
    }

    // Add: allow supplier, investor, or both in one save when dual-track.
    const dualAdd = showInvestorPurchaseTrack;
    const supplierFile = invoiceDualFiles.supplier;
    const investorFile = invoiceDualFiles.investor;
    const dealInvestorId = (draft.dealPartners ?? []).find((p) => p.partyId)?.partyId;
    const supplierPartyId = dualAdd
      ? invoiceDualParties.supplier
      : String(invoiceForm.partyId ?? "");
    // Investor is always auto-taken from deal partners (no manual dropdown).
    const investorPartyId =
      (dealInvestorId ? String(dealInvestorId) : "") ||
      invoiceDualParties.investor ||
      (invoiceForm.role === "investor" ? String(invoiceForm.partyId ?? "") : "");

    let rolesToSave: ProductInvoiceRole[] = [];
    if (dualAdd) {
      if (supplierFile || (editingInvoicePair && supplierPartyId)) {
        rolesToSave.push("supplier");
      }
      if (investorFile || (editingInvoicePair && investorPartyId)) {
        rolesToSave.push("investor");
      }
      if (rolesToSave.length === 0) {
        // No documents: allow a single invoice when only one party is set.
        if (supplierPartyId && !investorPartyId) {
          rolesToSave = ["supplier"];
        } else if (investorPartyId && !supplierPartyId) {
          rolesToSave = ["investor"];
        } else if (supplierPartyId && investorPartyId) {
          toast.error(
            "Attach a supplier and/or investor document to save one or both invoices.",
          );
          return;
        } else {
          toast.error("Select a party and/or attach an invoice document.");
          return;
        }
      }
    } else {
      rolesToSave = [invoiceForm.role === "investor" ? "investor" : "supplier"];
    }

    for (const role of rolesToSave) {
      const partyId =
        role === "investor"
          ? (dualAdd ? investorPartyId : String(invoiceForm.partyId ?? ""))
          : (dualAdd ? supplierPartyId : String(invoiceForm.partyId ?? ""));
      if (!partyId) {
        toast.error(
          role === "investor"
            ? "Attach an investor on this product before saving the investor invoice."
            : "Select a supplier for the supplier invoice.",
        );
        return;
      }
    }

    setDraft((d) => {
      let list = d.invoices ? [...d.invoices] : [];
      for (const role of rolesToSave) {
        const partyId =
          role === "investor"
            ? (dualAdd ? investorPartyId : String(invoiceForm.partyId ?? ""))
            : (dualAdd ? supplierPartyId : String(invoiceForm.partyId ?? ""));
        const party = parties.find((p) => String(p.id) === String(partyId));
        const partyName = party ? party.name || party.contact_person || "" : "";
        const filePath =
          role === "investor"
            ? (invoiceDualFiles.investor ?? null)
            : dualAdd
              ? (invoiceDualFiles.supplier ?? null)
              : (invoiceForm.filePath ?? invoiceDualFiles.supplier ?? null);
        const payload: ProductInvoice = {
          role,
          invoiceNo: String(invoiceForm.invoiceNo),
          invoiceDate: String(invoiceForm.invoiceDate),
          dueDate: invoiceForm.dueDate || null,
          partyId,
          partyName,
          filePath,
          notes: invoiceForm.notes ?? null,
        } as ProductInvoice;
        list = upsertInvoiceByRole(list, payload, null);
      }
      return { ...d, invoices: list };
    });

    setInvoiceModalOpen(false);
  };

  const handleDeleteInvoice = (inv: ProductInvoice) => {
    const invId = inv.id || inv.temp_id;
    if (!invId) return;
    setDraft((d) => {
      const baseInvoiceId = d.baseInvoiceId === invId ? null : d.baseInvoiceId;
      const investorBaseInvoiceId =
        d.investorBaseInvoiceId === invId ? null : d.investorBaseInvoiceId;
      const costLines = (d.purchaseCostLines ?? []).map((l) => {
        if (l.invoiceId === invId) {
          return { ...l, invoiceId: null };
        }
        return l;
      });
      const list = (d.invoices ?? []).filter(
        (i) => i.id !== invId && i.temp_id !== invId
      );
      return {
        ...d,
        baseInvoiceId,
        investorBaseInvoiceId,
        purchaseCostLines: costLines,
        invoices: list,
      };
    });
  };

  const handleDeleteInvoicePair = () => {
    const supplier = getInvoiceByRole("supplier");
    const investor = getInvoiceByRole("investor");
    const ids = [supplier?.id || supplier?.temp_id, investor?.id || investor?.temp_id].filter(
      Boolean,
    ) as Array<string | number>;
    if (ids.length === 0) return;
    setDraft((d) => {
      const idSet = new Set(ids.map(String));
      let baseInvoiceId = d.baseInvoiceId;
      let investorBaseInvoiceId = d.investorBaseInvoiceId;
      if (baseInvoiceId != null && idSet.has(String(baseInvoiceId))) baseInvoiceId = null;
      if (investorBaseInvoiceId != null && idSet.has(String(investorBaseInvoiceId))) {
        investorBaseInvoiceId = null;
      }
      const costLines = (d.purchaseCostLines ?? []).map((l) =>
        l.invoiceId != null && idSet.has(String(l.invoiceId)) ? { ...l, invoiceId: null } : l,
      );
      const list = (d.invoices ?? []).filter(
        (i) => !idSet.has(String(i.id ?? "")) && !idSet.has(String(i.temp_id ?? "")),
      );
      return {
        ...d,
        baseInvoiceId,
        investorBaseInvoiceId,
        purchaseCostLines: costLines,
        invoices: list,
      };
    });
  };

  const resetCostLineInvestorTrack = (seed?: PurchaseCostLine) => {
    const amt = seed?.currencyAmount != null && Number.isFinite(Number(seed.currencyAmount))
      ? Number(seed.currencyAmount)
      : seed?.amount != null && Number.isFinite(Number(seed.amount))
        ? Number(seed.amount)
        : 0;
    const rate = seed?.exchangeRate != null && Number.isFinite(Number(seed.exchangeRate))
      ? Number(seed.exchangeRate)
      : 1;
    setCostLineInvestorTrack({
      currencyAmount: amt,
      exchangeRate: rate,
      invoiceId: seed?.invoiceId ?? null,
      paidToPartyId: seed?.paidToPartyId != null ? Number(seed.paidToPartyId) : null,
    });
    setCostLineInvestorCurrencyAmountInput(amt > 0 ? String(amt) : "");
    setCostLineInvestorExchangeRateInput(String(rate));
  };

  const openAddCostLineModal = (_role: ProductInvoiceRole = "supplier") => {
    if (purchaseCostTypes.length === 0) {
      toast.error("No cost types available. Please create a cost type first.");
      return;
    }
    const autoPayer = resolveCostLineAutoPayer();
    const dealTypeForPayer =
      draft.acquisitionType === "consignment" ? "owner_vehicle" : (draft.dealType ?? "purchased");
    if (!autoPayer.paidByPartyId) {
      if (dealTypeForPayer === "owner_vehicle") {
        toast.error("Attach an investor on this product before adding cost lines.");
        return;
      }
      if (dealTypeForPayer === "partnership") {
        toast.error("Attach partnership partners before adding cost lines.");
        return;
      }
      toast.error("No company/showroom party available to attach as payer.");
      return;
    }
    setEditingCostLineIndex(null);
    setEditingInvestorCostLineIndex(null);
    costLineInvestorQuoteCustomizedRef.current = false;
    const supplierSeed = buildInitialCostLineModalForm(undefined, "supplier");
    const investorSeed = buildInitialCostLineModalForm(supplierSeed.typeId, "investor");
    setCostLineModalForm({ ...supplierSeed, role: "supplier" });
    setCostLineExchangeRateInput(supplierSeed.exchangeRate != null ? String(supplierSeed.exchangeRate) : "1");
    setCostLineCurrencyAmountInput("");
    resetCostLineInvestorTrack({
      ...investorSeed,
      currencyAmount: 0,
      amount: 0,
      exchangeRate: supplierSeed.exchangeRate ?? 1,
    });
    setCostTypeSelectKey((k) => k + 1);
    setCostLineModalOpen(true);
  };

  const openEditCostLineModal = (index: number) => {
    const lines = draft.purchaseCostLines ?? [];
    const line = lines[index];
    if (!line) return;

    const lineRole = line.role === "investor" ? "investor" : "supplier";
    const siblingIndex = lines.findIndex(
      (l, i) =>
        i !== index &&
        String(l.typeId) === String(line.typeId) &&
        (l.role === "investor" ? "investor" : "supplier") !== lineRole,
    );
    const sibling = siblingIndex >= 0 ? lines[siblingIndex] : null;

    const supplierLine = lineRole === "supplier" ? line : sibling;
    const investorLine = lineRole === "investor" ? line : sibling;
    setEditingCostLineIndex(
      lineRole === "supplier" ? index : siblingIndex >= 0 ? siblingIndex : null,
    );
    setEditingInvestorCostLineIndex(
      lineRole === "investor" ? index : siblingIndex >= 0 ? siblingIndex : null,
    );

    const supplierSeed = supplierLine
      ?? buildInitialCostLineModalForm(line.typeId, "supplier");
    const invoice = supplierSeed.invoiceId
      ? (draft.invoices ?? []).find(
          (inv) => String(inv.id || inv.temp_id) === String(supplierSeed.invoiceId),
        )
      : null;
    const resolvedDate = invoice?.invoiceDate
      ? invoice.invoiceDate.slice(0, 10)
      : supplierSeed.date
        ? String(supplierSeed.date).slice(0, 10)
        : new Date().toISOString().split("T")[0];

    const autoPayer = resolveCostLineAutoPayer();

    setCostLineModalForm({
      ...supplierSeed,
      role: "supplier",
      date: resolvedDate,
      typeId: line.typeId,
      paidByPartyId: autoPayer.paidByPartyId,
      costBearerPartyId: autoPayer.costBearerPartyId,
    });
    setCostLineExchangeRateInput(
      supplierSeed.exchangeRate != null ? String(supplierSeed.exchangeRate) : "1",
    );
    setCostLineCurrencyAmountInput(
      supplierSeed.currencyAmount != null && Number(supplierSeed.currencyAmount) > 0
        ? String(supplierSeed.currencyAmount)
        : supplierSeed.amount > 0
          ? String(supplierSeed.amount)
          : "",
    );

    if (investorLine) {
      costLineInvestorQuoteCustomizedRef.current = true;
      resetCostLineInvestorTrack(investorLine);
    } else {
      costLineInvestorQuoteCustomizedRef.current = false;
      const investorSeed = buildInitialCostLineModalForm(line.typeId, "investor");
      resetCostLineInvestorTrack({
        ...investorSeed,
        currencyAmount: supplierSeed.currencyAmount ?? supplierSeed.amount ?? 0,
        exchangeRate: supplierSeed.exchangeRate ?? 1,
        amount: supplierSeed.amount ?? 0,
      });
      setCostLineInvestorCurrencyAmountInput(
        supplierSeed.currencyAmount != null && Number(supplierSeed.currencyAmount) > 0
          ? String(supplierSeed.currencyAmount)
          : supplierSeed.amount > 0
            ? String(supplierSeed.amount)
            : "",
      );
      setCostLineInvestorExchangeRateInput(
        supplierSeed.exchangeRate != null ? String(supplierSeed.exchangeRate) : "1",
      );
    }

    setCostTypeSelectKey((k) => k + 1);
    setCostLineModalOpen(true);
  };

  const handleSaveCostLineModal = (andAddMore = false) => {
    if (!costLineModalForm.typeId) {
      toast.error("Please select a cost type.");
      return;
    }
    if (!costLineModalForm.paidToPartyId) {
      toast.error("Please select a supplier for the supplier cost line.");
      return;
    }

    const autoPayer = resolveCostLineAutoPayer();
    const dealTypeForPayer =
      draft.acquisitionType === "consignment" ? "owner_vehicle" : (draft.dealType ?? "purchased");
    if (!autoPayer.paidByPartyId) {
      if (dealTypeForPayer === "owner_vehicle") {
        toast.error("Attach an investor on this product before adding cost lines.");
        return;
      }
      if (dealTypeForPayer === "partnership") {
        toast.error("Attach partnership partners before adding cost lines.");
        return;
      }
      toast.error("No company/showroom party available to attach as payer.");
      return;
    }

    const hasSupplierInvoice = (draft.invoices ?? []).some(
      (inv) => inv.role === "supplier" || !inv.role,
    );
    if (isDiscountCostLine(costLineModalForm)) {
      if (!hasSupplierInvoice) {
        toast.error("Purchase Discount can only be applied when a supplier invoice is attached.");
        return;
      }
      if (!costLineModalForm.invoiceId) {
        toast.error("Link Purchase Discount to a supplier invoice.");
        return;
      }
    }

    const linkedInvoice = costLineModalForm.invoiceId
      ? (draft.invoices ?? []).find(
          (inv) => String(inv.id || inv.temp_id) === String(costLineModalForm.invoiceId),
        )
      : null;

    let finalDate: string | null = null;
    if (costLineModalForm.invoiceId) {
      finalDate = linkedInvoice?.invoiceDate
        ? linkedInvoice.invoiceDate.slice(0, 10)
        : costLineModalForm.date
          ? costLineModalForm.date.slice(0, 10)
          : null;
    } else {
      finalDate = costLineModalForm.date ? costLineModalForm.date.trim().slice(0, 10) : null;
      if (!finalDate) {
        toast.error("Date is required when no supplier invoice is linked.");
        return;
      }
    }

    const activeBase = getActiveCompanyCurrency().code;
    const cCode = costLineModalForm.currencyCode ?? draft.purchaseCurrency ?? activeBase;
    const isBase = cCode === activeBase;
    const supplierExRate = isBase
      ? 1
      : costLineModalForm.exchangeRate == null || !Number.isFinite(Number(costLineModalForm.exchangeRate))
        ? 1
        : Number(costLineModalForm.exchangeRate);
    const supplierCurrAmt =
      costLineModalForm.currencyAmount == null || !Number.isFinite(Number(costLineModalForm.currencyAmount))
        ? 0
        : Number(costLineModalForm.currencyAmount);
    const supplierBaseAmt = isBase ? supplierCurrAmt : Number((supplierCurrAmt * supplierExRate).toFixed(2));

    if (supplierBaseAmt <= 0) {
      toast.error("Enter a supplier cost amount greater than zero.");
      return;
    }

    const allowInvestorCostTrack =
      (draft.acquisitionType ?? "purchased") === "consignment" ||
      (draft.dealType ?? "purchased") !== "purchased";
    const investorExRate = isBase
      ? 1
      : costLineInvestorTrack.exchangeRate == null || !Number.isFinite(Number(costLineInvestorTrack.exchangeRate))
        ? 1
        : Number(costLineInvestorTrack.exchangeRate);
    const investorCurrAmt = !allowInvestorCostTrack
      ? 0
      : costLineInvestorTrack.currencyAmount == null || !Number.isFinite(Number(costLineInvestorTrack.currencyAmount))
        ? 0
        : Number(costLineInvestorTrack.currencyAmount);
    const investorBaseAmt = isBase ? investorCurrAmt : Number((investorCurrAmt * investorExRate).toFixed(2));

    const investorPaidTo =
      costLineInvestorTrack.paidToPartyId ??
      (investorBaseAmt > 0
        ? (
            (draft.dealPartners ?? []).find((p) => p.partyId)?.partyId != null
              ? Number((draft.dealPartners ?? []).find((p) => p.partyId)!.partyId)
              : costLineModalForm.paidToPartyId
          )
        : null);
    if (allowInvestorCostTrack && investorBaseAmt > 0 && !investorPaidTo) {
      toast.error("Attach an investor on this product (or investor invoice) for the quoted cost line.");
      return;
    }

    if (
      allowInvestorCostTrack &&
      isDiscountCostLine(costLineModalForm) &&
      investorBaseAmt > 0 &&
      !costLineInvestorTrack.invoiceId
    ) {
      toast.error("Link investor Purchase Discount to an investor invoice.");
      return;
    }

    const shared = {
      typeId: costLineModalForm.typeId,
      typeLabel: costLineModalForm.typeLabel ?? null,
      currencyCode: cCode,
      paidByPartyId: autoPayer.paidByPartyId,
      costBearerPartyId: autoPayer.costBearerPartyId,
      paymentMethod: costLineModalForm.paymentMethod ?? null,
      bankName: costLineModalForm.bankName ?? null,
      transactionReferenceNo: costLineModalForm.transactionReferenceNo ?? null,
      payOrderNo: costLineModalForm.payOrderNo ?? null,
      description: costLineModalForm.description ?? null,
      date: finalDate,
    };

    const supplierLine: PurchaseCostLine = {
      ...shared,
      lineId: editingCostLineIndex != null
        ? (draft.purchaseCostLines ?? [])[editingCostLineIndex]?.lineId ?? null
        : null,
      role: "supplier",
      currencyAmount: supplierCurrAmt,
      exchangeRate: supplierExRate,
      amount: supplierBaseAmt,
      paidToPartyId: costLineModalForm.paidToPartyId,
      invoiceId: costLineModalForm.invoiceId ?? null,
    };

    // Dual track: supplier (actual) + optional investor (quoted). Includes consignment.
    const investorLine: PurchaseCostLine | null =
      allowInvestorCostTrack && investorBaseAmt > 0
        ? {
            ...shared,
            lineId: editingInvestorCostLineIndex != null
              ? (draft.purchaseCostLines ?? [])[editingInvestorCostLineIndex]?.lineId ?? null
              : null,
            role: "investor",
            currencyAmount: investorCurrAmt,
            exchangeRate: investorExRate,
            amount: investorBaseAmt,
            paidToPartyId: investorPaidTo,
            invoiceId: costLineInvestorTrack.invoiceId ?? null,
          }
        : null;

    setDraft((d) => {
      const prev = d.purchaseCostLines ?? [];
      const next: PurchaseCostLine[] = [];
      let wroteSupplier = false;
      let wroteInvestor = false;
      prev.forEach((row, i) => {
        if (editingCostLineIndex != null && i === editingCostLineIndex) {
          next.push(supplierLine);
          wroteSupplier = true;
          return;
        }
        if (editingInvestorCostLineIndex != null && i === editingInvestorCostLineIndex) {
          if (investorLine) {
            next.push(investorLine);
            wroteInvestor = true;
          }
          return;
        }
        next.push(row);
      });
      if (!wroteSupplier) next.push(supplierLine);
      if (investorLine && !wroteInvestor) next.push(investorLine);
      return { ...d, purchaseCostLines: next };
    });

    const isEdit =
      editingCostLineIndex != null || editingInvestorCostLineIndex != null;

    if (andAddMore && !isEdit) {
      toast.success("Cost line added.");
      const supplierSeed = buildInitialCostLineModalForm(costLineModalForm.typeId, "supplier");
      const investorSeed = buildInitialCostLineModalForm(costLineModalForm.typeId, "investor");
      costLineInvestorQuoteCustomizedRef.current = false;
      setCostLineModalForm({
        ...supplierSeed,
        role: "supplier",
        currencyCode: cCode,
        exchangeRate: supplierExRate,
        currencyAmount: 0,
        amount: 0,
        paidToPartyId: null,
        paidByPartyId: autoPayer.paidByPartyId,
        costBearerPartyId: autoPayer.costBearerPartyId,
        description: null,
        bankName: null,
        transactionReferenceNo: null,
        payOrderNo: null,
        invoiceId: null,
      });
      setCostLineCurrencyAmountInput("");
      setCostLineExchangeRateInput(String(supplierExRate));
      resetCostLineInvestorTrack({
        ...investorSeed,
        currencyAmount: 0,
        amount: 0,
        exchangeRate: supplierExRate,
      });
      setCostTypeSelectKey((k) => k + 1);
    } else {
      toast.success(isEdit ? "Cost line updated." : "Cost line added.");
      setCostLineModalOpen(false);
    }
  };

  const addCostLine = (role: ProductInvoiceRole = "supplier") => {
    openAddCostLineModal(role);
  };

  const openCostTypeDialog = (lineIndex: number | null) => {
    setCostTypeDialogLineIndex(lineIndex);
    setCostTypeLabel("");
    const cats = listPurchaseCostCategories();
    setCostTypeCategoryId(cats[0]?.id ?? "");
    setCostTypeDialogOpen(true);
    void syncPurchaseCostCategoriesFromApi().then(() => {
      setPurchaseCostCategoriesVersion((v) => v + 1);
    });
  };

  const handleCreateCostType = async () => {
    const label = costTypeLabel.trim();
    if (!costTypeCategoryId) {
      toast.error("Purchase cost category is required.");
      return;
    }
    if (!label) {
      toast.error("Cost type name is required.");
      return;
    }
    setCostTypeSaving(true);
    try {
      const { record, message } = await createPurchaseCostType({
        label,
        purchaseCostCategoryId: costTypeCategoryId,
      });
      await syncPurchaseCostTypesFromApi().catch(() => undefined);
      setPurchaseCostTypesVersion((v) => v + 1);

      const lineIndex = costTypeDialogLineIndex;
      if (costLineModalOpen) {
        setCostLineModalForm((f) => ({ ...f, typeId: record.id }));
      } else {
        setDraft((d) => {
          const lines = [...(d.purchaseCostLines ?? [])];
          if (lineIndex != null && lineIndex >= 0 && lineIndex < lines.length) {
            lines[lineIndex] = { ...lines[lineIndex], typeId: record.id };
            return { ...d, purchaseCostLines: lines };
          }
          const dealType =
            d.acquisitionType === "consignment" ? "owner_vehicle" : (d.dealType ?? "purchased");
          const companyFallback =
            companyParties[0]?.id != null
              ? String(companyParties[0].id)
              : (parties.find((p) => Boolean(p.isCompany))?.id != null
                  ? String(parties.find((p) => Boolean(p.isCompany))!.id)
                  : null);
          const autoPayer = resolveAutoCostLinePayer({
            dealType,
            acquisitionType: d.acquisitionType ?? "purchased",
            dealPartners: d.dealPartners,
            fallbackCompanyPartyId: companyFallback,
          });
          const currCode = d.purchaseCurrency ?? getActiveCompanyCurrency().code;
          const exRate = d.purchaseExchangeRate ?? 1;
          return {
            ...d,
            purchaseCostLines: [
              ...lines,
              {
                typeId: record.id,
                amount: 0,
                currencyCode: currCode,
                currencyAmount: 0,
                exchangeRate: exRate,
                paidByPartyId: autoPayer.paidByPartyId,
                costBearerPartyId: autoPayer.costBearerPartyId,
                paymentMethod: "cash" as const,
                bankName: null,
                transactionReferenceNo: null,
                payOrderNo: null,
                description: null,
              },
            ],
          };
        });
      }

      toast.success(message || "Cost type created.");
      setCostTypeDialogOpen(false);
      setCostTypeDialogLineIndex(null);
    } catch (e) {
      toast.error(catalogProductErrorMessage(e));
    } finally {
      setCostTypeSaving(false);
    }
  };

  const handleCreateDocumentType = async () => {
    const name = documentTypeName.trim();
    if (!name) {
      toast.error("Document type name is required.");
      return;
    }
    const exists = listDocumentTypeRecords().some(
      (t) => t.name.toLowerCase() === name.toLowerCase(),
    );
    if (exists) {
      toast.error("A document type with this name already exists.");
      return;
    }
    setDocumentTypeSaving(true);
    try {
      const { record, message } = await createDocumentType({ name });
      await syncDocumentTypesFromApi().catch(() => undefined);
      setDocumentTypesVersion((v) => v + 1);
      setDraft((d) => ({
        ...d,
        documentCategory: record.name,
        documentCategoryId: record.id,
      }));
      toast.success(message || "Document type created.");
      setDocumentTypeDialogOpen(false);
      setDocumentTypeName("");
    } catch (e) {
      toast.error(catalogProductErrorMessage(e));
    } finally {
      setDocumentTypeSaving(false);
    }
  };

  const updateCostLine = (
    index: number,
    patch: Partial<{
      typeId: string;
      amount: number;
      currencyCode: string | null;
      currencyAmount: number | null;
      exchangeRate: number | null;
      paidByPartyId: string | null;
      paymentMethod: "cash" | "online" | "pay_order" | null;
      bankName: string | null;
      transactionReferenceNo: string | null;
      payOrderNo: string | null;
      description: string | null;
    }>,
  ) => {
    setDraft((d) => {
      const list = [...(d.purchaseCostLines ?? [])];
      const prevRow = list[index];
      if (!prevRow) return d;
      const row = { ...prevRow, ...patch };
      if (
        typeof patch.currencyAmount !== "undefined" ||
        typeof patch.exchangeRate !== "undefined" ||
        typeof patch.currencyCode !== "undefined"
      ) {
        const activeBase = getActiveCompanyCurrency().code;
        const cCode =
          typeof patch.currencyCode !== "undefined"
            ? patch.currencyCode
            : row.currencyCode ?? d.purchaseCurrency ?? activeBase;
        const isBase = cCode === activeBase;
        const exRate = isBase
          ? 1
          : typeof patch.exchangeRate !== "undefined"
          ? patch.exchangeRate ?? 1
          : row.exchangeRate ?? d.purchaseExchangeRate ?? 1;
        const cAmt =
          typeof patch.currencyAmount !== "undefined"
            ? patch.currencyAmount
            : row.currencyAmount ?? row.amount;
        row.currencyCode = cCode;
        row.exchangeRate = exRate;
        row.currencyAmount = cAmt;
        row.amount = cAmt != null && exRate != null ? cAmt * exRate : 0;
      } else if (typeof patch.amount !== "undefined") {
        const n = Number(patch.amount);
        row.amount = Number.isFinite(n) && n >= 0 ? n : 0;
      }
      if (typeof patch.description !== "undefined") {
        const raw = patch.description;
        row.description = raw == null || raw === "" ? null : raw;
      }
      list[index] = row;
      return { ...d, purchaseCostLines: list };
    });
  };

  const updateCostLinePaymentMethod = (index: number, method: PaymentMethodValue) => {
    setDraft((d) => {
      const list = [...(d.purchaseCostLines ?? [])];
      const prevRow = list[index];
      if (!prevRow) return d;
      const next = applyPaymentMethodChange(
        emptyPaymentMethodDraft({
          paymentMethod: (prevRow.paymentMethod ?? "") as PaymentMethodValue,
          bankId: prevRow.bankName ?? "",
          transactionReferenceNo: prevRow.transactionReferenceNo ?? "",
          payOrderNo: prevRow.payOrderNo ?? "",
        }),
        method,
      );
      list[index] = {
        ...prevRow,
        paymentMethod: next.paymentMethod || null,
        bankName: next.bankId.trim() || null,
        transactionReferenceNo: next.transactionReferenceNo.trim() || null,
        payOrderNo: next.payOrderNo.trim() || null,
      };
      return { ...d, purchaseCostLines: list };
    });
  };

  const removeCostLine = (index: number) => {
    setDraft((d) => {
      const lines = d.purchaseCostLines ?? [];
      const line = lines[index];
      if (!line) return d;

      // Dual-track: remove supplier + investor siblings that share the same cost type.
      let siblingIndex = -1;
      if (showCostLineInvestorTrack) {
        const lineRole = line.role === "investor" ? "investor" : "supplier";
        siblingIndex = lines.findIndex(
          (l, i) =>
            i !== index &&
            String(l.typeId) === String(line.typeId) &&
            (l.role === "investor" ? "investor" : "supplier") !== lineRole,
        );
      }

      return {
        ...d,
        purchaseCostLines: lines.filter((_, i) => i !== index && i !== siblingIndex),
      };
    });
  };

  const canAddCostLine =
    purchaseCostTypes.length > 0 &&
    purchaseCostTypes.some((t) => !(draft.purchaseCostLines ?? []).some((l) => l.typeId === t.id));

  if (isEdit && !editReady) {
    return (
      <Layout title="Edit product">
        <div className="glass-card p-8 flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading product…
        </div>
      </Layout>
    );
  }

  return (
    <Layout title={title}>
      <div className="w-full max-w-none space-y-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-3 min-w-0">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="text-muted-foreground hover:text-foreground shrink-0 mt-0.5"
              onClick={handleCancel}
            >
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div className="min-w-0">
              <h1 className="text-2xl font-display font-bold text-foreground">{title}</h1>
              <p className="text-sm text-muted-foreground mt-1">
                Step {wizardStep + 1} of {stepCount}.{" "}
                <span className="text-foreground/80">Save &amp; Next</span> writes to the catalog store (same session is kept in your browser).
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2 sm:shrink-0">
            <Button type="button" variant="glass" onClick={handleCancel}>
              Cancel
            </Button>
          </div>
        </div>

        <div className="w-full space-y-2">
          <div className="overflow-x-auto rounded-xl border border-border/50 bg-card/40 px-3 py-5 sm:px-6">
            <div className="flex min-w-[720px] items-center sm:min-w-0">
              {wizardSteps.map((s, i) => {
                const done = i < wizardStep;
                const active = i === wizardStep;
                const lineComplete = i < wizardStep;
                return (
                  <Fragment key={s.id}>
                    <button
                      type="button"
                      onClick={() => setWizardStep(i)}
                      className={cn(
                        "flex shrink-0 items-center gap-2 sm:gap-3 rounded-lg px-1.5 py-1 -mx-0.5 text-left outline-none transition-colors",
                        "hover:bg-muted/60 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                      )}
                      aria-label={`Go to ${s.label}`}
                      aria-current={active ? "step" : undefined}
                    >
                      <div
                        className={cn(
                          "flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-sm font-semibold transition-colors pointer-events-none",
                          done && "bg-[#1a56db] text-white shadow-sm",
                          active && !done && "bg-[#f05252] text-white shadow-sm",
                          !done && !active && "bg-[#f3f4f6] text-[#4b5563]",
                        )}
                      >
                        {done ? (
                          <Check className="h-5 w-5 stroke-[3]" aria-hidden />
                        ) : active ? (
                          <X className="h-5 w-5 stroke-[3]" aria-hidden />
                        ) : (
                          <span>{i + 1}</span>
                        )}
                      </div>
                      <span
                        className={cn(
                          "max-w-[5.5rem] truncate text-xs font-medium sm:max-w-[9rem] sm:text-sm pointer-events-none",
                          active ? "text-foreground" : done ? "text-foreground/90" : "text-muted-foreground",
                        )}
                        title={s.label}
                      >
                        {s.label}
                      </span>
                    </button>
                    {i < wizardSteps.length - 1 ? (
                      <div
                        className={cn(
                          "mx-2 h-0.5 min-w-[1.25rem] flex-1 rounded-full sm:mx-3",
                          lineComplete ? "bg-[#1a56db]" : "bg-[#e5e7eb]",
                        )}
                        aria-hidden
                      />
                    ) : null}
                  </Fragment>
                );
              })}
            </div>
          </div>

          {currentStepId === "basic" ? (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="glass-card p-6">
              <SectionHeader title="Basic information" subtitle="Category, listing fields, and core vehicle details" />
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">Category *</Label>
                  <SearchableSelect
                    value={draft.categoryId}
                    onChange={(v) => {
                      setDraft((d) => ({ ...d, categoryId: v, attributes: {} }));
                    }}
                    options={categories.map((c) => ({
                      value: c.id,
                      label: c.name,
                    }))}
                    placeholder="Select category"
                    searchPlaceholder="Search category..."
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">Name *</Label>
                  <Input
                    className="glass-input h-10 cursor-pointer"
                    readOnly
                    value={draft.name}
                    onClick={() => setVehiclePickerOpen(true)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setVehiclePickerOpen(true);
                      }
                    }}
                    placeholder="Click to select model year, make, model…"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">Location</Label>
                  <SearchableSelect
                    value={draft.warehouseId && draft.warehouseId !== "" ? draft.warehouseId : ""}
                    onChange={(v) => setDraft((d) => ({ ...d, warehouseId: v ? v : null }))}
                    options={[
                      { value: "", label: "No location" },
                      ...warehouses.map((w) => ({
                        value: w.id,
                        label: w.city ? `${w.name} — ${w.city}` : w.name,
                      })),
                    ]}
                    placeholder="Optional"
                    searchPlaceholder="Search location..."
                  />
                </div>

                {basicSpecGroups.some((group) =>
                  filterVisibleAttributes(
                    filterBasicSpecGridAttributes(group.attributes),
                    allSchemaAttributes,
                    draft.attributes,
                  ).length > 0,
                ) ? (
                  <div className="space-y-4 md:col-span-2 xl:col-span-3">
                    {basicSpecGroups.map((group) => {
                      const visibleAttrs = filterVisibleAttributes(
                        filterBasicSpecGridAttributes(group.attributes),
                        allSchemaAttributes,
                        draft.attributes,
                      );
                      if (visibleAttrs.length === 0) return null;
                      return (
                        <div
                          key={group.id}
                          className={
                            isBasicDetailsAttributeGroup(group)
                              ? undefined
                              : "rounded-lg border border-border/40 bg-muted/10 p-4"
                          }
                        >
                          {!isBasicDetailsAttributeGroup(group) && group.name ? (
                            <div className="mb-3 flex items-center gap-2">
                              <p className="text-sm font-semibold text-foreground">{group.name}</p>
                              {canQuickAddCategoryAttribute ? (
                                <button
                                  type="button"
                                  className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-primary hover:bg-primary/10 disabled:opacity-50 disabled:pointer-events-none"
                                  aria-label={`Add attribute to ${group.name}`}
                                  title="Add attribute and link to this category"
                                  disabled={isEditLocked || !draft.categoryId}
                                  onClick={() => openQuickAddAttribute({ id: group.id, name: group.name })}
                                >
                                  <Plus className="h-3.5 w-3.5" strokeWidth={2.5} />
                                </button>
                              ) : null}
                            </div>
                          ) : null}
                          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                            {visibleAttrs.map((attr) => (
                              <AttributeField
                                key={attr.id}
                                attribute={attr}
                                value={draft.attributes[attr.code]}
                                onChange={(v) => updateProductAttribute(attr.code, v, attr)}
                                onAttributeOptionsUpdated={handleAttributeOptionsUpdated}
                              />
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : null}

                <div className="space-y-2 md:col-span-2 xl:col-span-3">
                  <Label className="text-xs text-muted-foreground">Description</Label>
                  <Textarea
                    className="glass-input min-h-[120px]"
                    value={draft.description ?? ""}
                    onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))}
                    placeholder="Public description shown on listings…"
                  />
                </div>
              </div>
            </motion.div>
          ) : currentStepId === "spec" ? (
            <>
              {!draft.categoryId ? (
                <div className="glass-card p-8 text-sm text-muted-foreground text-center">
                  Select a <strong className="text-foreground">category</strong> under Basic information to load additional information fields.
                </div>
              ) : loadingSchema ? (
                <div className="glass-card p-8 text-sm text-muted-foreground text-center">Loading category fields…</div>
              ) : schema && specOnlyGroups.length === 0 ? (
                <div className="glass-card p-8 text-sm text-muted-foreground text-center space-y-4">
                  <div className="space-y-2">
                    <p>
                      This category has <strong className="text-foreground">no linked attributes for additional information</strong> yet.
                    </p>
                    <p>
                      Assign existing attributes to this category (same as Categories edit), create a new one, or manage links under{" "}
                      <strong className="text-foreground">Categories</strong>.
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center justify-center gap-2">
                    {canAssignCategoryAttributes ? (
                      <Button
                        type="button"
                        variant="premium"
                        disabled={isEditLocked || !draft.categoryId}
                        onClick={() => setAssignCategoryAttributesOpen(true)}
                      >
                        <ListTree className="h-4 w-4 mr-2" />
                        Assign attributes
                      </Button>
                    ) : null}
                    {canQuickAddCategoryAttribute ? (
                      <Button
                        type="button"
                        variant="glass"
                        disabled={isEditLocked || !draft.categoryId}
                        onClick={() => openQuickAddAttribute(null)}
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        New attribute
                      </Button>
                    ) : null}
                  </div>
                </div>
              ) : schema ? (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="glass-card overflow-hidden p-6 md:p-8"
                >
                  <div className="mb-6 flex flex-col gap-3 border-b border-border/50 pb-5 sm:flex-row sm:items-start sm:justify-between">
                    <div className="min-w-0 space-y-1">
                      <h2 className="font-display text-lg font-semibold tracking-tight text-foreground">Additional Information</h2>
                      <p className="max-w-2xl text-xs leading-relaxed text-muted-foreground">
                        All fields for this step are shown below. Values are saved with the product when you use{" "}
                        <span className="font-medium text-foreground/90">Save &amp; Next</span> or finish the wizard.
                      </p>
                    </div>
                    {canAssignCategoryAttributes || canQuickAddCategoryAttribute ? (
                      <div className="flex shrink-0 items-center justify-end gap-1 self-end sm:self-start">
                        {canAssignCategoryAttributes ? (
                          <button
                            type="button"
                            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-primary hover:bg-primary/10 disabled:opacity-50 disabled:pointer-events-none"
                            aria-label="Assign category attributes"
                            title="Assign existing attributes to this category"
                            disabled={isEditLocked || !draft.categoryId}
                            onClick={() => setAssignCategoryAttributesOpen(true)}
                          >
                            <ListTree className="h-4 w-4" strokeWidth={2.5} />
                          </button>
                        ) : null}
                        {canQuickAddCategoryAttribute ? (
                          <button
                            type="button"
                            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-primary hover:bg-primary/10 disabled:opacity-50 disabled:pointer-events-none"
                            aria-label="Add category attribute"
                            title="Create attribute and link to this category"
                            disabled={isEditLocked || !draft.categoryId}
                            onClick={() => openQuickAddAttribute(null)}
                          >
                            <Plus className="h-4 w-4" strokeWidth={2.5} />
                          </button>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                  <div className="space-y-8">
                    {additionalMainAttrs.length > 0 ? (
                      <div className="rounded-lg border border-border/40 bg-muted/10 p-4">
                        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                          {additionalMainAttrs.map((attr) => (
                            <AttributeField
                              key={attr.id}
                              attribute={attr}
                              value={draft.attributes[attr.code]}
                              onChange={(v) => updateProductAttribute(attr.code, v, attr)}
                              onAttributeOptionsUpdated={handleAttributeOptionsUpdated}
                            />
                          ))}
                        </div>
                      </div>
                    ) : null}
                    {additionalFeatureBooleans.length > 0 || additionalFeatureMultiselects.length > 0 ? (
                      <div className="space-y-3">
                        <div className="flex flex-wrap items-center justify-between gap-3">
                          <h3 className="text-base font-semibold text-foreground">
                            Features
                            {additionalFeatureBooleans.some((a) => a.isRequired) ||
                            additionalFeatureMultiselects.some((a) => a.isRequired) ? (
                              <span className="text-destructive"> *</span>
                            ) : null}
                          </h3>
                          <div className="flex items-center gap-2 rounded-md border border-border/60 bg-background/80 px-2.5 py-1.5 shadow-sm">
                            <Checkbox
                              id="features-select-all"
                              checked={areAllFeaturesSelected}
                              onCheckedChange={(checked) => handleToggleAllFeatures(Boolean(checked))}
                              className="border-border data-[state=checked]:bg-primary data-[state=checked]:border-primary"
                            />
                            <Label
                              htmlFor="features-select-all"
                              className="cursor-pointer text-xs font-medium text-foreground select-none"
                            >
                              Select All
                            </Label>
                          </div>
                        </div>
                        <div className="rounded-lg border border-border/40 bg-card/30 p-3 sm:p-4">
                          {additionalFeatureBooleans.length > 0 ? (
                            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4 md:gap-2 md:gap-y-3">
                              {additionalFeatureBooleans.map((attr) => (
                                <AttributeField
                                  key={attr.id}
                                  layout="additional"
                                  featureGridCell
                                  attribute={attr}
                                  value={draft.attributes[attr.code]}
                                  onChange={(v) => updateProductAttribute(attr.code, v, attr)}
                                  onAttributeOptionsUpdated={handleAttributeOptionsUpdated}
                                />
                              ))}
                            </div>
                          ) : null}
                          {additionalFeatureMultiselects.length > 0 ? (
                            <div
                              className={cn(
                                "space-y-4",
                                additionalFeatureBooleans.length > 0 ? "mt-6 border-t border-border/45 pt-6" : "",
                              )}
                            >
                              {additionalFeatureMultiselects.map((attr) => (
                                <div key={attr.id}>
                                  <div className="mb-2 flex items-center justify-between">
                                    <p className="text-sm font-medium text-foreground">
                                      {attr.name}
                                      {attr.isRequired ? <span className="text-destructive"> *</span> : null}
                                    </p>
                                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                      <button
                                        type="button"
                                        className="font-medium hover:text-primary transition-colors cursor-pointer"
                                        onClick={() => handleToggleMultiselectGroup(attr, true)}
                                      >
                                        Select all
                                      </button>
                                      <span>·</span>
                                      <button
                                        type="button"
                                        className="font-medium hover:text-primary transition-colors cursor-pointer"
                                        onClick={() => handleToggleMultiselectGroup(attr, false)}
                                      >
                                        Clear
                                      </button>
                                    </div>
                                  </div>
                                  <AttributeField
                                    layout="additional"
                                    suppressMultiselectLabel
                                    attribute={attr}
                                    value={draft.attributes[attr.code]}
                                    onChange={(v) => updateProductAttribute(attr.code, v, attr)}
                                    onAttributeOptionsUpdated={handleAttributeOptionsUpdated}
                                  />
                                </div>
                              ))}
                            </div>
                          ) : null}
                        </div>
                      </div>
                    ) : null}
                  </div>
                </motion.div>
              ) : (
                <div className="glass-card p-8 text-sm text-muted-foreground text-center">
                  Could not load additional information for this category.
                </div>
              )}
            </>
          ) : currentStepId === "media" ? (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="glass-card p-6 md:p-8">
              <SectionHeader
                title="Images & documents"
                subtitle="Gallery photos and supporting files — use the tabs below."
                icon={Camera}
              />

              <Tabs defaultValue="images" className="w-full">
                <TabsList className="grid h-auto w-full max-w-lg grid-cols-2 gap-1 p-1">
                  <TabsTrigger value="images" className="gap-2 px-3">
                    <Camera className="h-4 w-4 shrink-0" aria-hidden />
                    <span>Images</span>
                    {productImages.length > 0 ? (
                      <span className="ml-0.5 rounded-full bg-background/80 px-1.5 py-0.5 text-[10px] font-semibold tabular-nums text-muted-foreground shadow-sm">
                        {productImages.length}
                      </span>
                    ) : null}
                  </TabsTrigger>
                  <TabsTrigger value="documents" className="gap-2 px-3">
                    <FileText className="h-4 w-4 shrink-0" aria-hidden />
                    <span>Documents</span>
                    {productDocuments.length > 0 ? (
                      <span className="ml-0.5 rounded-full bg-background/80 px-1.5 py-0.5 text-[10px] font-semibold tabular-nums text-muted-foreground shadow-sm">
                        {productDocuments.length}
                      </span>
                    ) : null}
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="images" className="mt-4 focus-visible:outline-none">
                  <ProductImageGallery
                    images={productImages}
                    productId={isEdit ? routeId ?? null : persistedProductId}
                    disabled={saveLoading}
                    dropzoneClassName="py-12"
                    onChange={(nextImages) => setDraft((d) => ({ ...d, productImages: nextImages }))}
                  />
                </TabsContent>

                <TabsContent value="documents" className="mt-4 space-y-3 focus-visible:outline-none">
                  <input
                    ref={docInputRef}
                    type="file"
                    multiple
                    accept={PRODUCT_DOCS_ATTR.accept}
                    className="hidden"
                    onChange={(e) => {
                      const files = Array.from(e.target.files ?? []);
                      e.currentTarget.value = "";
                      void ingestDocFiles(files);
                    }}
                  />
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                    <div className="space-y-1.5">
                      <Label className="flex items-center justify-between w-full text-xs text-muted-foreground">
                        <span>Type</span>
                        {canCreateDocumentType ? (
                          <button
                            type="button"
                            className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-md text-primary hover:bg-primary/10"
                            aria-label="Add document type"
                            title="Add document type"
                            disabled={saveLoading}
                            onClick={() => {
                              setDocumentTypeName("");
                              setDocumentTypeDialogOpen(true);
                            }}
                          >
                            <Plus className="h-3.5 w-3.5" strokeWidth={2.5} />
                          </button>
                        ) : null}
                      </Label>
                      <DocumentTypeSelect
                        value={draft.documentCategory}
                        options={documentTypes}
                        onChange={(v) => {
                          const match = documentTypes.find((t) => t.name === v);
                          const id = match?.id ? String(match.id) : null;
                          setDraft((d) => ({
                            ...d,
                            documentCategory: v,
                            documentCategoryId: id && !id.startsWith("seed-") ? id : null,
                          }));
                        }}
                        disabled={saveLoading}
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs text-muted-foreground">Document Copy</Label>
                      <Select
                        value={draft.documentType === "duplicate" ? "duplicate" : "original"}
                        onValueChange={(v) =>
                          setDraft((d) => ({
                            ...d,
                            documentType: v === "duplicate" ? "duplicate" : "original",
                          }))
                        }
                        disabled={saveLoading}
                      >
                        <SelectTrigger className="glass-input h-10">
                          <SelectValue placeholder="Document copy" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="original">Original</SelectItem>
                          <SelectItem value="duplicate">Duplicate</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs text-muted-foreground" htmlFor="number-of-pages">
                        No. of Pages
                      </Label>
                      <Input
                        id="number-of-pages"
                        type="number"
                        inputMode="numeric"
                        min={0}
                        max={100}
                        step={1}
                        className="glass-input h-10"
                        value={draft.numberOfDocuments ?? ""}
                        disabled={saveLoading}
                        onChange={(e) => {
                          const raw = e.target.value;
                          if (raw === "") {
                            setDraft((d) => ({ ...d, numberOfDocuments: undefined }));
                            return;
                          }
                          if (!/^\d+$/.test(raw)) return;
                          const n = Number(raw);
                          if (!Number.isInteger(n)) return;
                          if (n < 0 || n > 100) return;
                          setDraft((d) => ({ ...d, numberOfDocuments: n }));
                        }}
                        onBlur={() => {
                          const n = Number(draftRef.current.numberOfDocuments);
                          if (!Number.isInteger(n) || n < 0) {
                            setDraft((d) => ({ ...d, numberOfDocuments: 0 }));
                          } else if (n > 100) {
                            setDraft((d) => ({ ...d, numberOfDocuments: 100 }));
                          }
                        }}
                      />
                    </div>
                  </div>
                  <button
                    type="button"
                    className={cn(
                      "w-full rounded-lg border-2 border-dashed px-6 py-12 text-center transition-colors outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                      docDropActive
                        ? "border-primary bg-primary/10"
                        : "border-border bg-muted/20 hover:bg-muted/35 hover:border-primary/45",
                    )}
                    onDragEnter={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setDocDropActive(true);
                    }}
                    onDragOver={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setDocDropActive(true);
                    }}
                    onDragLeave={(e) => {
                      const next = e.relatedTarget as Node | null;
                      if (!next || !e.currentTarget.contains(next)) setDocDropActive(false);
                    }}
                    onDrop={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setDocDropActive(false);
                      void ingestDocFiles(Array.from(e.dataTransfer.files));
                    }}
                    onClick={() => docInputRef.current?.click()}
                  >
                    <Upload className="w-10 h-10 mx-auto text-muted-foreground/80 mb-3" aria-hidden />
                    <p className="text-sm font-medium text-foreground">
                      Drag &amp; drop documents here or click to browse (multiple)
                    </p>
                    <p className="text-xs text-muted-foreground mt-2">
                      PDF, Word, Excel, images — up to 10MB each · max {PRODUCT_DOCS_ATTR.maxFiles} files
                    </p>
                  </button>
                  {productDocuments.length > 0 ? (
                    <div className="space-y-3 pt-2 border-t border-border/40 overflow-x-auto">
                      <table className="w-full min-w-[640px] text-sm">
                        <thead>
                          <tr className="border-b border-border/50 text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                            <th className="py-2 pr-2 font-medium w-12">Sr. #</th>
                            <th className="py-2 pr-2 font-medium">File</th>
                            <th className="py-2 pr-2 font-medium">Type</th>
                            <th className="py-2 pr-2 font-medium">Document Copy</th>
                            <th className="py-2 pr-2 font-medium whitespace-nowrap">No. of Pages</th>
                            <th className="py-2 pr-2 font-medium whitespace-nowrap">Size</th>
                            <th className="py-2 font-medium text-right w-20"> </th>
                          </tr>
                        </thead>
                        <tbody>
                          {productDocuments.map((u, index) => (
                            <tr key={u.id} className="border-b border-border/30 last:border-0">
                              <td className="py-2 pr-2 tabular-nums text-muted-foreground">{index + 1}</td>
                              <td className="py-2 pr-2 text-foreground/90 max-w-[180px] truncate" title={u.filename}>
                                {u.filename}
                              </td>
                              <td className="py-2 pr-2 text-foreground/90 max-w-[140px] truncate">
                                {u.documentTypeName?.trim() || "—"}
                              </td>
                              <td className="py-2 pr-2 capitalize text-foreground/90">
                                {u.documentCopy === "duplicate"
                                  ? "Duplicate"
                                  : u.documentCopy === "original"
                                    ? "Original"
                                    : "—"}
                              </td>
                              <td className="py-2 pr-2 tabular-nums text-foreground/90">
                                {u.numberOfPages == null ? "—" : u.numberOfPages}
                              </td>
                              <td className="py-2 pr-2 text-xs text-muted-foreground whitespace-nowrap">
                                {Math.round(u.size / 1024)} KB
                              </td>
                              <td className="py-2 text-right">
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  className="h-7 text-xs text-destructive"
                                  onClick={() =>
                                    setDraft((d) => ({
                                      ...d,
                                      productDocuments: (d.productDocuments ?? []).filter((x) => x.id !== u.id),
                                    }))
                                  }
                                >
                                  Remove
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : null}
                </TabsContent>
              </Tabs>
            </motion.div>
          ) : currentStepId === "purchase_details" ? (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="glass-card p-6 space-y-2">
              <SectionHeader
                title="Purchase Details"
                subtitle="How this vehicle was acquired — date, type, supplier, auction, and deal."
                icon={ClipboardList}
              />

              <div className="rounded-xl border border-border/50 bg-card/40 p-4 space-y-2.5">
                {/* Acquisition Date, Acquisition Type, Purchase Type, Import Country & Supplier Row */}
                <div className={cn(
                  "grid grid-cols-1 md:grid-cols-2 gap-4",
                  (draft.acquisitionType ?? "purchased") === "purchased" && draft.purchaseType === "import"
                    ? "xl:grid-cols-5"
                    : (draft.acquisitionType ?? "purchased") === "purchased"
                      ? "xl:grid-cols-4"
                      : "xl:grid-cols-3"
                )}>
                  {/* Acquisition Date */}
                  <div className="space-y-1.5 min-w-0">
                    <Label className="text-xs font-semibold text-foreground">Acquisition Date</Label>
                    <DatePartsPicker
                      value={draft.acquisitionDate ?? ""}
                      onChange={(val) => setDraft((d) => ({ ...d, acquisitionDate: val || null }))}
                      placeholder="Select date"
                    />
                  </div>

                  {/* Acquisition Type */}
                  <div className="space-y-1.5 min-w-0">
                    <Label className="text-xs font-semibold text-foreground">Acquisition Type</Label>
                    <div className="flex items-center gap-2 p-1 bg-muted/30 rounded-lg border border-border/40 h-11">
                      <button
                        type="button"
                        className={cn(
                          "flex-1 h-9 px-3 rounded-md text-xs font-semibold transition-all",
                          (draft.acquisitionType ?? "purchased") === "purchased"
                            ? "bg-background text-foreground shadow-xs border border-border/60"
                            : "text-muted-foreground hover:text-foreground",
                        )}
                        onClick={() => {
                          setDraft((d) => ({
                            ...d,
                            acquisitionType: "purchased",
                          }));
                        }}
                      >
                        Purchased
                      </button>
                      <button
                        type="button"
                        className={cn(
                          "flex-1 h-9 px-3 rounded-md text-xs font-semibold transition-all",
                          draft.acquisitionType === "consignment"
                            ? "bg-background text-foreground shadow-xs border border-border/60"
                            : "text-muted-foreground hover:text-foreground",
                        )}
                        onClick={() => {
                          const activeCode = getActiveCompanyCurrency().code;
                          const amt =
                            draft.purchaseCurrencyAmount ?? draft.purchasePrice ?? null;
                          setPurchaseExchangeRateInput("1");
                          setPurchaseCurrencyAmountInput(amt != null ? String(amt) : "");
                          setDraft((d) => {
                            const partners = d.dealPartners ?? [];
                            return {
                              ...d,
                              acquisitionType: "consignment",
                              // Consignment is always investor-vehicle for deal/payer resolution.
                              dealType: "owner_vehicle",
                              dealPartners:
                                partners.length > 0 ? partners : [emptyDealPartnerRow()],
                              purchaseCurrency: activeCode,
                              purchaseExchangeRate: 1,
                              purchaseCurrencyAmount: amt,
                              purchasePrice: amt,
                            };
                          });
                        }}
                      >
                        Consignment
                      </button>
                    </div>
                  </div>

                  {/* Purchased Amount — consignment only (required valuation floor, not a payable) */}
                  {draft.acquisitionType === "consignment" && (
                    <div className="space-y-1.5 min-w-0">
                      <div className="flex items-center justify-between gap-x-2 min-h-[1rem]">
                        <div className="min-w-0">
                          <Label className="text-xs font-semibold text-foreground">
                            Purchased Amount ({getActiveCompanyCurrency().code}){" "}
                            <span className="text-destructive">*</span>
                          </Label>
                        </div>
                        {(() => {
                          const amt = Number(purchaseCurrencyAmountInput || draft.purchaseCurrencyAmount || 0);
                          const short = Number.isFinite(amt) && amt > 0 ? pkrAmountShortLabel(amt) : "";
                          return short ? (
                            <span className="text-[11px] font-medium text-primary shrink-0 leading-none tabular-nums">
                              {short}
                            </span>
                          ) : null;
                        })()}
                      </div>
                      <Input
                        type="number"
                        className="glass-input h-11"
                        min={0}
                        step="any"
                        value={purchaseCurrencyAmountInput}
                        onChange={(e) => {
                          const raw = e.target.value;
                          setPurchaseCurrencyAmountInput(raw);
                          const amt = raw === "" ? null : Number(raw);
                          const activeCode = getActiveCompanyCurrency().code;
                          setDraft((d) => {
                            const basePrice = amt != null && Number.isFinite(amt) ? amt : null;
                            const isOwnerSurplus = d.profitRuleType === "owner_surplus";
                            return {
                              ...d,
                              purchaseCurrency: activeCode,
                              purchaseExchangeRate: 1,
                              purchaseCurrencyAmount: amt,
                              purchasePrice: basePrice,
                              ...(isOwnerSurplus && basePrice != null ? { ownerDemandPrice: basePrice } : {}),
                            };
                          });
                        }}
                        placeholder="0.00"
                      />
                    </div>
                  )}

                  {/* Purchase Type */}
                  {(draft.acquisitionType ?? "purchased") === "purchased" && (
                    <div className="space-y-1.5 min-w-0">
                      <Label className="text-xs font-semibold text-foreground">Purchase Type *</Label>
                      <div className="flex items-center gap-2 p-1 bg-muted/30 rounded-lg border border-border/40 h-11">
                        <button
                          type="button"
                          className={cn(
                            "flex-1 h-9 px-3 rounded-md text-xs font-semibold transition-all",
                            (draft.purchaseType ?? "local") === "local"
                              ? "bg-background text-foreground shadow-xs border border-border/60"
                              : "text-muted-foreground hover:text-foreground",
                          )}
                          onClick={() =>
                            setDraft((d) => ({
                              ...d,
                              purchaseType: "local",
                            }))
                          }
                        >
                          Local Purchase
                        </button>
                        <button
                          type="button"
                          className={cn(
                            "flex-1 h-9 px-3 rounded-md text-xs font-semibold transition-all",
                            draft.purchaseType === "import"
                              ? "bg-primary text-primary-foreground shadow-xs"
                              : "text-muted-foreground hover:text-foreground",
                          )}
                          onClick={() => setDraft((d) => ({ ...d, purchaseType: "import" }))}
                        >
                          Import Purchase
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Import Country (in front of Purchase Type when Import Purchase) */}
                  {(draft.acquisitionType ?? "purchased") === "purchased" && draft.purchaseType === "import" && (
                    <div className="space-y-1.5 min-w-0">
                      <Label className="text-xs font-semibold text-foreground">Import Country *</Label>
                      <ImportCountrySelect
                        value={draft.importData?.importCountryId ?? ""}
                        countries={countries}
                        placeholder="Select import country"
                        triggerClassName="h-11 text-sm bg-background"
                        onChange={(countryId) => {
                          const selectedCountry = countries.find((c) => c.id === countryId);
                          setDraft((d) => {
                            const countryCurrCode = selectedCountry?.currencyCode;
                            const activeCode = getActiveCompanyCurrency().code;
                            const nextCurrency = countryCurrCode || d.purchaseCurrency || activeCode;
                            const isBase = nextCurrency === activeCode;
                            const rate = isBase ? 1 : d.purchaseExchangeRate ?? 1;
                            const amt = d.purchaseCurrencyAmount ?? d.purchasePrice ?? null;
                            const basePrice = amt != null && rate != null ? amt * rate : null;

                            return {
                              ...d,
                              importData: { ...(d.importData || {}), importCountryId: countryId },
                              purchaseCurrency: nextCurrency,
                              purchaseExchangeRate: rate,
                              purchaseCurrencyAmount: amt,
                              purchasePrice: basePrice,
                            };
                          });
                        }}
                      />
                    </div>
                  )}

                  {/* Supplier Selector (in front of Purchase Type / Country) */}
                  {(draft.acquisitionType ?? "purchased") === "purchased" && (
                    <div className="space-y-1.5 min-w-0 relative">
                      <Label className="text-xs font-semibold text-foreground">Supplier</Label>
                      {canCreateSupplier ? (
                        <button
                          type="button"
                          className="absolute right-0 top-0 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-md text-primary hover:bg-primary/10"
                          aria-label="Add supplier"
                          title="Add supplier"
                          onClick={() => setAddSupplierOpen(true)}
                        >
                          <Plus className="h-3.5 w-3.5" strokeWidth={2.5} />
                        </button>
                      ) : null}
                      <PartySelect
                        value={draft.supplierId ? String(draft.supplierId) : ""}
                        options={parties.filter((p) => {
                          const type = (p.type ?? "").toLowerCase();
                          return type === "supplier" || type === "all" || type === "both" || Boolean(p.isCompany);
                        })}
                        onChange={(val) => setDraft((d) => ({ ...d, supplierId: val || null }))}
                        placeholder="Select supplier"
                        triggerClassName="h-11 text-sm"
                      />
                    </div>
                  )}
                </div>

                {/* Auction Data (Available for both Local and Import Purchase) */}
                {(draft.acquisitionType ?? "purchased") === "purchased" ? (
                  <div className="border border-border/50 bg-muted/20 p-4 rounded-xl space-y-4">
                    <div className="flex items-center gap-2 border-b border-border/50 pb-2 mb-2">
                      <Gavel className="h-4 w-4 text-primary" />
                      <h3 className="text-sm font-semibold text-foreground">Auction Data</h3>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-x-5 gap-y-4">
                      <div className="space-y-1.5 min-w-0">
                        <Label className="text-xs font-semibold text-foreground">
                          Auction Date <span className="text-muted-foreground font-normal">(optional)</span>
                        </Label>
                        <DatePartsPicker
                          value={draft.importData?.auctionDate ?? ""}
                          onChange={(val) =>
                            setDraft((d) => ({
                              ...d,
                              importData: { ...(d.importData || {}), auctionDate: val },
                            }))
                          }
                        />
                      </div>
                      <div className="space-y-1.5 min-w-0">
                        <Label className="text-xs font-semibold text-foreground">Auction House</Label>
                        <Input
                          placeholder="e.g. USS Tokyo"
                          className="h-11 bg-background"
                          value={draft.importData?.auctionHouse ?? ""}
                          onChange={(e) =>
                            setDraft((d) => ({
                              ...d,
                              importData: { ...(d.importData || {}), auctionHouse: e.target.value },
                            }))
                          }
                        />
                      </div>
                      <div className="space-y-1.5 min-w-0">
                        <Label className="text-xs font-semibold text-foreground">LOT #</Label>
                        <Input
                          placeholder="e.g. 8324"
                          className="h-11 bg-background"
                          value={draft.importData?.lotNumber ?? ""}
                          onChange={(e) =>
                            setDraft((d) => ({
                              ...d,
                              importData: { ...(d.importData || {}), lotNumber: e.target.value },
                            }))
                          }
                        />
                      </div>
                      <div className="space-y-1.5 min-w-0">
                        <Label className="text-xs font-semibold text-foreground">BOL (Bill of Lading No)</Label>
                        <Input
                          placeholder="e.g. BL-93284"
                          className="h-11 bg-background"
                          value={draft.importData?.billOfLadingNumber ?? ""}
                          onChange={(e) =>
                            setDraft((d) => ({
                              ...d,
                              importData: { ...(d.importData || {}), billOfLadingNumber: e.target.value },
                            }))
                          }
                        />
                      </div>
                    </div>
                  </div>
                ) : null}

                {/* Deal Type & Partner/Owner Selection */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5 min-w-0">
                    <Label className="text-xs font-semibold text-foreground">Deal Type *</Label>
                    {draft.acquisitionType === "consignment" ? (
                      <Input
                        className="glass-input h-11 text-sm bg-muted/30 font-medium cursor-default"
                        readOnly
                        value="Investor Vehicle"
                      />
                    ) : (
                      <Select
                        value={draft.dealType ?? undefined}
                        onValueChange={(v) => {
                          const nextType = v as ProductDealType;
                          setDraft((d) => ({
                            ...d,
                            dealType: nextType,
                            ...clearedDealDependents(nextType),
                          }));
                        }}
                      >
                        <SelectTrigger className="glass-input h-11 text-sm">
                          <SelectValue placeholder="Select deal type" />
                        </SelectTrigger>
                        <SelectContent>
                          {DEAL_TYPE_OPTIONS.map((o) => (
                            <SelectItem key={o.value} value={o.value}>
                              {o.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </div>

                  {effectiveDealType === "owner_vehicle" ? (
                    <div className="space-y-1.5 min-w-0">
                      <Label className="flex items-center justify-between gap-1 text-xs font-semibold text-foreground">
                        <span>Vehicle Owner / Seller *</span>
                        {canCreateInvestor ? (
                          <button
                            type="button"
                            className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-md text-primary hover:bg-primary/10"
                            aria-label="Add owner"
                            title="Add owner"
                            onClick={() => setAddOwnerOpen(true)}
                          >
                            <Plus className="h-3.5 w-3.5" strokeWidth={2.5} />
                          </button>
                        ) : null}
                      </Label>
                      <PartySelect
                        value={draft.dealPartners?.[0]?.partyId ? String(draft.dealPartners[0].partyId) : ""}
                        options={parties.filter(
                          (p) => p.type === "investor" || p.type === "all",
                        )}
                        onChange={(val) => {
                          if (!val) {
                            setDraft((d) => {
                              const currentPartners = d.dealPartners ?? [];
                              const first = currentPartners[0] ?? emptyDealPartnerRow();
                              return {
                                ...d,
                                dealPartners: [{ ...first, partyId: null, partyName: null }, ...currentPartners.slice(1)],
                              };
                            });
                            return;
                          }
                          const selectedParty = parties.find((p) => String(p.id) === String(val));
                          setDraft((d) => {
                            const currentPartners = d.dealPartners ?? [];
                            const first = currentPartners[0] ?? emptyDealPartnerRow();
                            const updatedFirst = {
                              ...first,
                              partyId: val,
                              partyName: selectedParty?.name || first.partyName,
                              investmentAmount: landedPreview,
                              investmentRatio: 100,
                            };
                            return {
                              ...d,
                              dealType:
                                d.acquisitionType === "consignment"
                                  ? "owner_vehicle"
                                  : (d.dealType ?? "owner_vehicle"),
                              dealPartners: [updatedFirst, ...currentPartners.slice(1)],
                            };
                          });
                        }}
                        placeholder="Select vehicle owner"
                        triggerClassName="h-11 text-sm"
                      />
                    </div>
                  ) : effectiveDealType === "partnership" ? (
                    <div className="space-y-1.5 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <Label className="text-xs font-semibold text-foreground">Select Partners *</Label>
                        {canCreateInvestor ? (
                          <button
                            type="button"
                            className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-primary hover:bg-primary/10"
                            aria-label="Add investor"
                            title="Add investor"
                            onClick={() => setAddInvestorOpen(true)}
                          >
                            <Plus className="h-3.5 w-3.5" strokeWidth={2.5} />
                          </button>
                        ) : null}
                      </div>
                      {(() => {
                        const partnerParties = parties.filter((p) => {
                          const type = (p.type ?? "").toLowerCase();
                          return type === "investor" || type === "all" || type === "both" || Boolean(p.isCompany);
                        });
                        const selectedPartners = (draft.dealPartners ?? []).filter((dp) => dp.partyId);
                        const selectedPartnerIds = selectedPartners.map((dp) => String(dp.partyId));

                        const togglePartner = (partyId: string) => {
                          const targetParty = parties.find((p) => String(p.id) === String(partyId));
                          const currentRows = draft.dealPartners ?? [];
                          const exists = currentRows.some((r) => String(r.partyId) === String(partyId));

                          if (exists) {
                            setDraft((d) => {
                              let nextRows = (d.dealPartners ?? []).filter((r) => String(r.partyId) !== String(partyId));
                              if (nextRows.length > 0) {
                                const equalShare = Number((100 / nextRows.length).toFixed(2));
                                nextRows = nextRows.map((r) => ({
                                  ...r,
                                  profitType: "percentage" as const,
                                  profitValue: equalShare,
                                }));
                              }
                              return { ...d, dealPartners: nextRows };
                            });
                            return;
                          }

                          setDraft((d) => {
                            const rows = d.dealPartners ?? [];
                            const newRow: DealPartnerDraft = {
                              key: `dp-part-${partyId}-${Date.now()}`,
                              partyId: String(partyId),
                              partyName: targetParty?.name || `Partner #${partyId}`,
                              investmentAmount: null,
                              investmentRatio: null,
                              profitType: "percentage",
                              profitValue: null,
                              isCompanyOwner: false,
                            };
                            let nextRows = [...rows.filter((r) => r.partyId), newRow];
                            if (nextRows.length > 0) {
                              const equalShare = Number((100 / nextRows.length).toFixed(2));
                              nextRows = nextRows.map((r) => ({
                                ...r,
                                profitType: "percentage" as const,
                                profitValue: equalShare,
                              }));
                            }
                            return { ...d, dealPartners: nextRows };
                          });
                        };

                        return (
                          <div>
                            <Popover>
                              <PopoverTrigger asChild>
                                <Button
                                  type="button"
                                  variant="outline"
                                  className="glass-input h-11 w-full justify-between text-sm font-normal"
                                >
                                  <span className="truncate">
                                    {selectedPartnerIds.length === 0
                                      ? "Select partners…"
                                      : `${selectedPartnerIds.length} partner${selectedPartnerIds.length > 1 ? "s" : ""} selected`}
                                  </span>
                                  <Search className="ml-2 h-4 w-4 shrink-0 opacity-70" />
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent
                                className="z-[200] w-[var(--radix-popover-trigger-width)] p-0"
                                align="start"
                                side="top"
                                sideOffset={4}
                                collisionPadding={16}
                                avoidCollisions
                              >
                                <div className="border-b border-border/50 p-2">
                                  <div className="relative">
                                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                    <Input
                                      autoFocus
                                      className="h-9 pl-8 text-xs"
                                      placeholder="Search partners by name…"
                                      value={partnerSearch}
                                      onChange={(e) => setPartnerSearch(e.target.value)}
                                    />
                                  </div>
                                </div>
                                <div className="max-h-[min(220px,40vh)] overflow-y-auto p-1">
                                  {(() => {
                                    const q = partnerSearch.trim().toLowerCase();
                                    const filteredParties = q
                                      ? partnerParties.filter((p) => (p.name ?? "").toLowerCase().includes(q))
                                      : partnerParties;

                                    if (filteredParties.length === 0) {
                                      return <p className="px-2 py-6 text-center text-xs text-muted-foreground">No partners found.</p>;
                                    }

                                    return filteredParties.map((p) => {
                                      const isSelected = selectedPartnerIds.includes(String(p.id));
                                      return (
                                        <button
                                          key={p.id}
                                          type="button"
                                          onClick={() => togglePartner(String(p.id))}
                                          className={cn(
                                            "flex w-full items-center gap-2.5 rounded-md px-2.5 py-1.5 text-left text-xs transition-colors hover:bg-muted/60",
                                            isSelected && "bg-muted/30 font-semibold text-foreground",
                                          )}
                                        >
                                          <span
                                            className={cn(
                                              "flex h-4 w-4 shrink-0 items-center justify-center rounded-[3px] border transition-colors",
                                              isSelected
                                                ? "border-primary bg-primary text-primary-foreground shadow-2xs"
                                                : "border-input bg-background hover:border-accent-foreground/30",
                                            )}
                                          >
                                            {isSelected ? <Check className="h-3 w-3 text-primary-foreground stroke-[2.5]" /> : null}
                                          </span>
                                          <span className="flex-1 truncate text-xs">{p.name}</span>
                                        </button>
                                      );
                                    });
                                  })()}
                                </div>
                              </PopoverContent>
                            </Popover>
                          </div>
                        );
                      })()}
                    </div>
                  ) : effectiveDealType === "purchased" ? (
                    <div className="space-y-1.5 min-w-0">
                      <Label className="text-xs font-semibold text-foreground">Purchased By (Showroom)</Label>
                      <Input
                        className="glass-input h-11 text-sm bg-muted/30 font-medium cursor-default"
                        readOnly
                        value={companyParties[0]?.name || "Company Showroom"}
                      />
                    </div>
                  ) : null}
                </div>
              </div>
            </motion.div>
          ) : currentStepId === "purchase" ? (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="glass-card p-6 space-y-2">
              <SectionHeader
                title="Purchase & landed cost"
                subtitle="Enter invoices, pricing, and additional costs. Landed cost = base purchase + additional cost lines."
                icon={Wallet}
              />

              <div className="rounded-xl border border-border/50 bg-card/40 p-4 space-y-2.5">
                {/* Dual invoice slots: Supplier | Investor */}
                {(draft.acquisitionType ?? "purchased") === "purchased" && (
                  <div className="rounded-xl border border-border/50 bg-muted/20 p-4 sm:p-5 space-y-4">
                    <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border/40 pb-3">
                      <div className="flex items-center gap-2.5 min-w-0">
                        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-primary/20 bg-primary/10 text-primary">
                          <Receipt className="h-4 w-4" />
                        </div>
                        <div className="min-w-0">
                          <h3 className="text-sm font-bold text-foreground">Purchase invoices</h3>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {showInvestorPurchaseTrack
                              ? "One supplier invoice and one investor invoice — link base price or cost lines as needed"
                              : "Supplier invoice — link base price or cost lines as needed"}
                          </p>
                        </div>
                      </div>
                      {(!getInvoiceByRole("supplier") ||
                        (showInvestorPurchaseTrack && !getInvoiceByRole("investor"))) ? (
                        <Button
                          type="button"
                          variant="default"
                          size="sm"
                          className="gap-1.5 h-8 font-medium shadow-xs shrink-0"
                          onClick={() =>
                            openAddInvoiceModal(
                              !getInvoiceByRole("supplier") ? "supplier" : "investor",
                            )
                          }
                        >
                          <Plus className="h-3.5 w-3.5" />
                          Add invoice
                        </Button>
                      ) : null}
                    </div>

                    {(() => {
                      const supplierInv = getInvoiceByRole("supplier");
                      const investorInv = showInvestorPurchaseTrack
                        ? getInvoiceByRole("investor")
                        : null;
                      const hasAny = Boolean(supplierInv || investorInv);

                      const renderInvSummary = (
                        inv: NonNullable<ReturnType<typeof getInvoiceByRole>> | null,
                        role: "supplier" | "investor",
                      ) => {
                        const partyLabel = role === "supplier" ? "Supplier" : "Investor";
                        if (!inv) {
                          return (
                            <div className="min-w-[8rem] text-right leading-tight">
                              <p className="text-[9px] text-muted-foreground">—</p>
                            </div>
                          );
                        }
                        const isLinkedToBase =
                          role === "supplier"
                            ? String(draft.baseInvoiceId) === String(inv.id || inv.temp_id)
                            : String(draft.investorBaseInvoiceId) === String(inv.id || inv.temp_id);
                        const linkedCostsCount = (draft.purchaseCostLines ?? []).filter(
                          (l) => String(l.invoiceId) === String(inv.id || inv.temp_id),
                        ).length;
                        return (
                          <div className="min-w-[8rem] text-right leading-tight space-y-0.5">
                            <p className="text-[11px] font-bold text-foreground truncate">
                              {inv.invoiceNo}
                            </p>
                            <p className="text-[9px] text-muted-foreground truncate">
                              {inv.partyName || `${partyLabel} #${inv.partyId}`}
                            </p>
                            <p className="text-[9px] text-muted-foreground">
                              Date: {inv.invoiceDate}
                              {inv.dueDate ? ` · Due: ${inv.dueDate}` : ""}
                            </p>
                            <div className="flex flex-wrap justify-end gap-1">
                              {isLinkedToBase ? (
                                <span className="inline-flex px-1 py-px rounded-full text-[8px] font-bold bg-primary/15 text-primary border border-primary/20">
                                  Base linked
                                </span>
                              ) : null}
                              {linkedCostsCount > 0 ? (
                                <span className="inline-flex px-1 py-px rounded-full text-[8px] font-bold bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border border-emerald-500/20">
                                  {linkedCostsCount} cost {linkedCostsCount === 1 ? "line" : "lines"}
                                </span>
                              ) : null}
                              {inv.filePath && typeof inv.filePath === "string" ? (
                                <button
                                  type="button"
                                  className="inline-flex items-center gap-0.5 px-1 py-px rounded-full text-[8px] font-semibold text-primary border border-primary/20 hover:underline"
                                  title="Open print view"
                                  onClick={() =>
                                    openDocumentPrintView(inv.filePath as string, {
                                      title: `Invoice ${inv.invoiceNo || ""}`.trim(),
                                    })
                                  }
                                >
                                  <FileText className="w-2.5 h-2.5" />
                                  View
                                </button>
                              ) : null}
                            </div>
                          </div>
                        );
                      };

                      return (
                        <div className="rounded-md border border-border/40 bg-card overflow-hidden">
                          {showInvestorPurchaseTrack ? (
                            <div className="flex items-center justify-between gap-1.5 px-1.5 py-1.5 border-b border-border/50 bg-muted/30">
                              <div className="min-w-0 flex-1">
                                <span className="text-[9px] font-bold uppercase tracking-wider text-muted-foreground">
                                  Invoice
                                </span>
                              </div>
                              <div className="flex items-center justify-end gap-3 shrink-0 pr-[3.25rem]">
                                <div className="min-w-[8rem] text-right">
                                  <span className="inline-flex rounded px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider bg-sky-500/15 text-sky-700 dark:text-sky-300 border border-sky-500/25">
                                    Supplier
                                  </span>
                                </div>
                                <div className="min-w-[8rem] text-right">
                                  <span className="inline-flex rounded px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider bg-violet-500/15 text-violet-700 dark:text-violet-300 border border-violet-500/25">
                                    Investor
                                  </span>
                                </div>
                              </div>
                            </div>
                          ) : null}

                          {!hasAny ? (
                            <div className="px-1.5 py-3 text-center">
                              <p className="text-xs font-semibold text-foreground/80">No invoices yet</p>
                              <p className="text-[10px] text-muted-foreground mt-0.5">
                                Use &quot;Add invoice&quot; above
                                {showInvestorPurchaseTrack ? " — supplier, investor, or both" : ""}
                              </p>
                            </div>
                          ) : (
                            <div className="flex items-center justify-between gap-1.5 p-1.5 text-[11px] hover:bg-muted/10 transition-colors">
                              <div className="min-w-0 flex-1">
                                <span className="font-bold text-foreground text-xs">
                                  {showInvestorPurchaseTrack ? "Purchase invoices" : "Supplier invoice"}
                                </span>
                              </div>
                              <div className="flex items-center justify-end gap-3 shrink-0">
                                {showInvestorPurchaseTrack ? (
                                  <>
                                    <div className="rounded-md bg-sky-500/5 px-1.5 py-0.5">
                                      {renderInvSummary(supplierInv, "supplier")}
                                    </div>
                                    <div className="rounded-md bg-violet-500/5 px-1.5 py-0.5">
                                      {renderInvSummary(investorInv, "investor")}
                                    </div>
                                  </>
                                ) : (
                                  renderInvSummary(supplierInv, "supplier")
                                )}
                                <div className="flex items-center gap-0">
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    className="h-6 w-6 text-muted-foreground hover:text-foreground hover:bg-muted"
                                    title="Edit invoice"
                                    aria-label="Edit invoice"
                                    onClick={() =>
                                      showInvestorPurchaseTrack
                                        ? openEditInvoicePairModal()
                                        : supplierInv
                                          ? openEditInvoiceModal(supplierInv)
                                          : undefined
                                    }
                                  >
                                    <Edit className="h-2.5 w-2.5" />
                                  </Button>
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    className="h-6 w-6 text-destructive/80 hover:text-destructive hover:bg-destructive/10"
                                    title="Delete invoice"
                                    aria-label="Delete invoice"
                                    onClick={() =>
                                      showInvestorPurchaseTrack
                                        ? handleDeleteInvoicePair()
                                        : supplierInv
                                          ? handleDeleteInvoice(supplierInv)
                                          : undefined
                                    }
                                  >
                                    <Trash2 className="h-2.5 w-2.5" />
                                  </Button>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })()}

                    {/* Dual base-price → invoice links */}
                    <div
                      className={cn(
                        "grid grid-cols-1 gap-3 pt-3 border-t border-border/40",
                        showInvestorPurchaseTrack && "md:grid-cols-2",
                      )}
                    >
                      <div className="space-y-1.5">
                        <Label className="text-xs font-bold text-foreground">Link supplier base price</Label>
                        <Select
                          value={draft.baseInvoiceId ? String(draft.baseInvoiceId) : "none"}
                          onValueChange={(val) => {
                            const invId = val === "none" ? null : val;
                            const invoice = (draft.invoices ?? []).find(
                              (inv) => String(inv.id || inv.temp_id) === String(invId),
                            );
                            setDraft((d) => ({
                              ...d,
                              baseInvoiceId: invId,
                              supplierId:
                                invoice && (invoice.role === "supplier" || !invoice.role)
                                  ? String(invoice.partyId)
                                  : d.supplierId,
                            }));
                          }}
                        >
                          <SelectTrigger className="h-10 text-xs sm:text-sm bg-background border border-border/60 shadow-2xs rounded-lg">
                            <SelectValue placeholder="Select supplier invoice" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">No Invoice (Direct Supplier Pay)</SelectItem>
                            {(draft.invoices ?? [])
                              .filter((inv) => inv.role === "supplier" || !inv.role)
                              .map((inv) => (
                                <SelectItem key={inv.id || inv.temp_id} value={String(inv.id || inv.temp_id)}>
                                  {inv.invoiceNo} ({inv.partyName || `#${inv.partyId}`})
                                </SelectItem>
                              ))}
                          </SelectContent>
                        </Select>
                      </div>
                      {showInvestorPurchaseTrack ? (
                        <div className="space-y-1.5">
                          <Label className="text-xs font-bold text-foreground">Link investor base price</Label>
                          <Select
                            value={draft.investorBaseInvoiceId ? String(draft.investorBaseInvoiceId) : "none"}
                            onValueChange={(val) => {
                              const invId = val === "none" ? null : val;
                              setDraft((d) => ({ ...d, investorBaseInvoiceId: invId }));
                            }}
                          >
                            <SelectTrigger className="h-10 text-xs sm:text-sm bg-background border border-border/60 shadow-2xs rounded-lg">
                              <SelectValue placeholder="Select investor invoice" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">No Invoice</SelectItem>
                              {(draft.invoices ?? [])
                                .filter((inv) => inv.role === "investor")
                                .map((inv) => (
                                  <SelectItem key={inv.id || inv.temp_id} value={String(inv.id || inv.temp_id)}>
                                    {inv.invoiceNo} ({inv.partyName || `#${inv.partyId}`})
                                  </SelectItem>
                                ))}
                            </SelectContent>
                          </Select>
                        </div>
                      ) : null}
                    </div>
                  </div>
                )}

                {/* Dual purchase amounts: Supplier | Investor */}
                {draft.acquisitionType !== "consignment" && (
                <div
                  className={cn(
                    "grid grid-cols-1 gap-4 pb-2 border-b border-border/40",
                    showInvestorPurchaseTrack && "md:grid-cols-2",
                  )}
                >
                <div className="space-y-3 rounded-xl border border-border/50 bg-muted/10 p-3">
                  <div>
                    <p className="text-sm font-bold text-foreground">Supplier purchase (actual)</p>
                    <p className="text-[10px] text-muted-foreground mt-0.5">
                      Real purchase paid to the supplier — base currency, amount, and rate used for landed cost
                    </p>
                  </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label className="text-xs text-muted-foreground">Purchased Currency</Label>
                      <CurrencySelect
                        value={draft.purchaseCurrency ?? getActiveCompanyCurrency().code}
                        currencies={currencies}
                        onChange={(v) => {
                          const activeCode = getActiveCompanyCurrency().code;
                          const isBase = v === activeCode;
                          if (isBase) {
                            setPurchaseExchangeRateInput("1");
                            if (!investorQuoteCustomizedRef.current) {
                              setInvestorExchangeRateInput("1");
                            }
                          }
                          setDraft((d) => {
                            const rate = isBase ? 1 : d.purchaseExchangeRate ?? 1;
                            const amt = d.purchaseCurrencyAmount ?? d.purchasePrice ?? null;
                            const basePrice = amt != null && rate != null ? amt * rate : null;
                            const next: ProductDraft = {
                              ...d,
                              purchaseCurrency: v,
                              purchaseExchangeRate: rate,
                              purchaseCurrencyAmount: amt,
                              purchasePrice: basePrice,
                            };
                            if (!investorQuoteCustomizedRef.current) {
                              next.investorPurchaseCurrency = v;
                              next.investorPurchaseExchangeRate = rate;
                              next.investorPurchaseCurrencyAmount = amt;
                              next.investorPurchasePrice = basePrice;
                            }
                            return next;
                          });
                        }}
                      />
                    </div>

                    <div className="space-y-1.5">
                      <div className="flex items-center justify-between gap-x-2 min-h-[1rem]">
                        <div className="min-w-0">
                          <Label className="text-xs text-muted-foreground">
                            Purchased Amount ({draft.purchaseCurrency ?? getActiveCompanyCurrency().code})
                          </Label>
                        </div>
                        {draft.purchaseCurrencyAmount && pkrAmountShortLabel(Number(draft.purchaseCurrencyAmount)) ? (
                          <span className="text-[11px] font-medium text-primary shrink-0 leading-none tabular-nums">
                            {pkrAmountShortLabel(Number(draft.purchaseCurrencyAmount))}
                          </span>
                        ) : null}
                      </div>
                      <Input
                        type="number"
                        className="glass-input h-10"
                        min={0}
                        step="any"
                        value={purchaseCurrencyAmountInput}
                        onChange={(e) => {
                          const raw = e.target.value;
                          setPurchaseCurrencyAmountInput(raw);
                          if (!investorQuoteCustomizedRef.current) {
                            setInvestorCurrencyAmountInput(raw);
                          }
                          const amt = raw === "" ? null : Number(raw);
                          setDraft((d) => {
                            const rate = d.purchaseExchangeRate == null || !Number.isFinite(d.purchaseExchangeRate) ? 1 : d.purchaseExchangeRate;
                            const basePrice = amt != null && Number.isFinite(amt) ? amt * rate : null;
                            const isOwnerSurplus = d.dealType === "owner_vehicle" && d.profitRuleType === "owner_surplus";
                            const next: ProductDraft = {
                              ...d,
                              purchaseCurrencyAmount: amt,
                              purchasePrice: basePrice,
                              ...(isOwnerSurplus && basePrice != null ? { ownerDemandPrice: basePrice } : {}),
                            };
                            if (!investorQuoteCustomizedRef.current) {
                              next.investorPurchaseCurrency =
                                d.purchaseCurrency ?? getActiveCompanyCurrency().code;
                              next.investorPurchaseCurrencyAmount = amt;
                              next.investorPurchaseExchangeRate = d.purchaseExchangeRate ?? 1;
                              next.investorPurchasePrice = basePrice;
                            }
                            return next;
                          });
                        }}
                        placeholder="0.00"
                      />
                    </div>

                    <div className="space-y-1.5">
                      <Label className="text-xs text-muted-foreground">
                        Conversion Rate ({draft.purchaseCurrency ?? getActiveCompanyCurrency().code} → {getActiveCompanyCurrency().code})
                      </Label>
                      <Input
                        type="number"
                        className="glass-input h-10"
                        min={0}
                        step="any"
                        value={purchaseExchangeRateInput}
                        onChange={(e) => {
                          const raw = e.target.value;
                          setPurchaseExchangeRateInput(raw);
                          if (!investorQuoteCustomizedRef.current) {
                            setInvestorExchangeRateInput(raw === "" ? "1" : raw);
                          }
                          const rate = raw === "" ? null : Number(raw);
                          setDraft((d) => {
                            const effectiveRate = rate == null || !Number.isFinite(rate) ? 1 : rate;
                            const amt = d.purchaseCurrencyAmount;
                            const basePrice = amt != null && Number.isFinite(amt) ? amt * effectiveRate : null;
                            const isOwnerSurplus = d.dealType === "owner_vehicle" && d.profitRuleType === "owner_surplus";
                            const next: ProductDraft = {
                              ...d,
                              purchaseExchangeRate: rate,
                              purchasePrice: basePrice,
                              ...(isOwnerSurplus && basePrice != null ? { ownerDemandPrice: basePrice } : {}),
                            };
                            if (!investorQuoteCustomizedRef.current) {
                              next.investorPurchaseCurrency =
                                d.purchaseCurrency ?? getActiveCompanyCurrency().code;
                              next.investorPurchaseCurrencyAmount = amt;
                              next.investorPurchaseExchangeRate = rate ?? 1;
                              next.investorPurchasePrice = basePrice;
                            }
                            return next;
                          });
                        }}
                        placeholder="1.00000"
                      />
                    </div>

                    <div className="space-y-1.5 min-w-0">
                      <div className="flex items-center justify-between gap-x-2 min-h-[1rem]">
                        <div className="min-w-0">
                          <Label htmlFor="converted-base-purchase" className="text-xs text-muted-foreground">
                            Converted Base Purchase ({getActiveCompanyCurrency().code})
                          </Label>
                        </div>
                        {draft.purchasePrice && pkrAmountShortLabel(draft.purchasePrice) ? (
                          <span className="text-[11px] font-medium text-primary shrink-0 leading-none tabular-nums">
                            {pkrAmountShortLabel(draft.purchasePrice)}
                          </span>
                        ) : null}
                      </div>
                      <div className="relative min-w-0">
                        <Input
                          id="converted-base-purchase"
                          type="text"
                          readOnly
                          className="h-10 tabular-nums glass-input font-semibold text-foreground cursor-default"
                          value={formatCurrency(draft.purchasePrice ?? 0)}
                        />
                      </div>
                    </div>
                    <div className="rounded-lg border border-border/40 bg-background/70 px-3 py-2 flex items-center justify-between gap-2">
                      <span className="text-xs text-muted-foreground">Supplier landed</span>
                      <span className="text-sm font-bold tabular-nums text-foreground">
                        {formatCurrency(landedPreview ?? 0)}
                      </span>
                    </div>
                  </div>
                </div>

                {showInvestorPurchaseTrack ? (
                <div className="space-y-3 rounded-xl border border-border/50 bg-muted/10 p-3">
                  <div>
                    <p className="text-sm font-bold text-foreground">Investor purchase (quoted)</p>
                    <p className="text-[10px] text-muted-foreground mt-0.5">
                      Fills from supplier automatically — edit here only if the investor quote differs
                    </p>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label className="text-xs text-muted-foreground">Currency</Label>
                      <CurrencySelect
                        value={draft.investorPurchaseCurrency ?? getActiveCompanyCurrency().code}
                        currencies={currencies}
                        onChange={(v) => {
                          investorQuoteCustomizedRef.current = true;
                          const activeCode = getActiveCompanyCurrency().code;
                          const isBase = v === activeCode;
                          if (isBase) setInvestorExchangeRateInput("1");
                          setDraft((d) => {
                            const rate = isBase ? 1 : d.investorPurchaseExchangeRate ?? 1;
                            const amt = d.investorPurchaseCurrencyAmount ?? d.investorPurchasePrice ?? null;
                            const basePrice = amt != null && rate != null ? amt * rate : null;
                            return {
                              ...d,
                              investorPurchaseCurrency: v,
                              investorPurchaseExchangeRate: rate,
                              investorPurchaseCurrencyAmount: amt,
                              investorPurchasePrice: basePrice,
                            };
                          });
                        }}
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs text-muted-foreground">
                        Amount ({draft.investorPurchaseCurrency ?? getActiveCompanyCurrency().code})
                      </Label>
                      <Input
                        type="number"
                        className="glass-input h-10"
                        min={0}
                        step="any"
                        value={investorCurrencyAmountInput}
                        onChange={(e) => {
                          investorQuoteCustomizedRef.current = true;
                          const raw = e.target.value;
                          setInvestorCurrencyAmountInput(raw);
                          const amt = raw === "" ? null : Number(raw);
                          setDraft((d) => {
                            const rate =
                              d.investorPurchaseExchangeRate == null || !Number.isFinite(d.investorPurchaseExchangeRate)
                                ? 1
                                : d.investorPurchaseExchangeRate;
                            const basePrice = amt != null && Number.isFinite(amt) ? amt * rate : null;
                            return {
                              ...d,
                              investorPurchaseCurrencyAmount: amt,
                              investorPurchasePrice: basePrice,
                            };
                          });
                        }}
                        placeholder="0.00"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs text-muted-foreground">
                        Conversion Rate → {getActiveCompanyCurrency().code}
                      </Label>
                      <Input
                        type="number"
                        className="glass-input h-10"
                        min={0}
                        step="any"
                        value={investorExchangeRateInput}
                        onChange={(e) => {
                          investorQuoteCustomizedRef.current = true;
                          const raw = e.target.value;
                          setInvestorExchangeRateInput(raw);
                          const rate = raw === "" ? null : Number(raw);
                          setDraft((d) => {
                            const effectiveRate = rate == null || !Number.isFinite(rate) ? 1 : rate;
                            const amt = d.investorPurchaseCurrencyAmount;
                            const basePrice = amt != null && Number.isFinite(amt) ? amt * effectiveRate : null;
                            return {
                              ...d,
                              investorPurchaseExchangeRate: rate,
                              investorPurchasePrice: basePrice,
                            };
                          });
                        }}
                        placeholder="1.00000"
                      />
                    </div>
                    <div className="space-y-1.5 min-w-0">
                      <Label className="text-xs text-muted-foreground">
                        Converted base ({getActiveCompanyCurrency().code})
                      </Label>
                      <Input
                        type="text"
                        readOnly
                        className="h-10 tabular-nums glass-input font-semibold text-foreground cursor-default"
                        value={formatCurrency(draft.investorPurchasePrice ?? 0)}
                      />
                    </div>
                  </div>
                  <div className="rounded-lg border border-border/40 bg-background/70 px-3 py-2 flex items-center justify-between gap-2">
                    <span className="text-xs text-muted-foreground">Investor landed</span>
                    <span className="text-sm font-bold tabular-nums text-foreground">
                      {formatCurrency(investorLandedPreview ?? 0)}
                    </span>
                  </div>
                </div>
                ) : null}
                </div>
                )}

              </div>

              <div className="rounded-xl border border-border/50 bg-card/50 p-2.5 space-y-2.5">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <p className="text-sm font-semibold text-foreground">Additional cost lines</p>
                    <p className="text-[10px] text-muted-foreground">
                      {showCostLineInvestorTrack
                        ? "One row per cost — supplier actual and investor quoted amounts side by side; edit/delete once"
                        : "Add supplier cost lines that make up landed cost"}
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="default"
                    size="sm"
                    className="gap-1 shrink-0 h-8 text-xs font-medium shadow-xs"
                    onClick={() => openAddCostLineModal("supplier")}
                  >
                    <Plus className="h-3.5 w-3.5" />
                    Add cost line
                  </Button>
                </div>

                {(() => {
                  const allLines = draft.purchaseCostLines ?? [];
                  const supplierLines = filterCostLinesByRole(allLines, "supplier");
                  const investorLines = filterCostLinesByRole(allLines, "investor");
                  const pairedTypeIds = new Set<string>();

                  type CostLinePair = {
                    key: string;
                    editIndex: number;
                    supplier: (typeof allLines)[number] | null;
                    investor: (typeof allLines)[number] | null;
                  };

                  const pairs: CostLinePair[] = [];

                  if (showCostLineInvestorTrack) {
                    for (const line of supplierLines) {
                      const supplierIndex = allLines.findIndex((l) => l === line);
                      const typeKey = String(line.typeId);
                      pairedTypeIds.add(typeKey);
                      const investor = investorLines.find((l) => String(l.typeId) === typeKey) ?? null;
                      pairs.push({
                        key: `pair-${typeKey}-${supplierIndex}`,
                        editIndex: supplierIndex,
                        supplier: line,
                        investor,
                      });
                    }
                    for (const line of investorLines) {
                      const typeKey = String(line.typeId);
                      if (pairedTypeIds.has(typeKey)) continue;
                      const investorIndex = allLines.findIndex((l) => l === line);
                      pairs.push({
                        key: `investor-only-${typeKey}-${investorIndex}`,
                        editIndex: investorIndex,
                        supplier: null,
                        investor: line,
                      });
                    }
                  } else {
                    for (const line of supplierLines) {
                      const supplierIndex = allLines.findIndex((l) => l === line);
                      pairs.push({
                        key: `supplier-${String(line.typeId)}-${supplierIndex}`,
                        editIndex: supplierIndex,
                        supplier: line,
                        investor: null,
                      });
                    }
                  }

                  const renderAmountColumn = (
                    label: string | null,
                    line: (typeof allLines)[number] | null,
                  ) => {
                    if (!line) {
                      return (
                        <div className="text-right leading-none min-w-[6.5rem]">
                          {label ? (
                            <p className="text-[8px] font-bold uppercase tracking-wider text-muted-foreground leading-none mb-0.5">
                              {label}
                            </p>
                          ) : null}
                          <p className="text-[10px] text-muted-foreground">—</p>
                        </div>
                      );
                    }
                    const currCode = line.currencyCode ?? draft.purchaseCurrency ?? getActiveCompanyCurrency().code;
                    return (
                      <div className="text-right leading-none min-w-[6.5rem]">
                        {label ? (
                          <p className="text-[8px] font-bold uppercase tracking-wider text-muted-foreground leading-none mb-0.5">
                            {label}
                          </p>
                        ) : null}
                        <p className="text-[8px] font-bold uppercase tracking-wider text-muted-foreground leading-none">
                          {isDiscountCostLine(line) ? "Discount" : "Converted"}
                        </p>
                        <p
                          className={cn(
                            "font-bold text-[11px] tabular-nums leading-tight",
                            isDiscountCostLine(line) ? "text-emerald-500" : "text-foreground",
                          )}
                        >
                          {isDiscountCostLine(line) ? `-${formatCurrency(line.amount)}` : formatCurrency(line.amount)}
                        </p>
                        <p className="text-[9px] text-muted-foreground leading-tight mt-0.5">
                          <strong className="text-foreground">
                            {currCode} {line.currencyAmount ?? 0}
                          </strong>
                          {currCode !== getActiveCompanyCurrency().code && line.exchangeRate ? (
                            <span className="ml-0.5">(@ {line.exchangeRate})</span>
                          ) : null}
                        </p>
                      </div>
                    );
                  };

                  return (
                    <div className="rounded-lg border border-border/50 bg-background/60 p-2 space-y-2">
                      {purchaseCostTypes.length === 0 ? (
                        <p className="text-xs text-muted-foreground py-1.5">
                          No cost types yet.{" "}
                          {canCreatePurchaseCostType ? (
                            <button
                              type="button"
                              className="text-primary font-medium hover:underline"
                              onClick={() => openCostTypeDialog(null)}
                            >
                              Add a cost type
                            </button>
                          ) : (
                            <Link to="/purchase-cost-types" className="text-primary font-medium hover:underline">
                              Add purchase cost types
                            </Link>
                          )}
                          .
                        </p>
                      ) : pairs.length === 0 ? (
                        <div className="text-xs text-muted-foreground rounded-md bg-muted/20 border border-border/40 p-2.5 text-center">
                          <p className="font-medium text-foreground/80">No cost lines yet.</p>
                          <p className="text-[10px] text-muted-foreground mt-0.5">
                            Use &quot;Add cost line&quot; to add supplier{showCostLineInvestorTrack ? " and investor" : ""} amounts.
                          </p>
                        </div>
                      ) : (
                        <div className="rounded-md border border-border/40 bg-card overflow-hidden">
                          {showCostLineInvestorTrack ? (
                            <div className="flex items-center justify-between gap-1.5 px-1.5 py-1.5 border-b border-border/50 bg-muted/30 sticky top-0 z-[1]">
                              <div className="min-w-0 flex-1">
                                <span className="text-[9px] font-bold uppercase tracking-wider text-muted-foreground">
                                  Cost type
                                </span>
                              </div>
                              <div className="flex items-center justify-end gap-3 shrink-0 pr-[3.25rem]">
                                <div className="min-w-[6.5rem] text-right">
                                  <span className="inline-flex items-center justify-end rounded px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider bg-sky-500/15 text-sky-700 dark:text-sky-300 border border-sky-500/25">
                                    Supplier
                                  </span>
                                </div>
                                <div className="min-w-[6.5rem] text-right">
                                  <span className="inline-flex items-center justify-end rounded px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider bg-violet-500/15 text-violet-700 dark:text-violet-300 border border-violet-500/25">
                                    Investor
                                  </span>
                                </div>
                              </div>
                            </div>
                          ) : null}
                          <div className="divide-y divide-border/40">
                          {pairs.map((pair) => {
                            const primary = pair.supplier ?? pair.investor;
                            if (!primary) return null;
                            const typeRec = purchaseCostTypes.find((t) => t.id === primary.typeId);
                            const typeName = typeRec?.label ?? primary.typeLabel ?? "Cost Line";
                            const paidByLabel = autoCostLinePayer.label;
                            const costBearerLabel = paidByLabel;
                            const metaLine = pair.supplier ?? pair.investor!;
                            const paidToParty = parties.find(
                              (p) => String(p.id) === String(metaLine.paidToPartyId ?? ""),
                            );
                            const paidToLabel = paidToParty?.name ?? null;
                            const investorPaidTo =
                              pair.investor &&
                              String(pair.investor.paidToPartyId ?? "") !== String(metaLine.paidToPartyId ?? "")
                                ? parties.find((p) => String(p.id) === String(pair.investor?.paidToPartyId ?? ""))
                                    ?.name ?? null
                                : null;
                            const invoice = (draft.invoices ?? []).find(
                              (inv) => String(inv.id || inv.temp_id) === String(metaLine.invoiceId),
                            );

                            return (
                              <div
                                key={pair.key}
                                className="flex flex-col sm:flex-row sm:items-center justify-between gap-1.5 p-1.5 text-[11px] hover:bg-muted/10 transition-colors"
                              >
                                <div className="space-y-0.5 min-w-0 flex-1">
                                  <div className="flex flex-wrap items-center gap-1">
                                    <span className="font-bold text-foreground text-xs">{typeName}</span>
                                    {autoCostLinePayer.paidByPartyId != null ? (
                                      <span
                                        className="inline-flex items-center px-1 py-px rounded-full text-[8px] font-semibold bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20"
                                        title={`Paid by ${paidByLabel}`}
                                      >
                                        Paid ({paidByLabel})
                                      </span>
                                    ) : (
                                      <span className="inline-flex items-center px-1 py-px rounded-full text-[8px] font-semibold bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/20">
                                        Pending
                                      </span>
                                    )}
                                    {paidToLabel ? (
                                      <span
                                        className="inline-flex items-center px-1 py-px rounded-full text-[8px] font-semibold bg-blue-500/10 text-blue-600 dark:text-blue-400 border border-blue-500/20"
                                        title="Pay by / Supplier"
                                      >
                                        Supplier: {paidToLabel}
                                      </span>
                                    ) : null}
                                    {investorPaidTo ? (
                                      <span
                                        className="inline-flex items-center px-1 py-px rounded-full text-[8px] font-semibold bg-sky-500/10 text-sky-600 dark:text-sky-400 border border-sky-500/20"
                                        title="Investor pay to"
                                      >
                                        Investor: {investorPaidTo}
                                      </span>
                                    ) : null}
                                    {invoice ? (
                                      <span
                                        className="inline-flex items-center px-1 py-px rounded-full text-[8px] font-bold bg-primary/10 text-primary border border-primary/20"
                                        title="Linked Supplier Invoice"
                                      >
                                        Invoice: {invoice.invoiceNo}
                                      </span>
                                    ) : null}
                                    {(invoice?.invoiceDate || metaLine.date) ? (
                                      <span
                                        className="inline-flex items-center px-1 py-px rounded-full text-[8px] font-semibold bg-muted text-muted-foreground border border-border"
                                        title="Cost Date"
                                      >
                                        Date:{" "}
                                        {invoice?.invoiceDate
                                          ? invoice.invoiceDate.slice(0, 10)
                                          : metaLine.date?.slice(0, 10)}
                                      </span>
                                    ) : null}
                                    <span
                                      className="inline-flex items-center px-1 py-px rounded-full text-[8px] font-semibold bg-purple-500/10 text-purple-600 dark:text-purple-400 border border-purple-500/20"
                                      title="Cost bearer"
                                    >
                                      Bearer: {costBearerLabel}
                                    </span>
                                  </div>

                                  {metaLine.description ? (
                                    <p className="text-[9px] italic text-muted-foreground truncate max-w-[360px] leading-tight">
                                      &quot;{metaLine.description}&quot;
                                    </p>
                                  ) : null}

                                  {(metaLine.bankName ||
                                    (metaLine.paymentMethod === "online" && metaLine.transactionReferenceNo) ||
                                    (metaLine.paymentMethod === "pay_order" && metaLine.payOrderNo)) ? (
                                    <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0 text-[9px] text-muted-foreground leading-tight">
                                      {metaLine.bankName ? (
                                        <span>
                                          Bank: <strong className="text-foreground">{metaLine.bankName}</strong>
                                        </span>
                                      ) : null}
                                      {metaLine.paymentMethod === "online" && metaLine.transactionReferenceNo ? (
                                        <span>
                                          Txn:{" "}
                                          <strong className="text-foreground">{metaLine.transactionReferenceNo}</strong>
                                        </span>
                                      ) : null}
                                      {metaLine.paymentMethod === "pay_order" && metaLine.payOrderNo ? (
                                        <span>
                                          PO: <strong className="text-foreground">{metaLine.payOrderNo}</strong>
                                        </span>
                                      ) : null}
                                    </div>
                                  ) : null}
                                </div>

                                <div className="flex items-center justify-between sm:justify-end gap-1.5 shrink-0 pt-1 sm:pt-0 border-t sm:border-t-0 border-border/30">
                                  <div className="flex items-start justify-end gap-3">
                                    {showCostLineInvestorTrack ? (
                                      <>
                                        <div className="rounded-md bg-sky-500/5 px-1.5 py-0.5">
                                          {renderAmountColumn(null, pair.supplier)}
                                        </div>
                                        <div className="rounded-md bg-violet-500/5 px-1.5 py-0.5">
                                          {renderAmountColumn(null, pair.investor)}
                                        </div>
                                      </>
                                    ) : (
                                      renderAmountColumn(null, pair.supplier)
                                    )}
                                  </div>

                                  <div className="flex items-center gap-0 self-stretch">
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="icon"
                                      className="h-6 w-6 text-muted-foreground hover:text-foreground hover:bg-muted"
                                      onClick={() => openEditCostLineModal(pair.editIndex)}
                                      aria-label="Edit line"
                                      title="Edit cost line"
                                    >
                                      <Edit className="h-2.5 w-2.5" />
                                    </Button>
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="icon"
                                      className="h-6 w-6 text-destructive/80 hover:text-destructive hover:bg-destructive/10"
                                      onClick={() => setPendingCostLineRemoveIndex(pair.editIndex)}
                                      aria-label="Remove line"
                                      title="Remove cost line"
                                    >
                                      <Trash2 className="h-2.5 w-2.5" />
                                    </Button>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                          </div>
                        </div>
                      )}

                      {supplierCostBreakdown.rows.length > 0 ? (
                        <div className="pt-1.5 border-t border-border/40 space-y-1.5">
                          <div className="flex items-center justify-between gap-2">
                            <p className="text-[10px] font-bold text-foreground uppercase tracking-wider">
                              Cost by Supplier
                            </p>
                            <div className="text-right">
                              <span className="text-[9px] font-bold uppercase tracking-wider text-muted-foreground mr-1">
                                Total:
                              </span>
                              <span className="text-[11px] font-extrabold tabular-nums text-primary">
                                {formatCurrency(supplierCostBreakdown.totalSupplierCost)}
                              </span>
                            </div>
                          </div>

                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-1">
                            {supplierCostBreakdown.rows.map((row) => (
                              <div
                                key={row.id}
                                className={cn(
                                  "rounded border border-border/50 bg-card/70 p-1",
                                  "hover:border-primary/30 transition-colors",
                                  row.hasSupplier || "border-dashed",
                                )}
                              >
                                <div className="flex items-center gap-1 min-w-0 mb-0.5">
                                  <span
                                    className={cn(
                                      "inline-flex items-center justify-center h-3.5 w-3.5 shrink-0 rounded text-[8px] font-bold",
                                      row.hasSupplier
                                        ? "bg-blue-500/10 text-blue-600 dark:text-blue-400"
                                        : "bg-muted text-muted-foreground",
                                    )}
                                  >
                                    {row.count}
                                  </span>
                                  <span className="truncate text-[10px] font-semibold text-foreground">
                                    {row.name}
                                  </span>
                                </div>
                                <div className="text-right">
                                  <p className="text-[10px] font-bold tabular-nums text-foreground leading-tight">
                                    {formatCurrency(row.amount)}
                                  </p>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : null}
                    </div>
                  );
                })()}

                {/* Summary Subsection: dual landed + sale */}
                <div
                  className={cn(
                    "grid grid-cols-1 gap-3 items-start pt-1 sm:grid-cols-2",
                    showInvestorPurchaseTrack && "lg:grid-cols-4",
                  )}
                >
                  <div className="space-y-1.5 min-w-0">
                    <Label className="text-xs text-muted-foreground">
                      Supplier additional ({getActiveCompanyCurrency().code})
                    </Label>
                    <Input
                      type="text"
                      readOnly
                      className="h-10 tabular-nums glass-input font-semibold text-foreground cursor-default"
                      value={formatCurrency(additionalSubtotal)}
                    />
                  </div>
                  <div className="space-y-1.5 min-w-0">
                    <Label className="text-xs font-semibold text-primary">
                      Supplier landed ({getActiveCompanyCurrency().code})
                    </Label>
                    <Input
                      type="text"
                      readOnly
                      className="h-10 tabular-nums glass-input font-bold text-foreground cursor-default border-primary/40 bg-primary/5"
                      value={formatCurrency(landedPreview ?? 0)}
                    />
                  </div>
                  {showInvestorPurchaseTrack ? (
                    <>
                      <div className="space-y-1.5 min-w-0">
                        <Label className="text-xs text-muted-foreground">
                          Investor additional ({getActiveCompanyCurrency().code})
                        </Label>
                        <Input
                          type="text"
                          readOnly
                          className="h-10 tabular-nums glass-input font-semibold text-foreground cursor-default"
                          value={formatCurrency(investorAdditionalCostsTotal)}
                        />
                      </div>
                      <div className="space-y-1.5 min-w-0">
                        <Label className="text-xs font-semibold text-primary">
                          Investor landed ({getActiveCompanyCurrency().code})
                        </Label>
                        <Input
                          type="text"
                          readOnly
                          className="h-10 tabular-nums glass-input font-bold text-foreground cursor-default border-primary/40 bg-primary/5"
                          value={formatCurrency(investorLandedPreview ?? 0)}
                        />
                      </div>
                    </>
                  ) : null}
                  <SalePriceInput
                    id="sale-price"
                    label="Expected Sale Price (optional)"
                    value={draft.salePrice ?? null}
                    onChange={(salePrice) => setDraft((d) => ({ ...d, salePrice }))}
                    inputClassName="glass-input"
                    showShortLabelHint
                    className="min-w-0 sm:col-span-2 lg:col-span-full"
                  />
                </div>
              </div>

              {/* Detailed Deal Information & Deal Partners Section in Original Position */}
              <div className="space-y-2">
                {effectiveDealType !== "purchased" && effectiveDealType !== "partnership" && (
                  <DealInformationSection
                    draft={draft}
                    onChange={(patch) => setDraft((d) => ({ ...d, ...patch }))}
                  />
                )}
                {effectiveDealType !== "purchased" && !isOwnerSurplus && (
                  <CompanyOwnerCommissionSection
                    value={draft.showroomCharge ?? draft.companyOwnerCommission}
                    companyParties={companyParties}
                    commissionBase={showroomCommissionBase}
                    dealType={effectiveDealType}
                    profitRuleType={draft.profitRuleType}
                    locked={isOwnerSurplus}
                    acquisitionType={draft.acquisitionType ?? "purchased"}
                    hasPurchaseAmount={hasConsignmentPurchaseAmount}
                    onChange={(showroomCharge) => {
                      setDraft((d) => ({
                        ...d,
                        showroomCharge,
                        companyOwnerCommission: showroomCharge,
                      }));
                    }}
                  />
                )}
                {effectiveDealType === "partnership" && (
                  <DealPartnersSection
                    dealType={effectiveDealType}
                    profitRuleType={draft.profitRuleType}
                    partners={draft.dealPartners ?? []}
                    parties={parties}
                    companyParties={companyParties}
                    investmentPool={partnerInvestmentPool}
                    ownerSurplusDefaults={isOwnerSurplus}
                    onPartiesChange={setParties}
                    onChange={(dealPartners) => {
                      setDraft((d) => ({
                        ...d,
                        dealPartners: withRecalculatedInvestmentRatios(
                          dealPartners,
                          partnerInvestmentPool,
                        ),
                      }));
                    }}
                  />
                )}
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">Purchase Notes (optional)</Label>
                <Textarea
                  className="glass-input min-h-[88px]"
                  value={draft.purchaseNotes ?? ""}
                  onChange={(e) => setDraft((d) => ({ ...d, purchaseNotes: e.target.value }))}
                  placeholder="Purchase notes…"
                  aria-label="Purchase notes"
                />
              </div>
            </motion.div>
          ) : null
        }

        <div className="flex flex-col-reverse gap-3 border-t border-border/40 pb-4 pt-4 sm:flex-row sm:items-center sm:justify-between">
          <Button
            type="button"
            variant="glass"
            className="w-full gap-2 sm:w-auto"
            disabled={wizardStep === 0}
            onClick={goBack}
          >
            <ChevronLeft className="h-4 w-4" />
            Back
          </Button>
          <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:justify-end">
            {isLastStep ? (
              <Button
                type="button"
                variant="premium"
                className="w-full gap-2 px-8 sm:w-auto"
                disabled={saveLoading || isEditLocked}
                title={isEditLocked ? "Sold or reserved products cannot be edited." : undefined}
                onClick={() => void finishWizard()}
              >
                {saveLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                {isEdit ? "Update" : "Save as draft"}
              </Button>
            ) : (
              <Button
                type="button"
                variant="premium"
                className="w-full gap-2 px-8 sm:w-auto"
                disabled={saveLoading || isEditLocked}
                title={isEditLocked ? "Sold or reserved products cannot be edited." : undefined}
                onClick={() => void goNext()}
              >
                {saveLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                Save &amp; Next
              </Button>
            )}
          </div>
        </div>
        </div>
      </div>

      <VehicleProductNameModal
        open={vehiclePickerOpen}
        onOpenChange={setVehiclePickerOpen}
        brands={brands}
        initialBrandId={draft.brandId ?? null}
        initialModelYear={draft.modelYear ?? null}
        initialVehicleModelId={draft.vehicleModelId ?? null}
        initialVehicleVersionId={draft.vehicleVersionId ?? null}
        onBrandCreated={(brand) => {
          setBrands((prev) => (prev.some((b) => b.id === brand.id) ? prev : [...prev, brand]));
        }}
        onApply={({ name, brandId, vehicleModelId, vehicleVersionId, modelYear }) => {
          setDraft((d) => ({
            ...d,
            name,
            brandId,
            vehicleModelId,
            vehicleVersionId,
            modelYear,
            slug: slugify(name),
          }));
        }}
      />

      <Dialog
        open={costTypeDialogOpen}
        onOpenChange={(open) => {
          if (costTypeSaving) return;
          setCostTypeDialogOpen(open);
          if (!open) {
            setCostTypeDialogLineIndex(null);
            setCostTypeLabel("");
            setCostTypeCategoryId("");
          }
        }}
      >
        <DialogContent className="sm:max-w-xl md:max-w-2xl p-6 gap-5">
          <DialogHeader className="border-b border-border/40 pb-3">
            <DialogTitle className="flex items-center gap-2.5 text-lg font-bold text-foreground">
              <Tag className="h-5 w-5 text-primary shrink-0" />
              New Cost Type
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-1 text-xs">
            <div className="space-y-4 bg-muted/20 p-4 rounded-xl border border-border/40">
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-foreground">Category *</Label>
                <PurchaseCostCategorySelect
                  value={costTypeCategoryId}
                  categories={purchaseCostCategories}
                  onChange={setCostTypeCategoryId}
                  placeholder="Search or select category"
                  disabled={costTypeSaving}
                  triggerClassName="h-11 text-sm"
                />
                <p className="text-[11px] text-muted-foreground mt-1">
                  Manage categories in{" "}
                  <Link to="/purchase-cost-categories" target="_blank" className="text-primary hover:underline font-medium">
                    Purchase cost categories
                  </Link>
                  .
                </p>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="quick-cost-type-label" className="text-xs font-semibold text-foreground">
                  Cost Type Name *
                </Label>
                <Input
                  id="quick-cost-type-label"
                  className="glass-input h-11 text-sm font-medium"
                  value={costTypeLabel}
                  disabled={costTypeSaving}
                  placeholder="e.g. Custom Duty, Transportation, Documentation"
                  onChange={(e) => setCostTypeLabel(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      void handleCreateCostType();
                    }
                  }}
                />
              </div>
            </div>
          </div>

          <DialogFooter className="flex flex-col sm:flex-row items-center justify-end gap-2 pt-3 border-t border-border/40">
            <Button
              type="button"
              variant="ghost"
              className="h-10 px-4 text-xs font-medium"
              disabled={costTypeSaving}
              onClick={() => setCostTypeDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="default"
              className="h-10 px-5 text-xs font-semibold bg-primary hover:bg-primary/90 text-primary-foreground shadow-xs"
              disabled={costTypeSaving}
              onClick={() => void handleCreateCostType()}
            >
              {costTypeSaving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                "Save"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={documentTypeDialogOpen}
        onOpenChange={(open) => {
          if (documentTypeSaving) return;
          setDocumentTypeDialogOpen(open);
          if (!open) setDocumentTypeName("");
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New document type</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-1">
            <div className="space-y-1.5">
              <Label htmlFor="quick-document-type-name">Name</Label>
              <Input
                id="quick-document-type-name"
                className="glass-input"
                value={documentTypeName}
                disabled={documentTypeSaving}
                placeholder="e.g. Fitness Certificate"
                autoFocus
                onChange={(e) => setDocumentTypeName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    void handleCreateDocumentType();
                  }
                }}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              disabled={documentTypeSaving}
              onClick={() => setDocumentTypeDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="default"
              disabled={documentTypeSaving}
              onClick={() => void handleCreateDocumentType()}
            >
              {documentTypeSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Additional Cost Line Add/Edit Modal - Larger & Premium Layout */}
      <Dialog open={costLineModalOpen} onOpenChange={setCostLineModalOpen}>
        <DialogContent
          className={cn(
            "p-6 gap-5 max-h-[90vh] overflow-y-auto",
            showCostLineInvestorTrack ? "sm:max-w-3xl md:max-w-5xl" : "sm:max-w-2xl md:max-w-3xl",
          )}
        >
          <DialogHeader className="pb-3 border-b border-border/50">
            <div className="flex items-center gap-2.5">
              <div className="h-9 w-9 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center text-primary shrink-0">
                <Wallet className="w-5 h-5" />
              </div>
              <div className="text-left">
                <DialogTitle className="text-lg font-bold text-foreground">
                  {editingCostLineIndex != null || editingInvestorCostLineIndex != null
                    ? "Edit Cost Line"
                    : "Add Cost Line"}
                </DialogTitle>
                <p className="text-xs text-muted-foreground">
                  Record duty, freight, clearing, or other expense lines for this vehicle.
                </p>
              </div>
            </div>
          </DialogHeader>

          <div className="space-y-4 py-1 text-xs">
            {/* Shared: Cost Type & Currency */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 bg-muted/20 p-4 rounded-xl border border-border/40">
              <div className="space-y-1.5 min-w-0">
                <Label className="flex items-center justify-between gap-1 text-xs font-semibold text-foreground">
                  <span>Cost Type *</span>
                  {canCreatePurchaseCostType ? (
                    <button
                      type="button"
                      className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-md text-primary hover:bg-primary/10"
                      aria-label="Add cost type"
                      title="Add cost type"
                      onClick={() => openCostTypeDialog(editingCostLineIndex)}
                    >
                      <Plus className="h-3.5 w-3.5" strokeWidth={2.5} />
                    </button>
                  ) : null}
                </Label>
                <PurchaseCostTypeSelect
                  key={costTypeSelectKey}
                  value={costLineModalForm.typeId}
                  types={purchaseCostTypes}
                  onChange={(v) => setCostLineModalForm((f) => ({ ...f, typeId: v }))}
                  placeholder="Select cost type"
                  triggerClassName="h-11 text-sm"
                />
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-foreground">Currency</Label>
                <CurrencySelect
                  value={costLineModalForm.currencyCode ?? draft.purchaseCurrency ?? getActiveCompanyCurrency().code}
                  currencies={currencies}
                  triggerClassName="h-11 text-sm"
                  onChange={(v) => {
                    const activeBase = getActiveCompanyCurrency().code;
                    const isBase = v === activeBase;
                    if (isBase) {
                      setCostLineExchangeRateInput("1");
                      if (!costLineInvestorQuoteCustomizedRef.current) {
                        setCostLineInvestorExchangeRateInput("1");
                      }
                    }
                    setCostLineModalForm((f) => ({
                      ...f,
                      currencyCode: v,
                      exchangeRate: isBase ? 1 : f.exchangeRate ?? 1,
                    }));
                    if (!costLineInvestorQuoteCustomizedRef.current) {
                      setCostLineInvestorTrack((t) => ({
                        ...t,
                        exchangeRate: isBase ? 1 : t.exchangeRate ?? 1,
                      }));
                    }
                  }}
                />
              </div>
            </div>

            {/* Dual tracks: Supplier (left) | Investor (right). Fields stack vertically inside each column. */}
            <div
              className={cn(
                "grid grid-cols-1 gap-4",
                showCostLineInvestorTrack && "md:grid-cols-2 md:items-start",
              )}
            >
              {(() => {
                const cCode = costLineModalForm.currencyCode ?? getActiveCompanyCurrency().code;
                const isBase = cCode === getActiveCompanyCurrency().code;
                const supplierRate = isBase ? 1 : costLineModalForm.exchangeRate ?? 1;
                const supplierAmt = costLineModalForm.currencyAmount ?? 0;
                const supplierBase = isBase ? supplierAmt : Number((supplierAmt * supplierRate).toFixed(2));
                const investorRate = isBase ? 1 : costLineInvestorTrack.exchangeRate ?? 1;
                const investorAmt = costLineInvestorTrack.currencyAmount ?? 0;
                const investorBase = isBase ? investorAmt : Number((investorAmt * investorRate).toFixed(2));
                const showInvestorCostTrack = showCostLineInvestorTrack;

                // Amount + rate on one row; cost base + invoice on the next (both tracks).
                const moneyRowClass = "grid grid-cols-1 gap-3 sm:grid-cols-2";
                const baseInvoiceRowClass = "grid grid-cols-1 gap-3 sm:grid-cols-2";

                return (
                  <>
                    <div className="space-y-3 rounded-xl border border-border/50 bg-primary/5 p-4">
                      <div>
                        <p className="text-sm font-bold text-foreground">
                          {showInvestorCostTrack ? "Supplier (actual)" : "Cost (actual)"}
                        </p>
                        <p className="text-[10px] text-muted-foreground mt-0.5">
                          {showInvestorCostTrack
                            ? "Real cost paid to the supplier — mirrors into investor unless you change it"
                            : "Actual cost amount for this line"}
                        </p>
                      </div>

                      <div className={moneyRowClass}>
                        <div className="space-y-1.5 min-w-0">
                          <div className="flex items-center justify-between gap-x-2 min-h-[1.25rem]">
                            <Label className="text-xs font-semibold text-foreground">Amount</Label>
                            {supplierAmt && pkrAmountShortLabel(Number(supplierAmt)) ? (
                              <span className="text-[11px] font-bold text-primary shrink-0 leading-none tabular-nums px-2 py-0.5 rounded bg-primary/10 border border-primary/20">
                                {pkrAmountShortLabel(Number(supplierAmt))}
                              </span>
                            ) : null}
                          </div>
                          <Input
                            type="number"
                            className="glass-input h-11 text-sm font-medium"
                            min={0}
                            step="any"
                            value={costLineCurrencyAmountInput}
                            onChange={(e) => {
                              const raw = e.target.value;
                              setCostLineCurrencyAmountInput(raw);
                              const n = raw === "" ? 0 : Number(raw);
                              const amt = Number.isFinite(n) ? n : 0;
                              setCostLineModalForm((f) => ({ ...f, currencyAmount: amt }));
                              if (
                                showInvestorCostTrack &&
                                !costLineInvestorQuoteCustomizedRef.current
                              ) {
                                setCostLineInvestorCurrencyAmountInput(raw);
                                setCostLineInvestorTrack((t) => ({ ...t, currencyAmount: amt }));
                              }
                            }}
                            placeholder="0.00"
                          />
                        </div>

                        <div className="space-y-1.5 min-w-0">
                          <Label className="text-xs font-semibold text-foreground">Conversion Rate</Label>
                          <Input
                            type="number"
                            className="glass-input h-11 text-sm"
                            min={0}
                            step="any"
                            disabled={isBase}
                            value={costLineExchangeRateInput}
                            onChange={(e) => {
                              const raw = e.target.value;
                              setCostLineExchangeRateInput(raw);
                              const n = raw === "" ? null : Number(raw);
                              const rate = n != null && Number.isFinite(n) ? n : null;
                              setCostLineModalForm((f) => ({ ...f, exchangeRate: rate }));
                              if (
                                showInvestorCostTrack &&
                                !costLineInvestorQuoteCustomizedRef.current
                              ) {
                                setCostLineInvestorExchangeRateInput(raw === "" ? "1" : raw);
                                setCostLineInvestorTrack((t) => ({ ...t, exchangeRate: rate ?? 1 }));
                              }
                            }}
                            placeholder="1.00000"
                          />
                        </div>
                      </div>

                      <div className={baseInvoiceRowClass}>
                        <div className="space-y-1.5 min-w-0">
                          <div className="flex items-center justify-between gap-x-2 min-h-[1.25rem]">
                            <Label className="text-xs font-semibold text-foreground">
                              Cost Base ({getActiveCompanyCurrency().code})
                            </Label>
                            {supplierBase && pkrAmountShortLabel(supplierBase) ? (
                              <span className="text-[11px] font-bold text-primary shrink-0 leading-none tabular-nums px-2 py-0.5 rounded bg-primary/10 border border-primary/20">
                                {pkrAmountShortLabel(supplierBase)}
                              </span>
                            ) : null}
                          </div>
                          <Input
                            type="text"
                            readOnly
                            className="glass-input h-11 text-sm font-bold tabular-nums cursor-default bg-background text-foreground border-primary/40"
                            value={formatCurrency(supplierBase)}
                          />
                        </div>

                        <div className="space-y-1.5 min-w-0">
                          <Label className="text-xs font-semibold text-foreground">
                            Link to Invoice
                            {isDiscountCostLine(costLineModalForm) ? (
                              <span className="text-destructive"> *</span>
                            ) : null}
                          </Label>
                          <Select
                            value={costLineModalForm.invoiceId ? String(costLineModalForm.invoiceId) : "none"}
                            onValueChange={(val) => {
                              const invId = val === "none" ? null : val;
                              const invoice = (draft.invoices ?? []).find(
                                (inv) => String(inv.id || inv.temp_id) === String(invId),
                              );
                              setCostLineModalForm((f) => ({
                                ...f,
                                invoiceId: invId,
                                paidToPartyId: invoice ? Number(invoice.partyId) : f.paidToPartyId,
                                date: invoice?.invoiceDate
                                  ? invoice.invoiceDate.slice(0, 10)
                                  : invId
                                    ? f.date
                                    : f.date || new Date().toISOString().split("T")[0],
                              }));
                            }}
                          >
                            <SelectTrigger className="h-11 text-sm bg-background border-border/40">
                              <SelectValue placeholder="Select invoice" />
                            </SelectTrigger>
                            <SelectContent>
                              {!isDiscountCostLine(costLineModalForm) ? (
                                <SelectItem value="none">No Invoice</SelectItem>
                              ) : null}
                              {(draft.invoices ?? [])
                                .filter((inv) => inv.role === "supplier" || !inv.role)
                                .map((inv) => (
                                  <SelectItem key={inv.id || inv.temp_id} value={String(inv.id || inv.temp_id)}>
                                    {inv.invoiceNo} ({inv.partyName || `#${inv.partyId}`})
                                  </SelectItem>
                                ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>

                    {showInvestorCostTrack ? (
                    <div className="space-y-3 rounded-xl border border-border/50 bg-muted/10 p-4">
                      <div>
                        <p className="text-sm font-bold text-foreground">Investor (quoted)</p>
                        <p className="text-[10px] text-muted-foreground mt-0.5">
                          Fills from supplier — change only if the investor quote differs
                        </p>
                      </div>

                      <div className={moneyRowClass}>
                        <div className="space-y-1.5 min-w-0">
                          <div className="flex items-center justify-between gap-x-2 min-h-[1.25rem]">
                            <Label className="text-xs font-semibold text-foreground">Amount</Label>
                            {investorAmt && pkrAmountShortLabel(Number(investorAmt)) ? (
                              <span className="text-[11px] font-bold text-primary shrink-0 leading-none tabular-nums px-2 py-0.5 rounded bg-primary/10 border border-primary/20">
                                {pkrAmountShortLabel(Number(investorAmt))}
                              </span>
                            ) : null}
                          </div>
                          <Input
                            type="number"
                            className="glass-input h-11 text-sm font-medium"
                            min={0}
                            step="any"
                            value={costLineInvestorCurrencyAmountInput}
                            onChange={(e) => {
                              costLineInvestorQuoteCustomizedRef.current = true;
                              const raw = e.target.value;
                              setCostLineInvestorCurrencyAmountInput(raw);
                              const n = raw === "" ? 0 : Number(raw);
                              setCostLineInvestorTrack((t) => ({
                                ...t,
                                currencyAmount: Number.isFinite(n) ? n : 0,
                              }));
                            }}
                            placeholder="0.00"
                          />
                        </div>

                        <div className="space-y-1.5 min-w-0">
                          <Label className="text-xs font-semibold text-foreground">Conversion Rate</Label>
                          <Input
                            type="number"
                            className="glass-input h-11 text-sm"
                            min={0}
                            step="any"
                            disabled={isBase}
                            value={costLineInvestorExchangeRateInput}
                            onChange={(e) => {
                              costLineInvestorQuoteCustomizedRef.current = true;
                              const raw = e.target.value;
                              setCostLineInvestorExchangeRateInput(raw);
                              const n = raw === "" ? null : Number(raw);
                              setCostLineInvestorTrack((t) => ({
                                ...t,
                                exchangeRate: n != null && Number.isFinite(n) ? n : null,
                              }));
                            }}
                            placeholder="1.00000"
                          />
                        </div>
                      </div>

                      <div className={baseInvoiceRowClass}>
                        <div className="space-y-1.5 min-w-0">
                          <div className="flex items-center justify-between gap-x-2 min-h-[1.25rem]">
                            <Label className="text-xs font-semibold text-foreground">
                              Cost Base ({getActiveCompanyCurrency().code})
                            </Label>
                            {investorBase && pkrAmountShortLabel(investorBase) ? (
                              <span className="text-[11px] font-bold text-primary shrink-0 leading-none tabular-nums px-2 py-0.5 rounded bg-primary/10 border border-primary/20">
                                {pkrAmountShortLabel(investorBase)}
                              </span>
                            ) : null}
                          </div>
                          <Input
                            type="text"
                            readOnly
                            className="glass-input h-11 text-sm font-bold tabular-nums cursor-default bg-background text-foreground border-primary/40"
                            value={formatCurrency(investorBase)}
                          />
                        </div>

                        <div className="space-y-1.5 min-w-0">
                          <Label className="text-xs font-semibold text-foreground">Link to Invoice</Label>
                          <Select
                            value={costLineInvestorTrack.invoiceId ? String(costLineInvestorTrack.invoiceId) : "none"}
                            onValueChange={(val) => {
                              const invId = val === "none" ? null : val;
                              const invoice = (draft.invoices ?? []).find(
                                (inv) => String(inv.id || inv.temp_id) === String(invId),
                              );
                              setCostLineInvestorTrack((t) => ({
                                ...t,
                                invoiceId: invId,
                                paidToPartyId: invoice ? Number(invoice.partyId) : t.paidToPartyId,
                              }));
                            }}
                          >
                            <SelectTrigger className="h-11 text-sm bg-background border-border/40">
                              <SelectValue placeholder="Select invoice" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">No Invoice</SelectItem>
                              {(draft.invoices ?? [])
                                .filter((inv) => inv.role === "investor")
                                .map((inv) => (
                                  <SelectItem key={inv.id || inv.temp_id} value={String(inv.id || inv.temp_id)}>
                                    {inv.invoiceNo} ({inv.partyName || `#${inv.partyId}`})
                                  </SelectItem>
                                ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                    </div>
                    ) : null}
                  </>
                );
              })()}
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:items-end bg-muted/20 p-4 rounded-xl border border-border/40">
              <div className="flex flex-col gap-1.5 min-w-0">
                <div className="flex h-5 items-center justify-between gap-2">
                  <Label className="text-xs font-semibold text-foreground leading-none">Pay By / Supplier *</Label>
                  {canCreateSupplier ? (
                    <button
                      type="button"
                      className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-md text-primary hover:bg-primary/10 transition-colors"
                      aria-label="Add supplier"
                      title="Add supplier"
                      onClick={() => setAddSupplierOpen(true)}
                    >
                      <Plus className="h-3.5 w-3.5" strokeWidth={2.5} />
                    </button>
                  ) : null}
                </div>
                <PartySelect
                  value={costLineModalForm.paidToPartyId ? String(costLineModalForm.paidToPartyId) : ""}
                  options={parties.filter((p) => {
                    if (costLineModalForm.paidToPartyId && String(p.id) === String(costLineModalForm.paidToPartyId)) {
                      return true;
                    }
                    const type = (p.type ?? "").toLowerCase();
                    return type === "supplier" || type === "all" || type === "both" || Boolean(p.isCompany);
                  })}
                  onChange={(val) =>
                    setCostLineModalForm((f) => ({
                      ...f,
                      paidToPartyId: val ? Number(val) : null,
                    }))
                  }
                  placeholder="Select supplier"
                  triggerClassName="h-11 text-sm bg-background border-border/40"
                />
              </div>

              <div className="flex flex-col gap-1.5 min-w-0">
                <div className="flex h-5 items-center gap-2 min-w-0">
                  <Label className="text-xs font-semibold text-foreground leading-none truncate">
                    Date {costLineModalForm.invoiceId ? (
                      <span className="text-[10px] font-normal text-muted-foreground ml-1">(Invoice Date · Read-only)</span>
                    ) : (
                      <span className="text-destructive ml-0.5">*</span>
                    )}
                  </Label>
                </div>
                <DatePartsPicker
                  value={costLineModalForm.date ?? ""}
                  onChange={(val) => setCostLineModalForm((f) => ({ ...f, date: val }))}
                  disabled={Boolean(costLineModalForm.invoiceId)}
                  placeholder="Select cost date"
                  className={cn(
                    "h-11",
                    costLineModalForm.invoiceId ? "opacity-80 bg-muted/50 cursor-not-allowed" : "",
                  )}
                />
              </div>
            </div>

            {/* Section 4: Notes */}
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-foreground">Notes (optional)</Label>
              <Input
                type="text"
                className="glass-input h-11 text-sm"
                value={costLineModalForm.description ?? ""}
                onChange={(e) => setCostLineModalForm((f) => ({ ...f, description: e.target.value }))}
                placeholder="Optional notes or reference..."
              />
            </div>
          </div>

          <DialogFooter className="flex flex-col sm:flex-row items-center justify-end gap-2 pt-3 border-t border-border/40">
            <Button
              type="button"
              variant="ghost"
              className="h-10 px-4 text-xs font-medium"
              onClick={() => setCostLineModalOpen(false)}
            >
              Cancel
            </Button>

            {editingCostLineIndex == null && editingInvestorCostLineIndex == null ? (
              <Button
                type="button"
                variant="outline"
                className="h-10 px-4 text-xs font-semibold border-primary/40 text-primary hover:bg-primary/10 shadow-2xs"
                onClick={() => handleSaveCostLineModal(true)}
              >
                Save and Add More
              </Button>
            ) : null}

            <Button
              type="button"
              variant="default"
              className="h-10 px-5 text-xs font-semibold bg-primary hover:bg-primary/90 text-primary-foreground shadow-xs"
              onClick={() => handleSaveCostLineModal(false)}
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <CreateInvestorDialog
        open={addInvestorOpen}
        onOpenChange={setAddInvestorOpen}
        onCreated={async (record) => {
          try {
            const nextParties = await syncPartiesFromApi();
            setParties(nextParties.length ? nextParties : listPartyOptions());
            const partyId = String(record.id);
            const partyName = record.name?.trim() || `Partner #${partyId}`;
            setDraft((d) => {
              const currentRows = d.dealPartners ?? [];
              if (currentRows.some((r) => String(r.partyId) === partyId)) {
                return d;
              }
              const newRow: DealPartnerDraft = {
                key: `dp-part-${partyId}-${Date.now()}`,
                partyId,
                partyName,
                investmentAmount: null,
                investmentRatio: null,
                profitType: "percentage",
                profitValue: null,
                isCompanyOwner: false,
              };
              let nextRows = [...currentRows.filter((r) => r.partyId), newRow];
              if (nextRows.length > 0) {
                const equalShare = Number((100 / nextRows.length).toFixed(2));
                nextRows = nextRows.map((r) => ({
                  ...r,
                  profitType: "percentage" as const,
                  profitValue: equalShare,
                }));
              }
              return { ...d, dealPartners: nextRows };
            });
          } catch (error) {
            console.error("[AddProduct] Failed to refresh parties after investor create", error);
            toast.error("Investor saved, but the party list could not be refreshed. Reload the page.");
          }
        }}
      />

      <CreateSupplierDialog
        open={addSupplierOpen}
        onOpenChange={setAddSupplierOpen}
        onCreated={async (record) => {
          try {
            const nextParties = await syncPartiesFromApi();
            setParties(nextParties.length ? nextParties : listPartyOptions());
            setCompanyParties(listCompanyPartyOptions());
            if (costLineModalOpen) {
              setCostLineModalForm((f) => ({
                ...f,
                paidToPartyId: record.id ? Number(record.id) : f.paidToPartyId,
              }));
            } else {
              setDraft((d) => ({
                ...d,
                supplierId: record.id ? String(record.id) : null,
              }));
            }
          } catch (error) {
            console.error("[AddProduct] Failed to refresh parties after supplier create", error);
            toast.error("Supplier saved, but the party list could not be refreshed. Reload the page.");
          }
        }}
      />

      <QuickAddAttributeDialog
        open={quickAddAttributeOpen}
        onOpenChange={(open) => {
          setQuickAddAttributeOpen(open);
          if (!open) setQuickAddAttributeGroup(null);
        }}
        group={quickAddAttributeGroup}
        categoryId={draft.categoryId || null}
        onLinked={refreshCategorySchema}
      />

      <AssignCategoryAttributesDialog
        open={assignCategoryAttributesOpen}
        onOpenChange={setAssignCategoryAttributesOpen}
        categoryId={draft.categoryId || null}
        categoryName={categories.find((c) => c.id === draft.categoryId)?.name ?? null}
        onSaved={refreshCategorySchema}
      />

      <CreateInvestorDialog
        open={addOwnerOpen}
        onOpenChange={setAddOwnerOpen}
        title="Add owner / seller"
        onCreated={async (record) => {
          try {
            const nextParties = await syncPartiesFromApi();
            setParties(nextParties.length ? nextParties : listPartyOptions());
            const partyId = String(record.id);
            const partyName = record.name?.trim() || `Owner #${partyId}`;
            setDraft((d) => {
              const currentPartners = d.dealPartners ?? [];
              const first = currentPartners[0] ?? emptyDealPartnerRow();
              const updatedFirst = {
                ...first,
                partyId,
                partyName,
                investmentAmount: landedPreview,
                investmentRatio: 100,
              };
              return {
                ...d,
                dealPartners: [updatedFirst, ...currentPartners.slice(1)],
              };
            });
          } catch (error) {
            console.error("[AddProduct] Failed to refresh parties after owner create", error);
            toast.error("Owner saved, but the party list could not be refreshed. Reload the page.");
          }
        }}
      />

      <Dialog open={invoiceModalOpen} onOpenChange={setInvoiceModalOpen}>
        <DialogContent
          className={cn(
            "p-6 max-h-[90vh] overflow-y-auto",
            showInvestorPurchaseTrack ? "sm:max-w-[720px]" : "sm:max-w-[540px]",
          )}
        >
          {/* Header matching Record Payment modal */}
          <DialogHeader className="pb-3 border-b border-border/50">
            <div className="flex items-center gap-2.5">
              <div className="h-9 w-9 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center text-primary shrink-0">
                <Receipt className="w-5 h-5" />
              </div>
              <div className="text-left">
                <DialogTitle className="text-lg font-bold text-foreground">
                  {(() => {
                    if (editingInvoicePair) return "Edit Invoices";
                    if (editingInvoice) {
                      const role = invoiceForm.role === "investor" ? "Investor" : "Supplier";
                      return `Edit ${role} Invoice`;
                    }
                    return showInvestorPurchaseTrack ? "Add Invoice" : "Add Supplier Invoice";
                  })()}
                </DialogTitle>
                <p className="text-xs text-muted-foreground">
                  {editingInvoicePair
                    ? "Update supplier and/or investor invoice details and documents."
                    : editingInvoice
                      ? invoiceForm.role === "investor"
                        ? "Update the investor reference, dates, and document attachment."
                        : "Update the supplier reference, dates, and document attachment."
                      : showInvestorPurchaseTrack
                        ? "Attach supplier, investor, or both documents in one save."
                        : "Record invoice details, billing date, and attach supplier document bills."}
                </p>
              </div>
            </div>
          </DialogHeader>

          {/* Modal Form Body */}
          <div className="space-y-4 py-3 text-foreground">
            {/* Supplier + Invoice No on one row (investor auto-selected, not shown) */}
            <div
              className={cn(
                "grid grid-cols-1 gap-4",
                !(editingInvoice && invoiceForm.role === "investor" && !editingInvoicePair) &&
                  "sm:grid-cols-2",
              )}
            >
              {!(editingInvoice && invoiceForm.role === "investor" && !editingInvoicePair) ? (
                <div className="space-y-1.5 min-w-0">
                  <Label className="text-xs font-semibold text-foreground">
                    {showInvestorPurchaseTrack && (!editingInvoice || editingInvoicePair)
                      ? "Supplier"
                      : "Supplier *"}
                  </Label>
                  <PartySelect
                    value={
                      showInvestorPurchaseTrack && (!editingInvoice || editingInvoicePair)
                        ? invoiceDualParties.supplier
                        : invoiceForm.partyId
                          ? String(invoiceForm.partyId)
                          : ""
                    }
                    options={parties.filter((p) => {
                      const type = (p.type ?? "").toLowerCase();
                      return type === "supplier" || type === "all" || type === "both" || Boolean(p.isCompany);
                    })}
                    onChange={(val) => {
                      if (showInvestorPurchaseTrack && (!editingInvoice || editingInvoicePair)) {
                        setInvoiceDualParties((f) => ({ ...f, supplier: val || "" }));
                      } else {
                        setInvoiceForm((f) => ({ ...f, partyId: val || "" }));
                      }
                    }}
                    placeholder="Select supplier"
                    triggerClassName="h-11 text-sm bg-background border-border/40"
                  />
                </div>
              ) : null}

              <div className="space-y-1.5 min-w-0">
                <Label className="text-xs font-semibold text-foreground">Invoice No *</Label>
                <Input
                  type="text"
                  className="glass-input h-11 text-sm"
                  value={invoiceForm.invoiceNo ?? ""}
                  onChange={(e) => setInvoiceForm((f) => ({ ...f, invoiceNo: e.target.value }))}
                  placeholder="e.g. INV-2026-00124"
                />
              </div>
            </div>

            {/* Dates row */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Invoice Date */}
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-foreground">Invoice Date *</Label>
                <DatePartsPicker
                  value={invoiceForm.invoiceDate ?? ""}
                  onChange={(val) => setInvoiceForm((f) => ({ ...f, invoiceDate: val }))}
                />
              </div>

              {/* Due Date */}
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-foreground">Due Date</Label>
                <DatePartsPicker
                  value={invoiceForm.dueDate ?? ""}
                  onChange={(val) => setInvoiceForm((f) => ({ ...f, dueDate: val }))}
                />
              </div>
            </div>

            {/* File Upload — dual track: Supplier | Investor (both can attach) */}
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-foreground">Invoice PDF/Image Document</Label>
              {showInvestorPurchaseTrack ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {(["supplier", "investor"] as const).map((docRole) => {
                    const editRole = invoiceForm.role === "investor" ? "investor" : "supplier";
                    const lockedOtherSide =
                      Boolean(editingInvoice) && !editingInvoicePair && editRole !== docRole;
                    const label = docRole === "supplier" ? "Supplier" : "Investor";
                    const file = invoiceDualFiles[docRole];
                    const hasFile = Boolean(file);
                    return (
                      <div
                        key={docRole}
                        className={cn(
                          "rounded-lg border p-2.5 space-y-1.5",
                          hasFile
                            ? "border-primary/40 bg-primary/5"
                            : "border-border/50 bg-background/60",
                          lockedOtherSide && "opacity-50",
                        )}
                      >
                        <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                          {label}
                        </p>
                        <Input
                          type="file"
                          accept="application/pdf,image/*"
                          disabled={lockedOtherSide}
                          className="h-11 text-xs file:bg-primary file:text-primary-foreground file:border-0 file:rounded-md file:px-2.5 file:py-1 file:mr-2 file:cursor-pointer cursor-pointer bg-background disabled:cursor-not-allowed"
                          onChange={(e) => {
                            const nextFile = e.target.files?.[0];
                            if (!nextFile) return;
                            setInvoiceDualFiles((f) => ({ ...f, [docRole]: nextFile }));
                            if (!editingInvoice) {
                              setInvoiceForm((f) => ({ ...f, role: docRole, filePath: nextFile }));
                            } else {
                              setInvoiceForm((f) => ({ ...f, filePath: nextFile }));
                            }
                          }}
                        />
                        {file && typeof file === "string" ? (
                          <div className="flex items-center gap-2 p-2 rounded-lg border border-border/50 bg-muted/40 text-xs font-medium text-foreground">
                            <FileText className="w-4 h-4 text-primary shrink-0" />
                            <button
                              type="button"
                              className="underline hover:text-primary font-semibold truncate text-left"
                              onClick={() =>
                                openDocumentPrintView(file, {
                                  title: `Invoice ${invoiceForm.invoiceNo || ""}`.trim(),
                                })
                              }
                            >
                              View Current Document
                            </button>
                          </div>
                        ) : null}
                        {file instanceof File ? (
                          <div className="flex items-center gap-2 p-2 rounded-lg border border-emerald-500/20 bg-emerald-500/10 text-xs font-medium text-emerald-700 dark:text-emerald-400">
                            <FileText className="w-4 h-4 shrink-0" />
                            <span className="truncate font-semibold">
                              {file.name} (Ready to upload)
                            </span>
                          </div>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <>
                  <Input
                    type="file"
                    accept="application/pdf,image/*"
                    className="h-11 text-xs file:bg-primary file:text-primary-foreground file:border-0 file:rounded-md file:px-2.5 file:py-1 file:mr-2 file:cursor-pointer cursor-pointer bg-background"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        setInvoiceForm((f) => ({ ...f, filePath: file }));
                        setInvoiceDualFiles((f) => ({ ...f, supplier: file }));
                      }
                    }}
                  />
                  {invoiceForm.filePath && typeof invoiceForm.filePath === "string" && (
                    <div className="flex items-center gap-2 mt-1.5 p-2 rounded-lg border border-border/50 bg-muted/40 text-xs font-medium text-foreground">
                      <FileText className="w-4 h-4 text-primary shrink-0" />
                      <button
                        type="button"
                        className="underline hover:text-primary font-semibold truncate text-left"
                        onClick={() =>
                          openDocumentPrintView(String(invoiceForm.filePath), {
                            title: `Invoice ${invoiceForm.invoiceNo || ""}`.trim(),
                          })
                        }
                      >
                        View Current Document
                      </button>
                    </div>
                  )}
                  {invoiceForm.filePath instanceof File && (
                    <div className="flex items-center gap-2 mt-1.5 p-2 rounded-lg border border-emerald-500/20 bg-emerald-500/10 text-xs font-medium text-emerald-700 dark:text-emerald-400">
                      <FileText className="w-4 h-4 shrink-0" />
                      <span className="truncate font-semibold">{invoiceForm.filePath.name} (Ready to upload)</span>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Notes */}
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-foreground">Notes (optional)</Label>
              <Textarea
                rows={3}
                className="glass-input text-sm resize-none bg-background border-border/40"
                value={invoiceForm.notes ?? ""}
                onChange={(e) => setInvoiceForm((f) => ({ ...f, notes: e.target.value }))}
                placeholder="Add any extra instructions or reference notes..."
              />
            </div>
          </div>

          <DialogFooter className="flex flex-col sm:flex-row items-center justify-end gap-2 pt-3 border-t border-border/50">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-9 px-4 text-xs font-semibold"
              onClick={() => setInvoiceModalOpen(false)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              size="sm"
              className="h-9 px-4 text-xs font-semibold shadow-xs"
              onClick={handleSaveInvoice}
            >
              {editingInvoice || editingInvoicePair
                ? "Save Changes"
                : showInvestorPurchaseTrack &&
                    invoiceDualFiles.supplier &&
                    invoiceDualFiles.investor
                  ? "Save Both Invoices"
                  : "Save Invoice"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmAlertDialog
        open={pendingCostLineRemoveIndex !== null}
        onOpenChange={(o) => !o && setPendingCostLineRemoveIndex(null)}
        title="Remove this cost line?"
        description={
          pendingCostLineRemoveIndex !== null ? (
            <>
              <span>
                Remove{" "}
                <strong className="text-foreground font-semibold">
                  {purchaseCostTypes.find(
                    (t) => t.id === draft.purchaseCostLines?.[pendingCostLineRemoveIndex]?.typeId,
                  )?.label ?? "this line"}
                </strong>{" "}
                from the landed cost breakdown
                {showCostLineInvestorTrack
                  ? " (supplier and investor amounts for this cost type)?"
                  : "?"}
              </span>
              {draft.purchaseCostLines?.[pendingCostLineRemoveIndex]?.description?.trim() ? (
                <span className="block mt-2 text-xs text-muted-foreground">
                  Description: {draft.purchaseCostLines[pendingCostLineRemoveIndex]?.description?.trim()}
                </span>
              ) : null}
            </>
          ) : (
            ""
          )
        }
        mode="confirm"
        variant="danger"
        confirmLabel="Remove"
        cancelLabel="Cancel"
        onConfirm={() => {
          if (pendingCostLineRemoveIndex !== null) removeCostLine(pendingCostLineRemoveIndex);
          setPendingCostLineRemoveIndex(null);
        }}
      />
    </Layout>
  );
};

export default AddProduct;
